import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { getAnalyticsConfigStatus, fetchAnalyticsEmbedToken, registerAnalyticsReport, AnalyticsEmbedConfig } from '../../services/analyticsService';
import { GlobalUser, Task, PhysicalUnit, WeeklyPlan, Contract, PaymentStatement, ProjectProgressSnapshot, LookaheadPlan, Responsible, MasterReportTemplate } from '../../types';
import { loadMasterReportTemplates } from '../admin/ReportTemplateManager';
import { useSyncState } from '../../hooks/useSyncState';
import { calculateProjectProgress, calculateSCurve, truncateProgress, findTaskById, calculateDelayDays, calculateProgrammedProgressAtDate, flattenTasks, getCriticalPathDelayAtDate } from '../../utils/planningUtils';
import { parseDateSafe, getISOWeekMonday, getDateHierarchyParts, extractRowDate, DateHierarchyParts, formatDateDDMMYYYY, formatIfDate } from '../../utils/dateHierarchyUtils';
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    LineChart,
    Line,
    ScatterChart,
    Scatter,
    ZAxis,
    RadarChart,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    Radar,
    ComposedChart
} from 'recharts';

export interface CalculatedColumn {
    id: string;
    name: string;
    calcType?: 'math' | 'text_concat' | 'text_transform' | 'conditional_if' | 'date_diff';
    // Math
    colA?: string;
    operator?: '+' | '-' | '*' | '/';
    colB?: string; // Puede ser un nombre de columna o 'constant'
    constantValue?: number;
    // Text Concat
    concatFields?: string[];
    concatSeparator?: string;
    // Text Transform
    transformField?: string;
    transformType?: 'uppercase' | 'lowercase' | 'trim';
    // Conditional IF
    conditionField?: string;
    conditionOperator?: 'equals' | 'not_equals' | 'greater' | 'less' | 'contains' | 'not_contains';
    conditionValue?: string;
    thenValue?: string;
    elseValue?: string;
    // Date Diff
    dateFieldA?: string;
    dateFieldB?: string;
}

export interface WidgetFieldConfig {
    fieldKey: string;
    aggregation?: 'sum' | 'avg' | 'count' | 'distinct_count' | 'min' | 'max' | 'none';
}

export interface LogicalFilterRule {
    id: string;
    field: string;
    filterType: 'basic' | 'advanced';
    operator: 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'greater' | 'less' | 'is_null' | 'is_not_null' | 'in';
    value: any; // string, number, or string[] for 'in' / basic checkboxes
    excludedValues?: string[]; // Para selección básica de checkboxes excluidos
}

export interface WidgetStyleConfig {
    // Objeto Visual
    showLegend?: boolean;
    legendPosition?: 'top' | 'bottom' | 'left' | 'right';
    showDataLabels?: boolean;
    dataLabelPosition?: 'outside' | 'inside';
    dataLabelContent?: 'value' | 'category_value' | 'percent_total' | 'both';
    dataLabelFormat?: 'auto' | 'integer' | 'decimal' | 'percent' | 'currency';
    dataLabelDecimals?: number;
    showXAxis?: boolean;
    showYAxis?: boolean;
    primaryColor?: string;
    paletteColors?: string[];
    // Slicer style
    slicerVisualMode?: 'dropdown' | 'checkbox' | 'pills';
    slicerDateHierarchy?: boolean; // Orden Jerárquico estilo Power BI (Año -> Mes -> Semana)
    // KPI Multi / Card Props
    kpiTargetValue?: number;
    kpiValueFontSize?: number;
    kpiLabelFontSize?: number;
    // Image Widget Props
    imageUrl?: string;
    imageFit?: 'cover' | 'contain' | 'fill';
    imageCaption?: string;
    // Header Banner Props
    bannerLogoUrl?: string;
    bannerTitle?: string;
    bannerSubtitle?: string;
    bannerProjectName?: string;
    bannerDate?: string;
    bannerEvaluator?: string;
    bannerGradient?: boolean;
    // Container Card / Box Props
    containerShadow?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
    containerOpacity?: number;
    // Text Box / Free Note Props
    textContent?: string;
    textFontSize?: number;
    textColor?: string;
    textAlign?: 'left' | 'center' | 'right' | 'justify';
    textBold?: boolean;
    textItalic?: boolean;
    // Table & Matrix Props
    showTableTotals?: boolean;
    showMatrixSubtotals?: boolean;
    showMatrixGrandTotals?: boolean;
    // General
    showTitle?: boolean;
    customTitle?: string;
    titleColor?: string;
    titleFontSize?: number;
    titleAlignment?: 'left' | 'center' | 'right';
    titlePadding?: number;
    titleMarginBottom?: number;
    showTitleDivider?: boolean;
    showBackground?: boolean;
    backgroundColor?: string;
    showBorder?: boolean;
    borderColor?: string;
    borderRadius?: number;
}

export interface ReportPage {
    id: string;
    name: string;
}

export interface WidgetConfig {
    id: string;
    pageId?: string;
    type: 
        | 'bar-vertical' 
        | 'bar-horizontal' 
        | 'pie' 
        | 'pie-solid'
        | 'donut'
        | 'bar-grouped-horizontal'
        | 'column-grouped-vertical'
        | 'column-stacked'
        | 'combo-chart'
        | 'kpi-multi'
        | 'slicer'
        | 'table'
        | 'matrix' 
        | 'line' 
        | 'area-stacked' 
        | 'scatter' 
        | 'radar' 
        | 'kpi-card' 
        | 'gauge'
        | 'image'
        | 'header-banner'
        | 'container-card'
        | 'text-box';
    componentId: string;
    width?: '1/3' | '1/2' | 'full';
    colSpan?: number; // 1 to 12 columns grid system (ancho)
    rowSpan?: number; // 1 to 6 rows grid system (alto)
    gridColStart?: number; // 1 to 12
    gridRowStart?: number; // 1 to 6
    minHeightPx?: number;
    axisFields?: WidgetFieldConfig[];
    valueFields?: WidgetFieldConfig[];
    legendField?: WidgetFieldConfig;
    detailFields?: WidgetFieldConfig[];
    sizeField?: WidgetFieldConfig;
    xAxisKey?: string;
    yAxisKey?: string;
    logicalFilters?: LogicalFilterRule[];
    style?: WidgetStyleConfig;
}

export const WIDGET_BUCKET_DEFS: Record<string, { id: 'axis' | 'value' | 'legend' | 'detail' | 'size'; label: string; max?: number; placeholder: string }[]> = {
    'bar-vertical': [
        { id: 'axis', label: 'Eje X (Categorías)', placeholder: 'Arrastra campos aquí para jerarquía/drill-down' },
        { id: 'value', label: 'Valores (Métricas Y)', placeholder: 'Arrastra campos numéricos a medir' },
        { id: 'legend', label: 'Leyenda (Series)', max: 1, placeholder: 'Arrastra campo para desglosar series' }
    ],
    'column-grouped-vertical': [
        { id: 'axis', label: 'Eje X (Categorías)', placeholder: 'Arrastra campo principal (ej: Semanas / Fases)' },
        { id: 'legend', label: 'Leyenda (Grupos/Series)', max: 1, placeholder: 'Arrastra campo de agrupación (ej: Subcontrato)' },
        { id: 'value', label: 'Valores (Eje Y)', placeholder: 'Arrastra campos numéricos a comparar lado a lado' }
    ],
    'column-stacked': [
        { id: 'axis', label: 'Eje X (Categorías)', placeholder: 'Arrastra campo principal (ej: Semanas / Fases)' },
        { id: 'legend', label: 'Leyenda (Subcategorías)', max: 1, placeholder: 'Arrastra campo para apilar verticalmente' },
        { id: 'value', label: 'Valores (Eje Y - Suma)', placeholder: 'Arrastra campos numéricos a acumular' }
    ],
    'bar-horizontal': [
        { id: 'axis', label: 'Eje Y (Categorías)', placeholder: 'Arrastra campos aquí para jerarquía/drill-down' },
        { id: 'value', label: 'Valores (Métricas X)', placeholder: 'Arrastra campos numéricos a medir' },
        { id: 'legend', label: 'Leyenda (Series)', max: 1, placeholder: 'Arrastra campo para desglosar series' }
    ],
    'bar-grouped-horizontal': [
        { id: 'axis', label: 'Eje Y (Categorías)', placeholder: 'Arrastra campo principal (ej: Subcontratista / Cuadrilla)' },
        { id: 'legend', label: 'Leyenda (Grupos/Series)', max: 1, placeholder: 'Arrastra campo de agrupación (ej: Estado)' },
        { id: 'value', label: 'Valores (Eje X)', placeholder: 'Arrastra métricas numéricas a comparar lado a lado' }
    ],
    'pie': [
        { id: 'axis', label: 'Leyenda / Segmentos', placeholder: 'Arrastra campo de agrupación' },
        { id: 'value', label: 'Valores', max: 1, placeholder: 'Arrastra campo numérico' }
    ],
    'pie-solid': [
        { id: 'axis', label: 'Leyenda (Categoría)', placeholder: 'Arrastra campo cualitativo (ej: Estado de Protocolo)' },
        { id: 'value', label: 'Valores (Métrica)', max: 1, placeholder: 'Arrastra campo de cantidad o suma' }
    ],
    'donut': [
        { id: 'axis', label: 'Leyenda (Categoría)', placeholder: 'Arrastra campo cualitativo (ej: Subcontrato / Tipo)' },
        { id: 'value', label: 'Valores (Métrica)', max: 1, placeholder: 'Arrastra campo numérico' }
    ],
    'combo-chart': [
        { id: 'axis', label: 'Eje X (Categoría / Tiempo)', placeholder: 'Arrastra campo temporal o categórico' },
        { id: 'value', label: 'Columnas (Monto / Cantidad Real)', placeholder: 'Primer campo para Barras (Eje Izq.)' },
        { id: 'detail', label: 'Línea (% PPC / Tendencia)', max: 1, placeholder: 'Segundo campo para Línea (Eje Der.)' }
    ],
    'kpi-card': [
        { id: 'value', label: 'Campos a Medir', max: 1, placeholder: 'Arrastra campo a calcular como KPI' }
    ],
    'kpi-multi': [
        { id: 'value', label: 'Valor Principal (Métrica)', max: 1, placeholder: 'Arrastra campo de métrica principal' },
        { id: 'detail', label: 'Meta / Target (Línea Base)', max: 1, placeholder: 'Opcional: Arrastra campo de meta' }
    ],
    'slicer': [
        { id: 'axis', label: 'Campo de Filtrado', max: 1, placeholder: 'Arrastra variable para filtrar dinámicamente' }
    ],
    'line': [
        { id: 'axis', label: 'Eje X (Tiempo / Categoría)', placeholder: 'Arrastra campo temporal o categoría' },
        { id: 'value', label: 'Valores (Líneas)', placeholder: 'Arrastra campos numéricos' },
        { id: 'legend', label: 'Leyenda (Desglose)', max: 1, placeholder: 'Arrastra campo de desglose' }
    ],
    'area-stacked': [
        { id: 'axis', label: 'Eje X (Tiempo / Categoría)', placeholder: 'Arrastra campo temporal' },
        { id: 'value', label: 'Valores (Áreas Apiladas)', placeholder: 'Arrastra campos numéricos' },
        { id: 'legend', label: 'Leyenda', max: 1, placeholder: 'Arrastra campo' }
    ],
    'scatter': [
        { id: 'axis', label: 'Eje X (Numérico)', max: 1, placeholder: 'Arrastra campo métrica X' },
        { id: 'value', label: 'Eje Y (Numérico)', max: 1, placeholder: 'Arrastra campo métrica Y' },
        { id: 'detail', label: 'Detalles (Etiquetas)', max: 1, placeholder: 'Arrastra campo identificador' },
        { id: 'size', label: 'Tamaño (Burbuja)', max: 1, placeholder: 'Arrastra campo métrica de tamaño' }
    ],
    'radar': [
        { id: 'axis', label: 'Categorías (Ejes Polares)', max: 1, placeholder: 'Arrastra campo categórico' },
        { id: 'value', label: 'Valores (Métrica)', max: 1, placeholder: 'Arrastra campo numérico' }
    ],
    'gauge': [
        { id: 'value', label: 'Valor (% Cumplimiento)', max: 1, placeholder: 'Arrastra campo de porcentaje' }
    ],
    'table': [
        { id: 'axis', label: 'Columnas (Campos a mostrar)', placeholder: 'Arrastra campos secuenciales para la tabla' }
    ],
    'matrix': [
        { id: 'axis', label: 'Filas (Jerarquía de Filas)', placeholder: 'Arrastra campos para crear niveles de árbol' },
        { id: 'legend', label: 'Columnas (Pivot Horizontal)', max: 1, placeholder: 'Opcional: Arrastra campo para pivotar en columnas' },
        { id: 'value', label: 'Valores (Métricas Agregadas)', placeholder: 'Arrastra campos numéricos a calcular' }
    ],
    'image': [],
    'header-banner': [],
    'container-card': [],
    'text-box': []
};

export interface SlideTheme {
    id: string;
    name: string;
    description: string;
    primaryColor: string;
    paletteColors: string[];
    bgSlideColor: string;
    cardBgColor: string;
    textColor: string;
    borderColor: string;
}

export const SLIDE_THEMES: SlideTheme[] = [
    {
        id: 'corp-ld',
        name: 'Corporativo L&D',
        description: 'Elegancia ejecutiva con azul marino, cobalto, esmeralda y ámbar.',
        primaryColor: '#2563eb',
        paletteColors: ['#2563eb', '#0284c7', '#059669', '#d97706', '#dc2626', '#4f46e5'],
        bgSlideColor: '#f8fafc',
        cardBgColor: '#ffffff',
        textColor: '#0f172a',
        borderColor: '#e2e8f0'
    },
    {
        id: 'obra-traffic',
        name: 'Obra / Semáforo',
        description: 'Enfoque en control de plazos y criticidad (Verde, Amarillo, Rojo).',
        primaryColor: '#059669',
        paletteColors: ['#059669', '#eab308', '#dc2626', '#2563eb', '#0284c7', '#84cc16'],
        bgSlideColor: '#f9fafb',
        cardBgColor: '#ffffff',
        textColor: '#111827',
        borderColor: '#e5e7eb'
    },
    {
        id: 'ocean-modern',
        name: 'Oceánico Moderno',
        description: 'Vibrante y tecnológico con cian, índigo y azul eléctrico.',
        primaryColor: '#06b6d4',
        paletteColors: ['#06b6d4', '#3b82f6', '#6366f1', '#8b5cf6', '#14b8a6', '#0ea5e9'],
        bgSlideColor: '#f0fdfa',
        cardBgColor: '#ffffff',
        textColor: '#0f172a',
        borderColor: '#ccfbf1'
    },
    {
        id: 'monochrome',
        name: 'Escala de Grises',
        description: 'Aspecto formal minimalista apto para impresiones y memorias.',
        primaryColor: '#334155',
        paletteColors: ['#1e293b', '#334155', '#475569', '#64748b', '#94a3b8', '#cbd5e1'],
        bgSlideColor: '#f8fafc',
        cardBgColor: '#ffffff',
        textColor: '#020617',
        borderColor: '#cbd5e1'
    },
    {
        id: 'dark-executive',
        name: 'Dark Ejecutivo',
        description: 'Modo oscuro de alto impacto para salas de conferencia.',
        primaryColor: '#38bdf8',
        paletteColors: ['#38bdf8', '#818cf8', '#34d399', '#fbbf24', '#f87171', '#c084fc'],
        bgSlideColor: '#090d16',
        cardBgColor: '#131b2e',
        textColor: '#f8fafc',
        borderColor: '#1e293b'
    }
];

export const COMPONENT_HIERARCHIES: Record<string, string[]> = {
    scurve: ['Semana', 'Tipo'],
    intermediate: ['Planificación Intermedia #', 'Tipo de Restricción', 'Responsable', 'Estado'],
    ppc: ['Responsable', 'Categoría C.N.C', 'C.N.C', 'Estado'],
    quality: ['Partida', 'Nivel', 'Estado'],
    subcontracts: ['Subcontrato', 'Partida', 'Unidad Fisica'],
    metrics: ['Módulo', 'Indicador']
};

interface LocalAnalyticsContainerProps {
    reportId?: string;
    projectId?: string;
    currentUser?: GlobalUser;
    initialMode?: 'view' | 'edit';
    canView?: boolean;
    canDesign?: boolean;
    canModifyDesign?: boolean;
    context?: 'consumption' | 'designer';
    action?: 'view' | 'edit' | 'create';
    onReportSaved?: (reportId: string, name: string, fullConfig?: any) => void;
    onSaveTemplate?: (templateData: MasterReportTemplate) => void;
    onCancel?: () => void;
    datasetId?: string;
    initialCategories?: string[];
    initialName?: string;
    initialDescription?: string;
    initialCategory?: string;
    initialBadge?: string;
    initialScope?: 'obra' | 'multiobra' | 'corporativo';
    projectsList?: any[];
    initialProjects?: string[];
    initialConfig?: any;
    initialWidgets?: WidgetConfig[];
    templateId?: string;
    isSlideEmbed?: boolean;
}

export const LocalAnalyticsContainer: React.FC<LocalAnalyticsContainerProps> = ({
    reportId,
    projectId: propProjectId,
    currentUser,
    initialMode = 'view',
    canView = false,
    canDesign = false,
    canModifyDesign = false,
    context = 'consumption',
    action = 'view',
    onReportSaved,
    onSaveTemplate,
    onCancel,
    datasetId,
    initialCategories,
    initialName,
    initialDescription,
    initialCategory,
    initialBadge,
    initialScope,
    projectsList = [],
    initialProjects,
    initialConfig,
    initialWidgets,
    templateId,
    isSlideEmbed = false,
}) => {
    const { instance, accounts } = useMsal();
    const [mode, setMode] = useState<'view' | 'edit'>(initialMode);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<{ code?: string; message: string; requestId?: string } | null>(null);
    const [config, setConfig] = useState<Partial<AnalyticsEmbedConfig> & { isMocked?: boolean; name?: string; metadata?: any }>({});
    const [status] = useState(getAnalyticsConfigStatus());
    const [showDiagPanel, setShowDiagPanel] = useState<boolean>(false);
    const refreshTimerRef = useRef<any>(null);
    const embedContainerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (initialMode) {
            setMode(initialMode);
        }
    }, [initialMode]);

    // --- CARGA DE DATOS LOCALES PARA EL MOTOR INTERNO ---
    const [allProjects] = useSyncState<any[]>('data', 'projects', []);
    const activeProjectId = typeof window !== 'undefined' ? (localStorage.getItem('selectedProjectId') || 'data') : 'data';

    // Determinar obra referencial para el diseñador de plantillas maestras ("318 - 94 Viviendas Las Quilas")
    const referentialProject = useMemo(() => {
        if (!allProjects || allProjects.length === 0) return null;
        return allProjects.find(p => {
            const name = (p.name || '').toLowerCase();
            const code = (p.code || '').toLowerCase();
            const id = String(p.id || '').toLowerCase();
            return name.includes('las quilas') || name.includes('318') || code.includes('318') || id.includes('318');
        }) || allProjects[0] || null;
    }, [allProjects]);

    // Determinar si estamos en el diseñador corporativo del maestro de reportes (sin obra activa seleccionada)
    const isMasterCorporateDesigner = context === 'designer' && (!propProjectId || propProjectId === 'global' || propProjectId === 'data') && (!activeProjectId || activeProjectId === 'data');

    // Resolución de la obra efectiva:
    // 1. Si viene propProjectId explícito y válido (de la obra activa), se usa estrictamente ese.
    // 2. Si no viene propProjectId pero hay una obra activa seleccionada en el sistema (activeProjectId !== 'data'), se usa activeProjectId.
    // 3. Únicamente si estamos en el diseñador corporativo de plantillas maestras sin obra, se toma como referencia "Las Quilas" para previsualizar.
    const effectiveProjectId = (propProjectId && propProjectId !== 'global' && propProjectId !== 'data')
        ? propProjectId
        : (!isMasterCorporateDesigner && activeProjectId && activeProjectId !== 'data')
            ? activeProjectId
            : (referentialProject ? referentialProject.id : 'data');

    const collectionName = effectiveProjectId !== 'data' ? `project_${effectiveProjectId}` : 'data';

    const [localProjectInfo] = useSyncState<any>(collectionName, 'projectInfo', {
        name: 'Obra Activa',
        startDate: '2026-01-05',
        endDate: '2026-12-31'
    });
    const [localTasks] = useSyncState<Task[]>(collectionName, 'tasks', []);
    const [localUnits] = useSyncState<PhysicalUnit[]>(collectionName, 'units', []);
    const [localMatrixData] = useSyncState<Record<string, any>>(collectionName, 'matrixData', {});
    const [localProtocolConfigs] = useSyncState<Record<string, any>>(collectionName, 'protocolConfigs', {});
    const [localSavedProtocols] = useSyncState<Record<string, any>>(collectionName, 'savedProtocols', {});
    const [localWeeklyPlans] = useSyncState<any[]>(collectionName, 'weeklyPlans', []);
    const [localContracts] = useSyncState<any[]>(collectionName, 'contracts', []);
    const [localPaymentStatements] = useSyncState<any[]>(collectionName, 'paymentStatements', []);
    const [localProgressHistory] = useSyncState<ProjectProgressSnapshot[]>(collectionName, 'progressHistory', []);
    const [localLookaheadPlans] = useSyncState<any[]>(collectionName, 'lookaheadPlans', []);
    const [localResponsibles] = useSyncState<any[]>(collectionName, 'responsibles', []);
    const [localSubcontractors] = useSyncState<any[]>(collectionName, 'subcontractors', []);
    const [globalUsers] = useSyncState<any[]>('data', 'globalUsers', []);
    const [globalProfiles] = useSyncState<any[]>('data', 'globalProfiles', []);
    const [incidences] = useSyncState<any[]>(collectionName, 'incidences', []);
    const [localLevels] = useSyncState<any[]>(collectionName, 'levels', []);
    const [globalCNCGroupsData] = useSyncState<any[]>('data', 'globalCNCGroups', []);
    const [legacyCNCGroups] = useSyncState<any[]>('data', 'cnc', []);
    const [localCNCGroups] = useSyncState<any[]>(collectionName, 'cncGroups', []);
    const [localConstraintGroups] = useSyncState<any[]>(collectionName, 'constraintGroups', []);
    const [globalConstraintGroups] = useSyncState<any[]>('data', 'globalConstraintGroups', []);

    const DEFAULT_COLUMNS: Record<string, string[]> = {
        kpi: ['Métrica', 'Valor', 'Detalle'],
        scurve: [
            'Semana',
            'Fecha',
            'Estado',
            '% Sem. Prog.',
            '% Sem. Real',
            'Diff. Semanal',
            '% Acum. Prog.',
            '% Acum. Real',
            'Diff. Acumulada',
            'Atraso Curva S (Días)',
            'Atraso Ruta Crítica (Días)'
        ],
        intermediate: ['Planificación Intermedia #', 'Nombre Tarea', 'Fecha Inicio', 'Fecha Termino', 'Restricción', 'Tipo de Restricción', 'Responsable', 'Fecha Requerida', 'Estado', 'Observaciones'],
        ppc: ['Planificación Semanal #', 'Fecha de Inicio', 'Fecha de Termino', 'Nivel', 'Unidades Físicas', 'Tarea', 'Responsable', 'L', 'M', 'X', 'J', 'V', 'S', '% Comprometido', '% Cumplimiento', 'Estado', 'Categoría C.N.C', 'C.N.C'],
        quality: ['Número', 'Partida', 'Nivel', 'Unidad Física', 'Estado Protocolo', 'Etapa', 'Responsable', 'Estado Firma', 'Fecha Firma / Mod', 'Total Revisores'],
        subcontracts: [
            'Subcontrato', 
            'Partida', 
            'Nivel', 
            'Unidad Fisica', 
            'Cant. Contractual', 
            'Val. Unitario', 
            'Valor Total', 
            'Avance Acumulado - Cant.', 
            'Avance Acumulado - %', 
            'Avance Acumulado - Total $', 
            'Avance Anterior - Cant.', 
            'Avance Anterior - %', 
            'Avance Anterior - Total $', 
            'Avance Actual EP - Cant.', 
            'Avance Actual EP - %', 
            'Avance Actual EP - Total $'
        ],
        metrics: ['Indicador', 'Módulo', 'Valor', 'Valor Numérico', 'Detalle']
    };

    const [selectedColumns, setSelectedColumns] = useState<Record<string, string[]>>({
        kpi: [...DEFAULT_COLUMNS.kpi],
        scurve: [...DEFAULT_COLUMNS.scurve],
        intermediate: [...DEFAULT_COLUMNS.intermediate],
        ppc: [...DEFAULT_COLUMNS.ppc],
        quality: [...DEFAULT_COLUMNS.quality],
        subcontracts: [...DEFAULT_COLUMNS.subcontracts],
        metrics: [...DEFAULT_COLUMNS.metrics]
    });

    // --- ESTADOS DEL DISEÑADOR LOCAL MOCK ---
    const [reportTitle, setReportTitle] = useState<string>(initialName || initialConfig?.name || '');
    const [reportScope, setReportScope] = useState<'obra' | 'multiobra' | 'corporativo'>(initialScope || 'obra');
    const [selectedProjects, setSelectedProjects] = useState<string[]>(initialProjects || [activeProjectId]);
    const [reportCategories, setReportCategories] = useState<string[]>(initialCategories || []);
    const [reportDescription, setReportDescription] = useState<string>(initialDescription || initialConfig?.description || '');
    const [reportCategory, setReportCategory] = useState<string>(initialCategory || initialConfig?.category || 'General');
    const [reportBadge, setReportBadge] = useState<string>(initialBadge || initialConfig?.badge || 'Personalizado');
    const [selectedComponents, setSelectedComponents] = useState<string[]>(() => {
        const defaultComps = ['scurve', 'intermediate', 'ppc', 'quality', 'subcontracts', 'metrics'];
        if (initialCategories && initialCategories.length > 0) {
            const comps: string[] = [];
            if (initialCategories.includes('Planificación')) comps.push('scurve', 'intermediate', 'ppc');
            if (initialCategories.includes('Calidad')) comps.push('quality');
            if (initialCategories.includes('Subcontratos')) comps.push('subcontracts');
            comps.push('metrics'); // Métricas siempre disponible
            return comps.length > 0 ? comps : defaultComps;
        }
        return defaultComps;
    });
    const [savingLocal, setSavingLocal] = useState<boolean>(false);

    // --- SISTEMA DE HOJAS / PÁGINAS DEL REPORTE (POWER BI / SLIDES 16:9) ---
    const [reportPages, setReportPages] = useState<ReportPage[]>([
        { id: 'page-1', name: 'Diapositiva 1' }
    ]);
    const [activePageId, setActivePageId] = useState<string>('page-1');
    const [editingPageId, setEditingPageId] = useState<string | null>(null);
    const [editingPageName, setEditingPageName] = useState<string>('');

    // --- TEMA GLOBAL Y MODO PRESENTACIÓN ---
    const [activeThemeId, setActiveThemeId] = useState<string>('corp-ld');
    const [isPresentationMode, setIsPresentationMode] = useState<boolean>(false);
    const [showPresetModal, setShowPresetModal] = useState<boolean>(false);
    const [showSaveConfirmModal, setShowSaveConfirmModal] = useState<boolean>(false);
    const [showCancelWarningModal, setShowCancelWarningModal] = useState<boolean>(false);
    const [showImagePickerModal, setShowImagePickerModal] = useState<boolean>(false);
    const [targetImageWidgetId, setTargetImageWidgetId] = useState<string | null>(null);

    // --- ESTADOS DE VISUALIZACIÓN ANALÍTICA LOCAL (DASHBOARD & CAPA DAX) ---
    const [widgets, setWidgets] = useState<WidgetConfig[]>(() => {
        if (initialWidgets && Array.isArray(initialWidgets) && initialWidgets.length > 0) {
            return initialWidgets;
        }
        if (initialConfig?.widgets && Array.isArray(initialConfig.widgets) && initialConfig.widgets.length > 0) {
            return initialConfig.widgets;
        }
        return [];
    });
    const [selectedWidgetId, setSelectedWidgetId] = useState<string | null>(null);
    const [rightPanelVisOpen, setRightPanelVisOpen] = useState<boolean>(true);
    const [rightPanelDataOpen, setRightPanelDataOpen] = useState<boolean>(true);
    const [draggedField, setDraggedField] = useState<{ fieldKey: string; sourceComponentId?: string } | null>(null);
    const [draggingWidget, setDraggingWidget] = useState<{ id: string; startCol: number; startRow: number; colSpan: number; rowSpan: number; grabColOffset?: number; grabRowOffset?: number } | null>(null);
    const [dragOverCell, setDragOverCell] = useState<{ col: number; row: number } | null>(null);
    
    // Estado para Redimensionamiento Interactivo de Bordes con Snap to Grid
    const [resizingWidget, setResizingWidget] = useState<{
        id: string;
        direction: 'e' | 'w' | 's' | 'n' | 'se' | 'sw' | 'ne' | 'nw';
        initialColStart: number;
        initialRowStart: number;
        initialColSpan: number;
        initialRowSpan: number;
        gridRect: DOMRect;
    } | null>(null);

    // Estado para Visuales de Tabla y Matriz (Ordenamiento y Expansión de Nodos)
    const [tableSortConfig, setTableSortConfig] = useState<Record<string, { field: string; direction: 'asc' | 'desc' }>>({});
    const [matrixExpandedNodes, setMatrixExpandedNodes] = useState<Record<string, Record<string, boolean>>>({});

    const [activeEditingComponent, setActiveEditingComponent] = useState<string>('scurve');
    const [calculatedColumns, setCalculatedColumns] = useState<CalculatedColumn[]>([]);
    const [showCalcModal, setShowCalcModal] = useState<boolean>(false);
    const [newCalcForm, setNewCalcForm] = useState<{
        name: string;
        calcType: 'math' | 'text_concat' | 'text_transform' | 'conditional_if' | 'date_diff';
        colA: string;
        operator: '+' | '-' | '*' | '/';
        colB: string;
        constantValue: number;
        concatFields: string[];
        concatSeparator: string;
        transformField: string;
        transformType: 'uppercase' | 'lowercase' | 'trim';
        conditionField: string;
        conditionOperator: 'equals' | 'not_equals' | 'greater' | 'less' | 'contains' | 'not_contains';
        conditionValue: string;
        thenValue: string;
        elseValue: string;
        dateFieldA: string;
        dateFieldB: string;
    }>({
        name: '',
        calcType: 'math',
        colA: '',
        operator: '-',
        colB: '',
        constantValue: 0,
        concatFields: [],
        concatSeparator: ' - ',
        transformField: '',
        transformType: 'uppercase',
        conditionField: '',
        conditionOperator: 'equals',
        conditionValue: '',
        thenValue: '',
        elseValue: '',
        dateFieldA: '',
        dateFieldB: ''
    });

    const [crossFilteringActive, setCrossFilteringActive] = useState<boolean>(false);
    const [crossFilter, setCrossFilter] = useState<{ key: string; value: any; componentId: string } | null>(null);
    const [slicerFilters, setSlicerFilters] = useState<Record<string, any>>({}); // fieldKey -> selected value(s)
    const [openDropdownSlicers, setOpenDropdownSlicers] = useState<Record<string, boolean>>({}); // widgetId -> boolean open state
    const [widgetHierarchyStates, setWidgetHierarchyStates] = useState<Record<string, { level: number; path: string[] }>>({});

    // --- JERARQUÍA DE FECHAS DINÁMICA Y FILTROS EN CASCADA (POWER BI STYLE) ---
    // Nivel 1: Año (YYYY) | Nivel 2: Mes (Mes / MM) | Nivel 3: Inicio de Semana (Lunes DD/MM/YYYY)
    const [selectedDateYear, setSelectedDateYear] = useState<string>('todos');
    const [selectedDateMonth, setSelectedDateMonth] = useState<string>('todos');
    const [selectedDateWeekStart, setSelectedDateWeekStart] = useState<string>('todos');

    // Cerrar automáticamente los desplegables de segmentación al hacer clic fuera
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement | null;
            if (target && target.closest('[data-slicer-dropdown="true"]')) {
                return;
            }
            setOpenDropdownSlicers(prev => {
                const hasOpen = Object.values(prev).some(Boolean);
                return hasOpen ? {} : prev;
            });
        };
        window.addEventListener('click', handleClickOutside);
        return () => window.removeEventListener('click', handleClickOutside);
    }, []);

    // Listener global para redimensionamiento fluido con Snap a las 12 Columnas × 7 Filas
    useEffect(() => {
        if (!resizingWidget) return;

        const handlePointerMove = (e: PointerEvent) => {
            const { id, direction, initialColStart, initialRowStart, initialColSpan, initialRowSpan, gridRect } = resizingWidget;
            const mouseX = e.clientX - gridRect.left;
            const mouseY = e.clientY - gridRect.top;

            // Calcular celda objetivo 1-based bajo el puntero (Grilla 16 Cols × 9 Filas)
            const currentHoverCol = Math.max(1, Math.min(16, Math.floor((mouseX / gridRect.width) * 16) + 1));
            const currentHoverRow = Math.max(1, Math.min(9, Math.floor((mouseY / gridRect.height) * 9) + 1));

            setWidgets(prev => prev.map(w => {
                if (w.id !== id) return w;

                let nextColStart = initialColStart;
                let nextColSpan = initialColSpan;
                let nextRowStart = initialRowStart;
                let nextRowSpan = initialRowSpan;

                // Redimensionamiento horizontal este (derecha)
                if (direction.includes('e')) {
                    const newSpan = Math.max(1, Math.min(16 - initialColStart + 1, currentHoverCol - initialColStart + 1));
                    nextColSpan = newSpan;
                }
                // Redimensionamiento horizontal oeste (izquierda)
                if (direction.includes('w')) {
                    const initialRightCol = initialColStart + initialColSpan - 1;
                    const newStart = Math.max(1, Math.min(initialRightCol, currentHoverCol));
                    nextColStart = newStart;
                    nextColSpan = initialRightCol - newStart + 1;
                }
                // Redimensionamiento vertical sur (abajo)
                if (direction.includes('s')) {
                    const newRowSpanCalc = Math.max(1, Math.min(9 - initialRowStart + 1, currentHoverRow - initialRowStart + 1));
                    nextRowSpan = newRowSpanCalc;
                }
                // Redimensionamiento vertical norte (arriba)
                if (direction.includes('n')) {
                    const initialBottomRow = initialRowStart + initialRowSpan - 1;
                    const newStartRow = Math.max(1, Math.min(initialBottomRow, currentHoverRow));
                    nextRowStart = newStartRow;
                    nextRowSpan = initialBottomRow - newStartRow + 1;
                }

                return {
                    ...w,
                    gridColStart: nextColStart,
                    colSpan: nextColSpan,
                    gridRowStart: nextRowStart,
                    rowSpan: nextRowSpan
                };
            }));
        };

        const handlePointerUp = () => {
            setResizingWidget(null);
        };

        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);
        return () => {
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
        };
    }, [resizingWidget]);

    // Navegación en Modo Presentación (Teclas Flecha Izquierda / Derecha / Escape)
    useEffect(() => {
        if (!isPresentationMode) return;

        const handlePresentationKeys = (e: KeyboardEvent) => {
            const pageIndex = reportPages.findIndex(p => p.id === activePageId);
            if (e.key === 'ArrowRight' || e.key === 'Space' || e.key === 'PageDown') {
                e.preventDefault();
                if (pageIndex < reportPages.length - 1) {
                    setActivePageId(reportPages[pageIndex + 1].id);
                }
            } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
                e.preventDefault();
                if (pageIndex > 0) {
                    setActivePageId(reportPages[pageIndex - 1].id);
                }
            } else if (e.key === 'Escape') {
                setIsPresentationMode(false);
            }
        };

        window.addEventListener('keydown', handlePresentationKeys);
        return () => window.removeEventListener('keydown', handlePresentationKeys);
    }, [isPresentationMode, reportPages, activePageId]);

    // Eliminación de gráfico mediante tecla Suprimir (Delete) o Backspace
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!selectedWidgetId) return;

            // Ignorar si el usuario está escribiendo en un input, textarea o select
            const target = e.target as HTMLElement;
            if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable)) {
                return;
            }

            if (e.key === 'Delete' || e.key === 'Backspace') {
                e.preventDefault();
                setWidgets(prev => prev.filter(w => w.id !== selectedWidgetId));
                setSelectedWidgetId(null);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedWidgetId]);
    
    // Configuración del Widget temporal a añadir (soporte legacy y creación rápida)
    const [selectedVisualType, setSelectedVisualType] = useState<WidgetConfig['type'] | null>(null);
    const [tempXAxisKey, setTempXAxisKey] = useState<string>('');
    const [tempYAxisKey, setTempYAxisKey] = useState<string>('');
    const [tempWidth, setTempWidth] = useState<'1/3' | '1/2' | 'full'>('1/2');

    // Capa DAX Local y Hoja de Cálculo
    const [runningTotalCol, setRunningTotalCol] = useState<string>('');
    const [movingAverageCol, setMovingAverageCol] = useState<string>('');
    const [movingAveragePeriod, setMovingAveragePeriod] = useState<number>(3);
    const [deltaCol, setDeltaCol] = useState<string>('');
    
    // Concatenador con Delimitador Personalizado
    const [concatColA, setConcatColA] = useState<string>('');
    const [concatColB, setConcatColB] = useState<string>('');
    const [concatDelimiter, setConcatDelimiter] = useState<string>(' - ');

    // Filtros múltiples avanzados AND/OR (Nivel Tabla / Hoja de Datos)
    const [filterRules, setFilterRules] = useState<any[]>([]);
    const [filterJoinType, setFilterJoinType] = useState<'AND' | 'OR'>('AND');

    // Estado de pestañas en el panel de Visualizaciones (Campos vs Formato Visual)
    const [visPanelMainTab, setVisPanelMainTab] = useState<'fields' | 'format'>('fields');
    const [visFormatSubTab, setVisFormatSubTab] = useState<'visual' | 'general'>('visual');

    // Estado del creador de Filtros Lógicos a nivel de Gráfico/Widget
    const [isAddingVisualFilter, setIsAddingVisualFilter] = useState<boolean>(false);
    const [filterTargetField, setFilterTargetField] = useState<string>('');
    const [filterMode, setFilterMode] = useState<'basic' | 'advanced'>('basic');
    const [filterAdvancedOp, setFilterAdvancedOp] = useState<LogicalFilterRule['operator']>('not_equals');
    const [filterAdvancedVal, setFilterAdvancedVal] = useState<string>('-');
    const [filterBasicChecked, setFilterBasicChecked] = useState<Record<string, boolean>>({});
    
    // Reglas de Formato Condicional
    const [cfRules, setCfRules] = useState<any[]>([]);

    // Formulario de Columnas Calculadas
    const [newCalcName, setNewCalcName] = useState<string>('');
    const [newCalcColA, setNewCalcColA] = useState<string>('');
    const [newCalcOperator, setNewCalcOperator] = useState<'+' | '-' | '*' | '/'>('+');
    const [newCalcColB, setNewCalcColB] = useState<string>('');
    const [newCalcConstVal, setNewCalcConstVal] = useState<number>(0);
    const [showCalcCreator, setShowCalcCreator] = useState<boolean>(false);

    // --- ESTADOS DEL VISOR LOCAL MOCK ---
    const [localActiveTab, setLocalActiveTab] = useState<string>('resumen');

    // Sincronizar props iniciales con estados locales cuando cambian
    useEffect(() => {
        if (initialName) {
            setReportTitle(initialName);
        }
    }, [initialName]);

    useEffect(() => {
        if (initialScope) {
            setReportScope(initialScope);
        }
    }, [initialScope]);

    useEffect(() => {
        if (initialCategories && initialCategories.length > 0) {
            setReportCategories(initialCategories);
        }
    }, [initialCategories]);

    // Inicializar el diseñador con datos si es modo edición/creación
    useEffect(() => {
        if (config.name) {
            setReportTitle(config.name);
        }
        if (config.metadata?.categories) {
            setReportCategories(config.metadata.categories);
        } else if (config.metadata?.category) {
            setReportCategories([config.metadata.category]);
        }
        if (config.metadata?.description) {
            setReportDescription(config.metadata.description);
        }
        if (config.metadata?.components) {
            setSelectedComponents(config.metadata.components);
        }
        
        // Cargar páginas del reporte (Power BI Pages)
        if (config.metadata?.reportPages && Array.isArray(config.metadata.reportPages) && config.metadata.reportPages.length > 0) {
            setReportPages(config.metadata.reportPages);
            setActivePageId(config.metadata.reportPages[0].id);
        } else {
            setReportPages([{ id: 'page-1', name: 'Página 1' }]);
            setActivePageId('page-1');
        }

        // Cargar widgets analíticos multigráficos con migración retrocompatible
        if (config.metadata?.widgets && Array.isArray(config.metadata.widgets)) {
            const migratedWidgets: WidgetConfig[] = config.metadata.widgets.map((w: any) => {
                const migrated = { ...w };
                if (!migrated.axisFields && migrated.xAxisKey) {
                    migrated.axisFields = [{ fieldKey: migrated.xAxisKey, aggregation: 'none' }];
                }
                if (!migrated.valueFields && migrated.yAxisKey) {
                    migrated.valueFields = [{ fieldKey: migrated.yAxisKey, aggregation: 'sum' }];
                }
                return migrated;
            });
            setWidgets(migratedWidgets);
            if (migratedWidgets.length > 0) {
                setSelectedWidgetId(migratedWidgets[0].id);
            }
        } else {
            setWidgets([]);
        }

        // Cargar operaciones y lógica analítica avanzada (DAX)
        if (config.metadata?.daxOperations) {
            const ops = config.metadata.daxOperations;
            setRunningTotalCol(ops.runningTotalCol || '');
            setMovingAverageCol(ops.movingAverageCol || '');
            setMovingAveragePeriod(ops.movingAveragePeriod || 3);
            setDeltaCol(ops.deltaCol || '');
            setConcatColA(ops.concatColA || '');
            setConcatColB(ops.concatColB || '');
            setConcatDelimiter(ops.concatDelimiter || ' - ');
            setFilterRules(ops.filterRules || []);
            setFilterJoinType(ops.filterJoinType || 'AND');
            setCfRules(ops.cfRules || []);
        } else {
            setRunningTotalCol('');
            setMovingAverageCol('');
            setMovingAveragePeriod(3);
            setDeltaCol('');
            setConcatColA('');
            setConcatColB('');
            setConcatDelimiter(' - ');
            setFilterRules([]);
            setFilterJoinType('AND');
            setCfRules([]);
        }

        if (config.metadata?.selectedColumns) {
            setSelectedColumns(config.metadata.selectedColumns);
        } else {
            setSelectedColumns({
                kpi: [...DEFAULT_COLUMNS.kpi],
                scurve: [...DEFAULT_COLUMNS.scurve],
                intermediate: [...DEFAULT_COLUMNS.intermediate],
                ppc: [...DEFAULT_COLUMNS.ppc],
                quality: [...DEFAULT_COLUMNS.quality],
                subcontracts: [...DEFAULT_COLUMNS.subcontracts]
            });
        }

        // Cargar columnas calculadas y filtrado cruzado
        if (config.metadata?.calculatedColumns) {
            setCalculatedColumns(config.metadata.calculatedColumns);
        } else {
            setCalculatedColumns([]);
        }
        setCrossFilteringActive(!!config.metadata?.crossFilteringActive);
        setCrossFilter(null);
        setWidgetHierarchyStates({});
    }, [config]);

    const getFreshMsalToken = async () => {
        const account = instance.getActiveAccount() || accounts[0];
        if (account) {
            try {
                const response = await instance.acquireTokenSilent({
                    scopes: ["User.Read"],
                    account: account
                });
                localStorage.setItem('klyd_msal_access_token', response.accessToken);
                return response.accessToken;
            } catch (e) {
                console.warn("Silent token acquisition failed:", e);
            }
        }
        return null;
    };

    const isAdmin = currentUser?.globalRole === 'Administrador' || currentUser?.globalRole === ('SYSADMIN' as any);

    const scheduleTokenRefresh = (expirationTimeStr?: string) => {
        if (refreshTimerRef.current) {
            clearTimeout(refreshTimerRef.current);
            refreshTimerRef.current = null;
        }

        if (!expirationTimeStr || config.isMocked) return;

        const expirationDate = new Date(expirationTimeStr);
        const now = new Date();
        const timeToExpiration = expirationDate.getTime() - now.getTime();

        const leadTime = 5 * 60 * 1000;
        let refreshIn = timeToExpiration - leadTime;
        if (refreshIn < 0) {
            refreshIn = Math.max(30 * 1000, timeToExpiration - 10 * 1000);
        }

        console.log(`Scheduling token refresh in ${(refreshIn / 1000).toFixed(0)} seconds.`);

        refreshTimerRef.current = setTimeout(() => {
            console.log("Triggering scheduled Embed Token refresh...");
            refreshToken();
        }, refreshIn);
    };

    const refreshToken = async () => {
        if (config.isMocked) return;
        try {
            await getFreshMsalToken();
            const effectiveMode = (mode === 'edit' && !isAdmin && !canModifyDesign) ? 'view' : mode;
            const res = await fetchAnalyticsEmbedToken(reportId, effectiveMode, context, action, datasetId);
            setConfig(res);
            scheduleTokenRefresh(res.expiresAt);
        } catch (err: any) {
            console.error("Failed to refresh analytics token, retrying in 30 seconds:", err);
            refreshTimerRef.current = setTimeout(refreshToken, 30 * 1000);
        }
    };

    useEffect(() => {
        let isMounted = true;
        setLoading(true);
        setError(null);

        const effectiveMode = (mode === 'edit' && !isAdmin && !canModifyDesign) ? 'view' : mode;
        getFreshMsalToken()
            .catch(err => {
                console.warn("MSAL token fetch warning, continuing in local mode:", err);
            })
            .finally(() => {
                fetchAnalyticsEmbedToken(reportId, effectiveMode, context, action, datasetId)
                    .then((res) => {
                        if (isMounted) {
                            // Si initialConfig viene con datos directos del reporte (ej. catálogo de Supabase), unificamos
                            const mergedName = res?.name || initialConfig?.name || initialName;
                            const mergedMetadata = res?.metadata || initialConfig?.metadata || {};
                            
                            setConfig({
                                ...res,
                                name: mergedName,
                                metadata: mergedMetadata,
                                isMocked: true
                            });
                            scheduleTokenRefresh(res.expiresAt);
                            setError(null);
                            setLoading(false);
                        }
                    })
                    .catch((err: any) => {
                        console.warn("Activando motor de analítica local tras excepción de conexión:", err);
                        if (isMounted) {
                            setConfig({
                                reportId: reportId || 'local-report-id',
                                accessToken: 'local-analytics-token',
                                embedUrl: '',
                                tokenType: 'Embed',
                                permissions: 'ReadWrite',
                                status: 'OK',
                                name: initialConfig?.name || initialName,
                                metadata: initialConfig?.metadata || {},
                                isMocked: true
                            });
                            setError(null);
                            setLoading(false);
                        }
                    });
            });

        return () => {
            isMounted = false;
            if (refreshTimerRef.current) {
                clearTimeout(refreshTimerRef.current);
                refreshTimerRef.current = null;
            }
        };
    }, [reportId, mode, action, datasetId, initialConfig]);

    // --- CÁLCULOS ANALÍTICOS LOCALES ---
    const formatCLP = (val: number) => {
        return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(val);
    };

    const projectProgress = useMemo(() => {
        if (!localTasks || localTasks.length === 0) return 0;
        try {
            return calculateProjectProgress(
                localTasks,
                localUnits,
                localMatrixData,
                localProtocolConfigs,
                localProjectInfo,
                localSavedProtocols,
                localWeeklyPlans
            );
        } catch (e) {
            console.error(e);
            return 0;
        }
    }, [localTasks, localUnits, localMatrixData, localProtocolConfigs, localProjectInfo, localSavedProtocols, localWeeklyPlans]);

    const ppcMetrics = useMemo(() => {
        let executed = 0;
        let notExecuted = 0;
        let missing = 0;

        localWeeklyPlans.forEach((plan: any) => {
            if (plan.status === 'Cerrada' && Array.isArray(plan.allocations)) {
                plan.allocations.forEach((alloc: any) => {
                    if (alloc.isWeeklySubtask === true) return; // Regla de negocio de AGENTS.md
                    if (alloc.executed === true) {
                        executed++;
                    } else if (alloc.executed === false) {
                        notExecuted++;
                    } else {
                        missing++;
                    }
                });
            }
        });

        const totalValid = executed + notExecuted;
        const ppcValid = totalValid > 0 ? (executed / totalValid) * 100 : 0;
        const totalGross = executed + notExecuted + missing;
        const ppcGross = totalGross > 0 ? (executed / totalGross) * 100 : 0;

        return {
            ppcValid,
            ppcGross,
            executed,
            notExecuted,
            missing,
            totalValid
        };
    }, [localWeeklyPlans]);

    const qualityMetrics = useMemo(() => {
        let openCount = 0;
        let closedCount = 0;
        let rejectedCount = 0;

        Object.values(localSavedProtocols).forEach((p: any) => {
            if (p) {
                const s = (p.status || '').toLowerCase();
                if (s.includes('cerr') || s === 'closed' || s === 'approved') {
                    closedCount++;
                } else if (s.includes('rech') || s === 'rejected' || s === 'failed') {
                    rejectedCount++;
                } else {
                    openCount++;
                }
            }
        });

        const total = openCount + closedCount + rejectedCount;
        return { total, openCount, closedCount, rejectedCount };
    }, [localSavedProtocols]);

    const financialMetrics = useMemo(() => {
        let contracted = 0;
        let approved = 0;
        let retentions = 0;

        localContracts.forEach((c: any) => {
            if (c && c.status !== 'Anulado' && !c.isDeleted) {
                contracted += (c.amount || 0);
            }
        });

        localPaymentStatements.forEach((ps: any) => {
            if (ps && ps.status === 'Aprobado') {
                approved += (ps.netAmount || 0);
                retentions += (ps.retentionAmount || (ps.netAmount * 0.05) || 0);
            }
        });

        return { contracted, approved, retentions };
    }, [localContracts, localPaymentStatements]);

    const kpiRows = useMemo(() => [
        { 'Métrica': 'Avance Físico', 'Valor': `${projectProgress.toFixed(2)}%`, 'Detalle': 'Global Ponderado' },
        { 'Métrica': 'PPC Válido', 'Valor': `${ppcMetrics.ppcValid.toFixed(1)}%`, 'Detalle': 'Last Planner Standard' },
        { 'Métrica': 'Pagos Aprobados', 'Valor': formatCLP(financialMetrics.approved), 'Detalle': 'Neto Subcontratos' },
        { 'Métrica': 'Garantía Retenida', 'Valor': formatCLP(financialMetrics.retentions), 'Detalle': 'Retención Fija 5%' }
    ], [projectProgress, ppcMetrics, financialMetrics]);

    // 6. Métricas del Proyecto — Indicadores estándar consolidados
    const metricsRows = useMemo(() => {
        // PCR: Porcentaje de Compromisos Requeridos liberados (restricciones del último Lookahead)
        let pcrPercent = 0;
        if (localLookaheadPlans && localLookaheadPlans.length > 0) {
            const sortedPlans = [...localLookaheadPlans].sort((a, b) =>
                new Date(b.startDate || 0).getTime() - new Date(a.startDate || 0).getTime()
            );
            const latestPlan = sortedPlans[0];
            if (latestPlan && Array.isArray(latestPlan.constraints) && latestPlan.constraints.length > 0) {
                const total = latestPlan.constraints.length;
                const liberated = latestPlan.constraints.filter((c: any) => {
                    const s = (c.status || '').toLowerCase();
                    return s === 'liberada' || s === 'liberado' || s === 'released' || s === 'closed' || s.includes('cerr') || s.includes('liber');
                }).length;
                pcrPercent = (liberated / total) * 100;
            }
        }

        // Confiabilidad: % de protocolos cerrados/aprobados sobre el total
        const confiabilidadPercent = qualityMetrics.total > 0
            ? (qualityMetrics.closedCount / qualityMetrics.total) * 100
            : 0;

        return [
            {
                'Indicador': 'PPC',
                'Módulo': 'Planificación Semanal',
                'Valor': `${ppcMetrics.ppcValid.toFixed(1)}%`,
                'Valor Numérico': Number(ppcMetrics.ppcValid.toFixed(1)),
                'Detalle': 'Porcentaje de Plan Completado (válido)'
            },
            {
                'Indicador': 'PPC Bruto',
                'Módulo': 'Planificación Semanal',
                'Valor': `${ppcMetrics.ppcGross.toFixed(1)}%`,
                'Valor Numérico': Number(ppcMetrics.ppcGross.toFixed(1)),
                'Detalle': 'PPC incluyendo asignaciones no informadas'
            },
            {
                'Indicador': 'PCR',
                'Módulo': 'Planificación Intermedia',
                'Valor': `${pcrPercent.toFixed(1)}%`,
                'Valor Numérico': Number(pcrPercent.toFixed(1)),
                'Detalle': 'Porcentaje de Compromisos Requeridos liberados'
            },
            {
                'Indicador': 'Confiabilidad',
                'Módulo': 'Calidad',
                'Valor': `${confiabilidadPercent.toFixed(1)}%`,
                'Valor Numérico': Number(confiabilidadPercent.toFixed(1)),
                'Detalle': `Protocolos cerrados: ${qualityMetrics.closedCount} de ${qualityMetrics.total}`
            },
            {
                'Indicador': 'Avance Físico Global',
                'Módulo': 'Planificación Semanal',
                'Valor': `${projectProgress.toFixed(2)}%`,
                'Valor Numérico': Number(projectProgress.toFixed(2)),
                'Detalle': 'Avance ponderado global del proyecto'
            },
            {
                'Indicador': 'Monto Total Contratado',
                'Módulo': 'Subcontratos',
                'Valor': formatCLP(financialMetrics.contracted),
                'Valor Numérico': financialMetrics.contracted,
                'Detalle': 'Suma de contratos vigentes'
            },
            {
                'Indicador': 'Pagos Aprobados',
                'Módulo': 'Subcontratos',
                'Valor': formatCLP(financialMetrics.approved),
                'Valor Numérico': financialMetrics.approved,
                'Detalle': 'Monto neto de estados de pago aprobados'
            },
            {
                'Indicador': 'Garantía Retenida',
                'Módulo': 'Subcontratos',
                'Valor': formatCLP(financialMetrics.retentions),
                'Valor Numérico': financialMetrics.retentions,
                'Detalle': 'Retención acumulada de subcontratos (5%)'
            }
        ];
    }, [ppcMetrics, qualityMetrics, projectProgress, financialMetrics, localLookaheadPlans]);

    const scurvePoints = useMemo(() => {
        if (!localTasks || localTasks.length === 0) return [];
        try {
            return calculateSCurve(localTasks, localProgressHistory);
        } catch (e) {
            console.error(e);
            return [];
        }
    }, [localTasks, localProgressHistory]);

    // PPC por Semana
    const ppcTrendData = useMemo(() => {
        const sortedPlans = [...localWeeklyPlans]
            .filter(p => p.status === 'Cerrada' && p.weekRange)
            .sort((a, b) => (a.weekRange || '').localeCompare(b.weekRange || ''));

        return sortedPlans.map((p, idx) => {
            let executed = 0;
            let total = 0;
            p.allocations?.forEach((alloc: any) => {
                if (alloc.isWeeklySubtask === true) return;
                if (alloc.executed === true) {
                    executed++;
                    total++;
                } else if (alloc.executed === false) {
                    total++;
                }
            });
            const pct = total > 0 ? (executed / total) * 100 : 0;
            return {
                name: `Sem ${idx + 1}`,
                rango: p.weekRange,
                PPC: Number(pct.toFixed(1))
            };
        });
    }, [localWeeklyPlans]);

    // Datos Financieros Subcontratos
    const subcontractChartData = useMemo(() => {
        return localContracts.map(c => {
            const approvedAmount = localPaymentStatements
                .filter(ps => ps.contractId === c.id && ps.status === 'Aprobado')
                .reduce((sum, ps) => sum + (ps.netAmount || 0), 0);
            return {
                name: c.subcontractorName?.substring(0, 15) || 'Subcontratista',
                Contratado: c.amount || 0,
                Visado: approvedAmount
            };
        }).slice(0, 8); // Top 8 subcontratos
    }, [localContracts, localPaymentStatements]);

    // Mapeo dinámico de datos para Tablas Personalizadas (Excel)

    // 1. Planificación Maestra (Gantt - Cuadro de Sincronización de Avance)
    const scurveRows = useMemo(() => {
        if (!localTasks || localTasks.length === 0) return [];

        const startStr = localTasks[0]?.start || localProjectInfo?.startDate;
        const endStr = localTasks[0]?.end || localProjectInfo?.endDate;
        if (!startStr || !endStr) return [];

        const maxHistoryDateStr = localProgressHistory && localProgressHistory.length > 0
            ? localProgressHistory.reduce((max, h) => (h.date > max ? h.date : max), localProgressHistory[0].date)
            : "";

        const limitDate = (maxHistoryDateStr && maxHistoryDateStr > endStr)
            ? new Date(maxHistoryDateStr + "T12:00:00")
            : new Date(endStr + "T12:00:00");

        // Generate base weeks
        const weeksList: { index: number; dateStr: string }[] = [];
        const current = new Date(startStr + "T12:00:00");
        const syncDay = 5; // Viernes estándar de corte
        while (current.getDay() !== (syncDay % 7)) {
            current.setDate(current.getDate() + 1);
        }

        let idx = 1;
        while (current <= limitDate) {
            weeksList.push({
                index: idx++,
                dateStr: current.toISOString().split('T')[0]
            });
            current.setDate(current.getDate() + 7);
        }

        // Calculation helpers
        const getProgPct = (dateStr: string): number => {
            return calculateProgrammedProgressAtDate(localTasks, dateStr);
        };

        const flat = flattenTasks(localTasks).filter(t => t.type === 'task' && !t.isWeeklySubtask);
        let minStart = "", maxEnd = "";
        flat.forEach(t => {
            if (t.start && (!minStart || t.start < minStart)) minStart = t.start;
            if (t.end && (!maxEnd || t.end > maxEnd)) maxEnd = t.end;
        });

        let projectTotalDays = 90;
        if (minStart && maxEnd) {
            const sTime = new Date(minStart + "T12:00:00").getTime();
            const eTime = new Date(maxEnd + "T12:00:00").getTime();
            projectTotalDays = Math.max(1, Math.ceil(Math.abs(eTime - sTime) / (1000 * 60 * 60 * 24)) + 1);
        }

        const curveData = calculateSCurve(localTasks, localProgressHistory);

        return weeksList.map((week, index) => {
            const date = week.dateStr;
            const record = localProgressHistory.find(r => (r.weekIndex ? r.weekIndex === week.index : r.date === date));

            // Programmed accumulated and weekly
            const currentPlan = getProgPct(date);
            const prevWeek = weeksList[index - 1];
            const prevPlan = prevWeek ? getProgPct(prevWeek.dateStr) : 0;
            const programmedWeekly = Math.max(0, currentPlan - prevPlan);

            const prevRecord = prevWeek ? localProgressHistory.find(r => (r.weekIndex ? r.weekIndex === prevWeek.index : r.date === prevWeek.dateStr)) : null;

            let weeklyGain: number | null = null;
            let diffWeekly: number | null = null;
            let diffAccumulated: number | null = null;

            if (record) {
                if (prevRecord) {
                    weeklyGain = record.progress - prevRecord.progress;
                } else if (index === 0) {
                    weeklyGain = record.progress;
                } else {
                    let lastSyncedRecord = null;
                    for (let i = index - 1; i >= 0; i--) {
                        const r = localProgressHistory.find(h => (h.weekIndex ? h.weekIndex === weeksList[i].index : h.date === weeksList[i].dateStr));
                        if (r) {
                            lastSyncedRecord = r;
                            break;
                        }
                    }
                    weeklyGain = record.progress - (lastSyncedRecord ? lastSyncedRecord.progress : 0);
                }

                diffWeekly = weeklyGain - programmedWeekly;
                diffAccumulated = record.progress - currentPlan;
            }

            const isSynced = record !== undefined && record.progress > 0;
            const currentWeeklyGain = isSynced && weeklyGain !== null ? weeklyGain : null;
            const currentDiffWeekly = isSynced && diffWeekly !== null ? diffWeekly : null;
            const currentAccumReal = isSynced && record ? record.progress : null;
            const currentDiffAccumulated = isSynced && record ? record.progress - currentPlan : null;

            const accumRealVal = currentAccumReal !== null ? currentAccumReal : (record ? record.progress : 0);
            const calculatedCS = calculateDelayDays(curveData, accumRealVal, date, projectTotalDays, currentPlan);
            const calculatedRC = getCriticalPathDelayAtDate(localTasks, date);

            const delayCSVal = (record && record.delayCurvaS && record.delayCurvaS > 0) ? record.delayCurvaS : calculatedCS;
            const delayRCVal = (record && record.delayRutaCritica && record.delayRutaCritica > 0) ? record.delayRutaCritica : calculatedRC;

            return {
                'Semana': `Semana ${week.index}`,
                'Fecha': date,
                'Estado': isSynced ? 'Sincronizado' : 'Pendiente',
                '% Sem. Prog.': `${truncateProgress(programmedWeekly)}%`,
                '% Sem. Real': currentWeeklyGain !== null ? `${truncateProgress(currentWeeklyGain)}%` : '-',
                'Diff. Semanal': currentDiffWeekly !== null ? `${currentDiffWeekly >= 0 ? '+' : ''}${truncateProgress(currentDiffWeekly)}%` : '-',
                '% Acum. Prog.': `${truncateProgress(currentPlan)}%`,
                '% Acum. Real': currentAccumReal !== null ? `${truncateProgress(currentAccumReal)}%` : '-',
                'Diff. Acumulada': currentDiffAccumulated !== null ? `${currentDiffAccumulated >= 0 ? '+' : ''}${truncateProgress(currentDiffAccumulated)}%` : '-',
                'Atraso Curva S (Días)': `${delayCSVal}d`,
                'Atraso Ruta Crítica (Días)': `${delayRCVal}d`
            };
        });
    }, [localTasks, localProjectInfo, localProgressHistory]);

    // 2. Planificación Intermedia (Lookahead)
    const intermediateRows = useMemo(() => {
        const rows: any[] = [];
        // Sort lookaheads chronologically to assign Planificación Intermedia #1, #2, etc.
        const sortedLookaheadPlans = [...localLookaheadPlans].sort((a, b) => new Date(a.startDate || 0).getTime() - new Date(b.startDate || 0).getTime());

        const allConstraintGroups = [
            ...(Array.isArray(globalConstraintGroups) ? globalConstraintGroups : []),
            ...(Array.isArray(localConstraintGroups) ? localConstraintGroups : [])
        ];

        const resolveResponsibleName = (rawIdOrObj: any, directName?: string): string => {
            if (directName && directName !== 'Sin asignar' && directName !== 'Sin Asignar' && directName !== 'unassigned') {
                return directName;
            }
            if (!rawIdOrObj) return 'Sin asignar';
            const idStr = String(rawIdOrObj).trim();
            if (!idStr || idStr === 'unassigned' || idStr === 'undefined' || idStr === 'null') return 'Sin asignar';

            // A. Buscar en responsables locales del proyecto
            const localResp = localResponsibles.find(r => 
                r && (r.id === idStr || r.email?.toLowerCase() === idStr.toLowerCase() || r.name?.toLowerCase() === idStr.toLowerCase())
            );
            if (localResp?.name) return localResp.name;

            // B. Buscar en usuarios globales
            const globalU = globalUsers.find(u => 
                u && (u.id === idStr || u.email?.toLowerCase() === idStr.toLowerCase() || u.name?.toLowerCase() === idStr.toLowerCase())
            );
            if (globalU?.name) return globalU.name;

            // C. Buscar en subcontratistas
            const subc = (localSubcontractors || []).find(s => 
                s && (s.id === idStr || s.name?.toLowerCase() === idStr.toLowerCase() || s.contactName?.toLowerCase() === idStr.toLowerCase())
            );
            if (subc?.name) return subc.name;

            // D. Buscar en perfiles globales si el código correspondía a un profileId
            const prof = (globalProfiles || []).find(p => 
                p && (p.id === idStr || p.name?.toLowerCase() === idStr.toLowerCase())
            );
            if (prof?.name) return prof.name;

            // E. Si el idStr contiene espacios y parece un nombre real (no un UUID o código técnico)
            if (idStr.length > 2 && !idStr.startsWith('usr_') && !idStr.startsWith('temp-') && !idStr.match(/^[0-9a-f]{8}-[0-9a-f]{4}/i)) {
                return idStr;
            }

            return 'Sin asignar';
        };

        const resolveTypeName = (typeId?: string): string => {
            if (!typeId) return 'General';
            for (const g of allConstraintGroups) {
                if (g && Array.isArray(g.definitions)) {
                    const def = g.definitions.find((d: any) => d && (d.id === typeId || d.name === typeId));
                    if (def) return def.name;
                }
            }
            return typeId;
        };

        // 1. Recorrer planes Lookahead registrados
        sortedLookaheadPlans.forEach((plan, planIdx) => {
            if (plan && Array.isArray(plan.constraints)) {
                const planLabel = `Planificación Intermedia #${planIdx + 1}`;
                const planStart = plan.startDate || '-';
                const planEnd = plan.endDate || '-';

                plan.constraints.forEach(c => {
                    const foundTask = findTaskById(c.taskId, localTasks);
                    const taskName = foundTask?.name || (c as any).taskName || 'Tarea no encontrada';
                    const typeName = resolveTypeName(c.typeId);
                    const responsibleName = resolveResponsibleName(c.responsibleId, (c as any).responsibleName);

                    rows.push({
                        'Planificación Intermedia #': planLabel,
                        'Nombre Tarea': taskName,
                        'Fecha Inicio': planStart,
                        'Fecha Termino': planEnd,
                        'Restricción': c.description || 'Sin descripción',
                        'Tipo de Restricción': typeName,
                        'Responsable': responsibleName,
                        'Fecha Requerida': c.requiredDate || '-',
                        'Estado': c.status || 'Pendiente',
                        'Observaciones': c.observations || '-'
                    });
                });
            }
        });

        // 2. Si no hay planes lookahead, buscar restricciones en las tareas maestras (localTasks)
        if (rows.length === 0 && localTasks && localTasks.length > 0) {
            const flat = flattenTasks(localTasks);
            flat.forEach((t) => {
                if ((t as any).constraints && Array.isArray((t as any).constraints) && (t as any).constraints.length > 0) {
                    (t as any).constraints.forEach((c: any) => {
                        const typeName = resolveTypeName(c.typeId || c.type);
                        const responsibleName = resolveResponsibleName(c.responsibleId, c.responsible || c.responsibleName);
                        rows.push({
                            'Planificación Intermedia #': 'Planificación Intermedia #1',
                            'Nombre Tarea': t.name,
                            'Fecha Inicio': t.start || '-',
                            'Fecha Termino': t.end || '-',
                            'Restricción': c.description || c.name || 'Sin descripción',
                            'Tipo de Restricción': typeName,
                            'Responsable': responsibleName,
                            'Fecha Requerida': c.requiredDate || t.start || '-',
                            'Estado': c.status || 'Pendiente',
                            'Observaciones': c.observations || '-'
                        });
                    });
                }
            });
        }

        // 3. Fallback demostrativo enriquecido para el Diseñador de Reportes y Plantillas
        if (rows.length === 0) {
            const sampleTasks = (localTasks && localTasks.length > 0)
                ? flattenTasks(localTasks).filter(t => t.type === 'task').slice(0, 8)
                : [];

            const sampleConstraints = [
                { task: sampleTasks[0]?.name || 'Excavación y Movimiento de Tierras', restr: 'Recepción y Visto Bueno de Mecánica de Suelos', type: 'Ingeniería', resp: 'Jefe de Calidad', status: 'Liberado', obs: 'Aprobado por ITO según protocolo' },
                { task: sampleTasks[1]?.name || 'Emplantillado y Fundaciones', restr: 'Trazado topográfico y verificación de cotas', type: 'Topografía', resp: 'Topógrafo Principal', status: 'Liberado', obs: 'Puntos de referencia validados' },
                { task: sampleTasks[2]?.name || 'Armadura de Muros y Pilares 1° Piso', restr: 'Suministro y dimensionado de enfierradura A63-42H', type: 'Materiales', resp: 'Administrador de Obra', status: 'Pendiente', obs: 'Despacho programado para el día lunes' },
                { task: sampleTasks[3]?.name || 'Moldajes de Muros y Losas', restr: 'Llegada de piezas especiales de moldaje peri', type: 'Equipos', resp: 'Jefe de Terreno', status: 'NO LIBERADO', obs: 'Retraso de 2 días por transporte' },
                { task: sampleTasks[4]?.name || 'Hormigonado de Elementos Verticales', restr: 'Coordinación con planta de hormigón y bomba pluma', type: 'Subcontrato', resp: 'Jefe de Terreno', status: 'Pendiente', obs: 'Volumen confirmado: 85 m3' },
                { task: sampleTasks[5]?.name || 'Instalaciones Sanitarias Embutidas', restr: 'Aprobación de RDI #14 (Pase de tuberías en vigas)', type: 'Ingeniería', resp: 'Proyectista Sanitario', status: 'Liberado', obs: 'RDI aprobada con plano complementario' },
                { task: sampleTasks[6]?.name || 'Instalaciones Eléctricas en Losa', restr: 'Suministro de ductos conduit libre de halógeno', type: 'Materiales', resp: 'Subcontratista Eléctrico', status: 'Pendiente', obs: 'Stock en bodega obra' },
                { task: sampleTasks[7]?.name || 'Descimbre y Curado de Hormigón', restr: 'Disponibilidad de membrana de curado y termómetros', type: 'Seguridad / Calidad', resp: 'Jefe de Calidad', status: 'Liberado', obs: 'Control térmico diario' }
            ];

            sampleConstraints.forEach((sc, idx) => {
                rows.push({
                    'Planificación Intermedia #': idx < 4 ? 'Planificación Intermedia #1' : 'Planificación Intermedia #2',
                    'Nombre Tarea': sc.task,
                    'Fecha Inicio': '2026-03-02',
                    'Fecha Termino': '2026-03-27',
                    'Restricción': sc.restr,
                    'Tipo de Restricción': sc.type,
                    'Responsable': sc.resp,
                    'Fecha Requerida': '2026-03-10',
                    'Estado': sc.status,
                    'Observaciones': sc.obs
                });
            });
        }

        return rows;
    }, [localLookaheadPlans, localTasks, localResponsibles, globalUsers, localSubcontractors, globalProfiles, localConstraintGroups, globalConstraintGroups]);

    // 3. Planificación Semanal (LPS)
    const ppcRows = useMemo(() => {
        const rows: any[] = [];
        localWeeklyPlans.forEach((plan, planIdx) => {
            if (plan && Array.isArray(plan.allocations)) {
                const planLabel = plan.id ? `Semana #${plan.id}` : `Semana #${planIdx + 1}`;
                const planStart = plan.startDate || '-';
                const planEnd = plan.endDate || '-';
                
                plan.allocations.forEach(alloc => {
                    if (alloc.isWeeklySubtask === true) return;
                    
                    // 1. Tarea: Búsqueda profunda en árbol y fallback robusto
                    const foundTask = findTaskById(alloc.taskId, localTasks);
                    const taskName = foundTask?.name || alloc.taskName || alloc.additionalTaskName || 'Tarea';
                    
                    // 2. Unidad Física y Nivel
                    const unit = localUnits.find(u => u.id === alloc.unitId);
                    const level = localLevels.find(l => l.id === unit?.levelId);
                    const levelName = level ? level.name : (unit?.levelId || 'Nivel General');

                    // 3. Responsable: Búsqueda unificada en responsables locales, usuarios globales y asignación directa
                    const resolveResponsibleName = (rawIdOrObj: any, directName?: string): string => {
                        if (directName && directName !== 'Sin asignar' && directName !== 'Sin Asignar' && directName !== 'unassigned') {
                            return directName;
                        }
                        if (!rawIdOrObj) return 'Sin asignar';
                        const idStr = String(rawIdOrObj).trim();
                        if (!idStr || idStr === 'unassigned' || idStr === 'undefined' || idStr === 'null') return 'Sin asignar';

                        const localResp = localResponsibles.find(r => 
                            r && (r.id === idStr || r.email?.toLowerCase() === idStr.toLowerCase() || r.name?.toLowerCase() === idStr.toLowerCase())
                        );
                        if (localResp?.name) return localResp.name;

                        const globalU = globalUsers.find(u => 
                            u && (u.id === idStr || u.email?.toLowerCase() === idStr.toLowerCase() || u.name?.toLowerCase() === idStr.toLowerCase())
                        );
                        if (globalU?.name) return globalU.name;

                        const subc = (localSubcontractors || []).find(s => 
                            s && (s.id === idStr || s.name?.toLowerCase() === idStr.toLowerCase() || s.contactName?.toLowerCase() === idStr.toLowerCase())
                        );
                        if (subc?.name) return subc.name;

                        const prof = (globalProfiles || []).find(p => 
                            p && (p.id === idStr || p.name?.toLowerCase() === idStr.toLowerCase())
                        );
                        if (prof?.name) return prof.name;

                        if (idStr.length > 2 && !idStr.startsWith('usr_') && !idStr.startsWith('temp-') && !idStr.match(/^[0-9a-f]{8}-[0-9a-f]{4}/i)) {
                            return idStr;
                        }

                        return 'Sin asignar';
                    };

                    const responsibleName = resolveResponsibleName(alloc.responsibleId, alloc.responsibleName);

                    // 4. Estado de Ejecución
                    let estadoEjecucion = 'Pendiente';
                    if (alloc.executed === true) {
                        estadoEjecucion = 'Ejecutado';
                    } else if (alloc.executed === false) {
                        estadoEjecucion = 'No ejecutado';
                    }

                    // 5. Causa de No Cumplimiento (CNC): Obtener el nombre descriptivo de la causa y su Categoría
                    let cncName = '-';
                    let cncCategory = '-';
                    const allCNCGroups = [
                        ...(Array.isArray(globalCNCGroupsData) ? globalCNCGroupsData : []),
                        ...(Array.isArray(legacyCNCGroups) ? legacyCNCGroups : []),
                        ...(Array.isArray(localCNCGroups) ? localCNCGroups : [])
                    ];

                    if (alloc.cncId) {
                        for (const group of allCNCGroups) {
                            if (group && Array.isArray(group.causes)) {
                                const matchedCause = group.causes.find((c: any) => 
                                    c && (c.id === alloc.cncId || c.name === alloc.cncId || String(c.id).toLowerCase() === String(alloc.cncId).toLowerCase())
                                );
                                if (matchedCause) {
                                    cncName = matchedCause.name;
                                    cncCategory = group.name || 'General';
                                    break;
                                }
                            }
                        }
                        if (cncName === '-') {
                            cncName = alloc.cncName || alloc.cncReason || alloc.cncDescription || alloc.cncId;
                        }
                        if (cncCategory === '-' && (alloc.cncCategory || alloc.cncGroupName || (alloc as any).cncGroup)) {
                            cncCategory = alloc.cncCategory || alloc.cncGroupName || (alloc as any).cncGroup;
                        }
                    }

                    const d = alloc.days || {};

                    rows.push({
                        'Planificación Semanal #': planLabel,
                        'Fecha de Inicio': planStart,
                        'Fecha de Termino': planEnd,
                        'Nivel': levelName,
                        'Unidades Físicas': unit ? unit.name : (alloc.unitName || 'Unidad'),
                        'Tarea': taskName,
                        'Responsable': responsibleName,
                        'L': d['L'] ? '✓' : '',
                        'M': d['M'] ? '✓' : '',
                        'X': d['X'] ? '✓' : '',
                        'J': d['J'] ? '✓' : '',
                        'V': d['V'] ? '✓' : '',
                        'S': d['S'] ? '✓' : '',
                        '% Comprometido': `${alloc.percentComprometido || 100}%`,
                        '% Cumplimiento': alloc.executed === true ? '100%' : alloc.executed === false ? '0%' : '-',
                        'Estado': estadoEjecucion,
                        'Categoría C.N.C': cncCategory,
                        'C.N.C': cncName
                    });
                });
            }
        });
        return rows;
    }, [localWeeklyPlans, localTasks, localUnits, localResponsibles, globalUsers, localLevels, globalCNCGroupsData, legacyCNCGroups, localCNCGroups]);

    // 4. Control de Calidad
    const qualityRows = useMemo(() => {
        const rows: any[] = [];

        Object.values(localSavedProtocols).forEach((p: any) => {
            const unit = localUnits.find(u => u.id === p.unitId);
            const task = localTasks.find(t => t.id === p.taskId);
            const lastHistory = p.history && p.history.length > 0 ? p.history[p.history.length - 1] : null;
            
            const level = localLevels.find(l => l.id === unit?.levelId);
            const levelName = level ? level.name : (unit?.levelId || 'Nivel General');

            const config = localProtocolConfigs[p.taskId];
            const group = config?.reviewerGroups?.find((g: any) => g.unitIds && g.unitIds.includes(p.unitId));
            const reviewersList: any[] = (group && Array.isArray(group.reviewers) && group.reviewers.length > 0)
                ? group.reviewers
                : [];

            const totalReviewers = Math.max(reviewersList.length, 1);
            const protoStatus = p.status || 'Abierto';

            // Formatear fecha segura (evitar Invalid Date)
            const safeFormatDate = (rawDate: any): string => {
                if (!rawDate) return '-';
                const d = new Date(rawDate);
                return isNaN(d.getTime()) ? '-' : d.toLocaleDateString('es-CL');
            };

            const fallbackDate = lastHistory?.date ? safeFormatDate(lastHistory.date) : safeFormatDate(p.creationDate);

            if (reviewersList.length > 0) {
                reviewersList.forEach((rev: any, idx: number) => {
                    // Nombre del revisor
                    let respName = rev.name || rev.responsibleName;
                    if (!respName || respName.trim() === '') {
                        respName = `Revisor ${idx + 1}`;
                    }

                    // Determinar si este revisor en particular ya firmó
                    const hasSignature = !!(p.signatures && p.signatures[idx]);
                    const estadoFirma = hasSignature ? 'Firmado' : (protoStatus === 'Cerrado' ? 'Firmado' : 'Pendiente');

                    // Buscar fecha de la firma en el historial si existe
                    let firmaFecha = fallbackDate;
                    if (p.history && Array.isArray(p.history)) {
                        const signAction = p.history.find((h: any) => 
                            (h.action === 'approved' || h.action === 'closed') && 
                            (h.user?.includes(respName) || h.user?.includes(`Revisor ${idx + 1}`))
                        );
                        if (signAction && signAction.date) {
                            firmaFecha = safeFormatDate(signAction.date);
                        }
                    }

                    rows.push({
                        'Número': p.id,
                        'Partida': p.protocolName || p.taskName || task?.name || 'Protocolo de Control',
                        'Nivel': levelName,
                        'Unidad Física': unit ? unit.name : 'Unidad',
                        'Estado Protocolo': protoStatus,
                        'Etapa': `Revisor ${idx + 1} de ${totalReviewers}`,
                        'Responsable': respName,
                        'Estado Firma': estadoFirma,
                        'Fecha Firma / Mod': firmaFecha,
                        'Total Revisores': totalReviewers
                    });
                });
            } else {
                // Caso fallback si el protocolo no tiene grupo de revisores configurado
                const currentIdx = p.currentReviewerIndex || 0;
                const isSigned = !!(p.signatures && p.signatures[currentIdx]) || protoStatus === 'Cerrado';
                rows.push({
                    'Número': p.id,
                    'Partida': p.protocolName || p.taskName || task?.name || 'Protocolo de Control',
                    'Nivel': levelName,
                    'Unidad Física': unit ? unit.name : 'Unidad',
                    'Estado Protocolo': protoStatus,
                    'Etapa': `Revisor 1 de 1`,
                    'Responsable': 'Sin Asignar',
                    'Estado Firma': isSigned ? 'Firmado' : 'Pendiente',
                    'Fecha Firma / Mod': fallbackDate,
                    'Total Revisores': 1
                });
            }
        });

        return rows;
    }, [localSavedProtocols, localUnits, localTasks, localLevels, localProtocolConfigs]);

    // 5. Subcontratos
    const subcontractRows = useMemo(() => {
        const rows: any[] = [];
        
        if (localContracts && localContracts.length > 0) {
            localContracts.forEach(contract => {
                if (contract && Array.isArray(contract.items)) {
                    const subc = (localSubcontractors || []).find(s => s.id === contract.subcontractorId);
                    const subcName = subc?.name || (contract as any).subcontractorName || 'Subcontratista';

                    contract.items.forEach(item => {
                        const totalApprovedQty = (localPaymentStatements || [])
                            .filter(ps => ps.contractId === contract.id && (ps.status === 'Aprobado' || ps.status === 'Autorizado'))
                            .reduce((sum, ps) => {
                                const matchedItem = ps.items?.find(i => i.id === item.id || i.description === item.description);
                                return sum + (matchedItem?.paidAmount || 0);
                            }, 0);
                        const unitPrice = item.unitPrice ? parseFloat(item.unitPrice.toString().replace(/[^0-9.-]+/g,"")) : 0;
                        const matchedDetails = item.details?.[0];
                        const accumulatedQty = totalApprovedQty;
                        const qty = item.quantity || 1;
                        const accumulatedPct = ((totalApprovedQty / qty) * 100).toFixed(1) + '%';
                        const accumulatedTotal = totalApprovedQty * unitPrice;

                        const currentEPQty = item.paidAmount || 0;
                        const currentEPPct = (((item.paidAmount || 0) / qty) * 100).toFixed(1) + '%';
                        const currentEPTotal = (item.paidAmount || 0) * unitPrice;

                        const previousQty = Math.max(0, totalApprovedQty - currentEPQty);
                        const previousPct = ((previousQty / qty) * 100).toFixed(1) + '%';
                        const previousTotal = previousQty * unitPrice;

                        rows.push({
                            'Subcontrato': subcName,
                            'Partida': item.description || 'Partida Subcontratada',
                            'Nivel': matchedDetails?.unitName || 'General',
                            'Unidad Fisica': matchedDetails?.unitName || 'Global',
                            'Cant. Contractual': qty,
                            'Val. Unitario': formatCLP(unitPrice),
                            'Valor Total': formatCLP(item.totalValue || (qty * unitPrice)),
                            'Avance Acumulado - Cant.': accumulatedQty.toFixed(1),
                            'Avance Acumulado - %': accumulatedPct,
                            'Avance Acumulado - Total $': formatCLP(accumulatedTotal),
                            'Avance Anterior - Cant.': previousQty.toFixed(1),
                            'Avance Anterior - %': previousPct,
                            'Avance Anterior - Total $': formatCLP(previousTotal),
                            'Avance Actual EP - Cant.': currentEPQty.toFixed(1),
                            'Avance Actual EP - %': currentEPPct,
                            'Avance Actual EP - Total $': formatCLP(currentEPTotal)
                        });
                    });
                }
            });
        }

        // Si no hay contratos registrados (ej: diseñador de plantillas maestras, nuevo proyecto o catálogo), proveer datos modelo completos
        if (rows.length === 0) {
            const sampleSubcontracts = [
                {
                    subc: 'Sociedad Hormigones y Moldajes SpA',
                    partida: 'Hormigonado de Muros, Pilares y Vigas H-30',
                    nivel: 'Piso 1 al 4',
                    unidad: 'Torre A',
                    cant: 1250,
                    valUnit: 48500,
                    accQty: 875,
                    prevQty: 625,
                    currQty: 250
                },
                {
                    subc: 'Sociedad Hormigones y Moldajes SpA',
                    partida: 'Instalación y Descimbre de Moldajes Trepantes',
                    nivel: 'Piso 1 al 4',
                    unidad: 'Torre A',
                    cant: 3400,
                    valUnit: 14200,
                    accQty: 2380,
                    prevQty: 1700,
                    currQty: 680
                },
                {
                    subc: 'Instalaciones Eléctricas y Clima del Sur Ltda',
                    partida: 'Canalización y Cableado Alumbrado y Fuerza',
                    nivel: 'Subterráneo -1 y -2',
                    unidad: 'Sector Estacionamientos',
                    cant: 860,
                    valUnit: 32000,
                    accQty: 516,
                    prevQty: 344,
                    currQty: 172
                },
                {
                    subc: 'Instalaciones Eléctricas y Clima del Sur Ltda',
                    partida: 'Montaje de Tableros Eléctricos y TTA',
                    nivel: 'Subterráneo -1',
                    unidad: 'Sala Técnica',
                    cant: 18,
                    valUnit: 850000,
                    accQty: 9,
                    prevQty: 4,
                    currQty: 5
                },
                {
                    subc: 'Terminaciones y Pinturas del Pacífico SpA',
                    partida: 'Enlucido de Yeso y Empaste sobre Muros',
                    nivel: 'Piso 1 y 2',
                    unidad: 'Departamentos Tipo A',
                    cant: 2800,
                    valUnit: 8900,
                    accQty: 1120,
                    prevQty: 560,
                    currQty: 560
                },
                {
                    subc: 'Terminaciones y Pinturas del Pacífico SpA',
                    partida: 'Pintura Látex Esmalte al Agua 3 Manos',
                    nivel: 'Piso 1 y 2',
                    unidad: 'Pasillos y Hall',
                    cant: 1950,
                    valUnit: 6400,
                    accQty: 585,
                    prevQty: 195,
                    currQty: 390
                },
                {
                    subc: 'Estructuras Metálicas Andinas Ltda',
                    partida: 'Fabricación y Montaje de Escaleras de Emergencia',
                    nivel: 'Caja Escala 1',
                    unidad: 'Torre A y B',
                    cant: 24,
                    valUnit: 1650000,
                    accQty: 18,
                    prevQty: 12,
                    currQty: 6
                },
                {
                    subc: 'Climatización y Ventilación Austral SpA',
                    partida: 'Ductos de Extracción de Aire e Inyección Forzada',
                    nivel: 'Subterráneo -1 al -3',
                    unidad: 'Shaft Principal',
                    cant: 420,
                    valUnit: 52000,
                    accQty: 294,
                    prevQty: 210,
                    currQty: 84
                }
            ];

            sampleSubcontracts.forEach(s => {
                const totalVal = s.cant * s.valUnit;
                const accTotal = s.accQty * s.valUnit;
                const prevTotal = s.prevQty * s.valUnit;
                const currTotal = s.currQty * s.valUnit;

                rows.push({
                    'Subcontrato': s.subc,
                    'Partida': s.partida,
                    'Nivel': s.nivel,
                    'Unidad Fisica': s.unidad,
                    'Cant. Contractual': s.cant,
                    'Val. Unitario': formatCLP(s.valUnit),
                    'Valor Total': formatCLP(totalVal),
                    'Avance Acumulado - Cant.': s.accQty.toFixed(1),
                    'Avance Acumulado - %': ((s.accQty / s.cant) * 100).toFixed(1) + '%',
                    'Avance Acumulado - Total $': formatCLP(accTotal),
                    'Avance Anterior - Cant.': s.prevQty.toFixed(1),
                    'Avance Anterior - %': ((s.prevQty / s.cant) * 100).toFixed(1) + '%',
                    'Avance Anterior - Total $': formatCLP(prevTotal),
                    'Avance Actual EP - Cant.': s.currQty.toFixed(1),
                    'Avance Actual EP - %': ((s.currQty / s.cant) * 100).toFixed(1) + '%',
                    'Avance Actual EP - Total $': formatCLP(currTotal)
                });
            });
        }

        return rows;
    }, [localContracts, localPaymentStatements, localSubcontractors]);

    // Función unificada para resolver las filas según el componente de datos
    const getComponentRows = (componentId?: string): any[] => {
        if (!componentId) return [];
        if (componentId === 'kpi') return kpiRows;
        if (componentId === 'scurve') return scurveRows;
        if (componentId === 'intermediate' || componentId === 'restrictions') return intermediateRows;
        if (componentId === 'ppc') return ppcRows;
        if (componentId === 'quality' || componentId === 'ssoma') return qualityRows;
        if (componentId === 'subcontracts') return subcontractRows;
        if (componentId === 'metrics') return metricsRows;
        return [];
    };

    // --- OPCIONES DISPONIBLES PARA FILTROS EN CASCADA DE JERARQUÍA TEMPORAL (POWER BI STYLE) ---
    const dateHierarchyOptions = useMemo(() => {
        const allRows = [
            ...scurveRows,
            ...intermediateRows,
            ...ppcRows,
            ...qualityRows,
            ...subcontractRows
        ];

        const yearsSet = new Set<string>();
        const monthsMap = new Map<string, { monthKey: string; monthLabel: string; year: string; monthNumber: string }>();
        const weeksMap = new Map<string, { weekStart: string; weekLabel: string; monthKey: string; year: string }>();

        allRows.forEach(r => {
            const d = extractRowDate(r);
            if (!d) return;
            const h = getDateHierarchyParts(d);
            if (!h) return;

            yearsSet.add(h.year);

            if (!monthsMap.has(h.monthKey)) {
                monthsMap.set(h.monthKey, {
                    monthKey: h.monthKey,
                    monthLabel: `${h.month} ${h.year}`,
                    year: h.year,
                    monthNumber: h.monthNumber
                });
            }

            if (!weeksMap.has(h.weekStart)) {
                weeksMap.set(h.weekStart, {
                    weekStart: h.weekStart,
                    weekLabel: h.weekStartLabel,
                    monthKey: h.monthKey,
                    year: h.year
                });
            }
        });

        const sortedYears = Array.from(yearsSet).sort((a, b) => a.localeCompare(b));
        
        // Meses ordenados cronológicamente
        const allMonths = Array.from(monthsMap.values()).sort((a, b) => a.monthKey.localeCompare(b.monthKey));
        // Semanas ordenadas cronológicamente
        const allWeeks = Array.from(weeksMap.values()).sort((a, b) => a.weekStart.localeCompare(b.weekStart));

        // Filtrar meses según el Año seleccionado
        const availableMonths = selectedDateYear === 'todos' 
            ? allMonths 
            : allMonths.filter(m => m.year === selectedDateYear);

        // Filtrar semanas según el Mes o Año seleccionado
        const availableWeeks = selectedDateMonth !== 'todos'
            ? allWeeks.filter(w => w.monthKey === selectedDateMonth)
            : selectedDateYear !== 'todos'
                ? allWeeks.filter(w => w.year === selectedDateYear)
                : allWeeks;

        return {
            years: sortedYears,
            months: availableMonths,
            weeks: availableWeeks
        };
    }, [scurveRows, intermediateRows, ppcRows, qualityRows, subcontractRows, selectedDateYear, selectedDateMonth]);

    const renderCustomDataTable = (componentId: string, dataRows: any[]) => {
        const columns = selectedColumns[componentId] && selectedColumns[componentId].length > 0
            ? selectedColumns[componentId]
            : (DEFAULT_COLUMNS[componentId] || []);

        if (dataRows.length === 0) {
            return (
                <div className="p-8 text-center text-xs text-slate-450 dark:text-slate-500 italic bg-slate-50/50 dark:bg-slate-800/10 rounded-2xl border border-dashed border-slate-200 dark:border-slate-700">
                    No hay registros de datos para mostrar.
                </div>
            );
        }

        return (
            <div className="w-full overflow-x-auto rounded-2xl border border-slate-200/60 dark:border-slate-700/60 bg-white/50 dark:bg-slate-900/40 shadow-sm mt-4">
                <table className="w-full border-collapse text-left text-xs">
                    <thead>
                        <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200/60 dark:border-slate-700/60">
                            {columns.map((col, idx) => (
                                <th key={idx} className="px-4 py-3 font-extrabold uppercase tracking-wider text-[10px] text-slate-400 dark:text-slate-500">
                                    {col}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50 text-slate-700 dark:text-slate-200">
                        {dataRows.map((row, rowIdx) => (
                            <tr key={rowIdx} className="hover:bg-slate-50/40 dark:hover:bg-slate-800/20 transition-colors">
                                {columns.map((col, colIdx) => (
                                    <td key={colIdx} className="px-4 py-3 whitespace-nowrap">
                                        {row[col] !== undefined ? row[col] : '-'}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const isEditAuthorized = config.permissions === 'ReadWrite' || config.isMocked;
    const showPermissionError = mode === 'edit' && !isEditAuthorized && !canDesign;
    const hasWarningBanner = !isAdmin && !canModifyDesign && mode === 'edit' && canDesign && !config.isMocked;

    // --- ACCIÓN DE EXPORTACIÓN ---
    const handleExportPDF = () => {
        window.print();
    };

    const handleExportHTML = () => {
        const buildHTMLTable = (componentId: string, rows: any[]) => {
            const columns = selectedColumns[componentId] && selectedColumns[componentId].length > 0
                ? selectedColumns[componentId]
                : (DEFAULT_COLUMNS[componentId] || []);
            
            if (rows.length === 0) {
                return '<p style="color: #64748b; font-style: italic; font-size: 11px;">No hay registros de datos disponibles.</p>';
            }

            return `
            <div style="overflow-x: auto; margin-top: 12px; border: 1px solid #e2e8f0; border-radius: 8px;">
                <table style="width: 100%; border-collapse: collapse; text-align: left; font-size: 11px;">
                    <thead>
                        <tr style="background-color: #f8fafc; border-bottom: 1px solid #e2e8f0;">
                            ${columns.map(c => `<th style="padding: 10px; font-weight: bold; color: #475569; border-bottom: 1px solid #e2e8f0;">${c}</th>`).join('')}
                        </tr>
                    </thead>
                    <tbody>
                        ${rows.map(row => `
                            <tr style="border-bottom: 1px solid #f1f5f9;">
                                ${columns.map(col => `<td style="padding: 10px; color: #334155;">${row[col] !== undefined ? row[col] : '-'}</td>`).join('')}
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            </div>`;
        };

        const html = `
<!DOCTYPE html>
<html lang="es">
<head>
    <meta charset="UTF-8">
    <title>Reporte: ${config.name || 'Sin Título'}</title>
    <script src="https://cdn.jsdelivr.net/npm/chart.js"></script>
    <style>
        body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; padding: 32px; color: #1e293b; background-color: #f8fafc; }
        .report-header { margin-bottom: 24px; border-bottom: 2px solid #e2e8f0; padding-bottom: 16px; display: flex; justify-content: space-between; align-items: flex-end; }
        .report-title { font-size: 24px; font-weight: 800; color: #0f172a; margin: 0; }
        .report-meta { font-size: 12px; color: #64748b; margin-top: 4px; }
        .grid-layout { display: flex; flex-direction: column; gap: 24px; }
        .section-card { background: white; border-radius: 12px; border: 1px solid #e2e8f0; padding: 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); }
        .section-title { font-size: 14px; font-weight: 700; text-transform: uppercase; color: #334155; margin-bottom: 16px; letter-spacing: 0.5px; border-bottom: 1px solid #f1f5f9; padding-bottom: 8px; }
        .chart-container { position: relative; width: 100%; height: 260px; margin-bottom: 16px; }
    </style>
</head>
<body>
    <div class="report-header">
        <div>
            <h1 class="report-title">${config.name || 'Reporte de Inteligencia'}</h1>
            <div class="report-meta">Generado el: ${new Date().toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })} • K-LyDApp Engine</div>
        </div>
    </div>

    <div class="grid-layout">
        ${selectedComponents.includes('scurve') ? `
        <div class="section-card">
            <div class="section-title">Planificación Maestra (Curva S)</div>
            <div class="chart-container">
                <canvas id="scurveChart"></canvas>
            </div>
            ${buildHTMLTable('scurve', scurveRows)}
        </div>` : ''}

        ${selectedComponents.includes('intermediate') ? `
        <div class="section-card">
            <div class="section-title">Planificación Intermedia (Lookahead)</div>
            ${buildHTMLTable('intermediate', intermediateRows)}
        </div>` : ''}

        ${selectedComponents.includes('ppc') ? `
        <div class="section-card">
            <div class="section-title">Planificación Semanal (PPC LPS)</div>
            <div class="chart-container" style="height: 240px;">
                <canvas id="ppcChart"></canvas>
            </div>
            ${buildHTMLTable('ppc', ppcRows)}
        </div>` : ''}

        ${selectedComponents.includes('quality') ? `
        <div class="section-card">
            <div class="section-title">Control de Calidad</div>
            <div style="display: flex; gap: 40px; margin-bottom: 24px; align-items: center;">
                <div style="width: 180px; height: 180px; position: relative;">
                    <canvas id="qualityChart"></canvas>
                </div>
            </div>
            ${buildHTMLTable('quality', qualityRows)}
        </div>` : ''}

        ${selectedComponents.includes('subcontracts') ? `
        <div class="section-card">
            <div class="section-title">Subcontrato</div>
            <div class="chart-container">
                <canvas id="subcontractsChart"></canvas>
            </div>
            ${buildHTMLTable('subcontracts', subcontractRows)}
        </div>` : ''}

        ${selectedComponents.includes('metrics') ? `
        <div class="section-card">
            <div class="section-title">Métricas del Proyecto</div>
            ${buildHTMLTable('metrics', metricsRows)}
        </div>` : ''}
    </div>

    <script>
        const scurveData = ${JSON.stringify(scurvePoints)};
        const ppcData = ${JSON.stringify(ppcTrendData)};
        const qualityData = {
            closed: ${qualityMetrics.closedCount},
            open: ${qualityMetrics.openCount},
            rejected: ${qualityMetrics.rejectedCount}
        };

        window.onload = function() {
            // Curva S
            const ctxS = document.getElementById('scurveChart');
            if (ctxS && scurveData.length > 0) {
                new Chart(ctxS, {
                    type: 'line',
                    data: {
                        labels: scurveData.map(d => d.date),
                        datasets: [
                            { label: 'Programado (%)', data: scurveData.map(d => d.planned), borderColor: '#0ea5e9', backgroundColor: 'rgba(14, 165, 233, 0.1)', fill: true, tension: 0.3 },
                            { label: 'Real (%)', data: scurveData.map(d => d.actual), borderColor: '#10b981', backgroundColor: 'rgba(16, 185, 129, 0.1)', fill: true, tension: 0.3 }
                        ]
                    },
                    options: { responsive: true, maintainAspectRatio: false, scales: { y: { min: 0, max: 100 } } }
                });
            }

            // PPC
            const ctxP = document.getElementById('ppcChart');
            if (ctxP && ppcData.length > 0) {
                new Chart(ctxP, {
                    type: 'bar',
                    data: {
                        labels: ppcData.map(d => d.planLabel),
                        datasets: [{ label: '% Cumplimiento', data: ppcData.map(d => d.ppcPercent), backgroundColor: '#f59e0b', borderRadius: 6 }]
                    },
                    options: { responsive: true, maintainAspectRatio: false, scales: { y: { min: 0, max: 100 } } }
                });
            }

            // Calidad
            const ctxQ = document.getElementById('qualityChart');
            if (ctxQ) {
                new Chart(ctxQ, {
                    type: 'doughnut',
                    data: {
                        labels: ['Aprobados', 'Abiertos', 'Rechazados'],
                        datasets: [{ data: [qualityData.closed, qualityData.open, qualityData.rejected], backgroundColor: ['#10b981', '#f59e0b', '#ef4444'] }]
                    },
                    options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }
                });
            }

            // Subcontratos
            const ctxSub = document.getElementById('subcontractsChart');
            if (ctxSub) {
                new Chart(ctxSub, {
                    type: 'bar',
                    data: {
                        labels: ${JSON.stringify(subcontractRows.slice(0, 10).map(r => r.Subcontrato))},
                        datasets: [{ label: 'Avance Acumulado ($)', data: ${JSON.stringify(subcontractRows.slice(0, 10).map(r => parseFloat(String(r['Avance Acumulado - Total $']).replace(/[^\d.-]/g, '')) || 0))}, backgroundColor: '#6366f1', borderRadius: 6 }]
                    },
                    options: { responsive: true, maintainAspectRatio: false }
                });
            }
        };
    </script>
</body>
</html>`;

        const blob = new Blob([html], { type: 'text/html;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `Reporte_Personalizado_${config.name || 'Local'}.html`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleExportCSV = () => {
        let csvContent = "";

        const appendCSVSection = (componentId: string, title: string, rows: any[]) => {
            if (!selectedComponents.includes(componentId) || rows.length === 0) return;
            const columns = selectedColumns[componentId] && selectedColumns[componentId].length > 0
                ? selectedColumns[componentId]
                : (DEFAULT_COLUMNS[componentId] || []);
            
            csvContent += `=== ${title.toUpperCase()} ===\n`;
            csvContent += columns.map(c => `"${c}"`).join(",") + "\n";
            rows.forEach(row => {
                csvContent += columns.map(col => `"${row[col] !== undefined ? row[col].toString().replace(/"/g, '""') : '-'}"`).join(",") + "\n";
            });
            csvContent += "\n";
        };

        appendCSVSection('scurve', 'Planificación Maestra (Gantt)', scurveRows);
        appendCSVSection('intermediate', 'Planificación Intermedia', intermediateRows);
        appendCSVSection('ppc', 'Planificación Semanal (LPS)', ppcRows);
        appendCSVSection('quality', 'Control de Calidad', qualityRows);
        appendCSVSection('subcontracts', 'Subcontrato', subcontractRows);
        appendCSVSection('metrics', 'Métricas del Proyecto', metricsRows);

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `Reporte_Personalizado_${config.name || 'Local'}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleExportExcel = () => {
        if (!activeEditingComponent) {
            alert("No hay ningún componente de datos seleccionado para exportar.");
            return;
        }

        const sourceRows = getComponentRows(activeEditingComponent);
        const processed = processCalculations(activeEditingComponent, sourceRows);
        let activeCols = selectedColumns[activeEditingComponent] || DEFAULT_COLUMNS[activeEditingComponent] || [];
        
        // Agregar columnas calculadas al Excel exportado
        if (concatColA && concatColB && !activeCols.includes('Concatenado')) activeCols = [...activeCols, 'Concatenado'];
        if (runningTotalCol && !activeCols.includes('Acumulado')) activeCols = [...activeCols, 'Acumulado'];
        if (movingAverageCol && !activeCols.includes(`Promedio móvil (N=${movingAveragePeriod})`)) {
            activeCols = [...activeCols, `Promedio móvil (N=${movingAveragePeriod})`];
        }
        if (deltaCol && !activeCols.includes('Variación %')) activeCols = [...activeCols, 'Variación %'];

        if (processed.length === 0) {
            alert("No hay datos para exportar.");
            return;
        }

        const headers = activeCols.join(';');
        const rowsCsv = processed.map(row => {
            return activeCols.map(col => {
                const val = row[col] !== undefined ? String(row[col]) : '';
                return `"${val.replace(/"/g, '""')}"`;
            }).join(';');
        });

        const csvContent = '\uFEFF' + [headers, ...rowsCsv].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `klydapp_excel_${activeEditingComponent}_${Date.now()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleSaveLocalReport = async () => {
        if (!reportTitle.trim()) {
            alert("Por favor, ingrese un nombre para el reporte.");
            return;
        }
        setSavingLocal(true);
        setShowSaveConfirmModal(false);
        try {
            const localReportId = onSaveTemplate
                ? (templateId || reportId || `tpl-${Date.now()}`)
                : (reportId && reportId.startsWith('local-') 
                    ? reportId 
                    : `local-${Math.random().toString(36).substring(2, 11)}-${Date.now().toString(36)}`);
            
            const updatedMetadata = {
                category: reportCategory || reportCategories[0] || 'General',
                categories: reportCategories.length > 0 ? reportCategories : [reportCategory || 'General'],
                badge: reportBadge || 'Personalizado',
                themeId: activeThemeId,
                description: reportDescription || 'Reporte interactivo local.',
                components: selectedComponents,
                selectedColumns: selectedColumns,
                reportPages: reportPages,
                widgets: widgets,
                calculatedColumns: calculatedColumns,
                crossFilteringActive: crossFilteringActive,
                daxOperations: {
                    runningTotalCol,
                    movingAverageCol,
                    movingAveragePeriod,
                    deltaCol,
                    concatColA,
                    concatColB,
                    concatDelimiter,
                    filterRules,
                    filterJoinType,
                    cfRules
                }
            };

            if (onSaveTemplate) {
                const templateData: MasterReportTemplate = {
                    id: localReportId,
                    name: reportTitle.trim(),
                    description: reportDescription.trim() || 'Plantilla de reporte estándar configurada desde el diseñador.',
                    category: (reportCategory as any) || 'General',
                    badge: reportBadge || 'Personalizado',
                    icon: 'analytics',
                    widgets,
                    themeId: activeThemeId,
                    updatedAt: new Date().toISOString(),
                    createdAt: initialConfig?.createdAt || new Date().toISOString()
                };
                onSaveTemplate(templateData);
            } else {
                const targetProjects = reportScope === 'corporativo' 
                    ? [] 
                    : (reportScope === 'multiobra' ? selectedProjects : [String(effectiveProjectId)]);

                await registerAnalyticsReport(
                    localReportId,
                    reportTitle.trim(),
                    reportScope,
                    targetProjects,
                    updatedMetadata
                );
            }

            // Actualizar config interno y cambiar a modo Lectura
            setConfig(prev => ({
                ...prev,
                name: reportTitle.trim(),
                metadata: updatedMetadata
            }));
            setMode('view');

            if (onReportSaved) {
                onReportSaved(localReportId, reportTitle.trim(), updatedMetadata);
            }
        } catch (err: any) {
            console.error(err);
            alert(`Error al guardar reporte local: ${err.message || 'Error del servidor'}`);
        } finally {
            setSavingLocal(false);
        }
    };

    const handleExitWithSaving = async () => {
        setShowCancelWarningModal(false);
        await handleSaveLocalReport();
        if (onCancel) onCancel();
    };

    const handleExitWithoutSaving = () => {
        setShowCancelWarningModal(false);
        if (onCancel) onCancel();
    };

    const toggleComponent = (comp: string) => {
        setSelectedComponents(prev => {
            const updated = prev.includes(comp)
                ? prev.filter(c => c !== comp)
                : [...prev, comp];
            
            if (!prev.includes(comp)) {
                setActiveEditingComponent(comp);
            } else if (activeEditingComponent === comp) {
                const remaining = updated.filter(c => c !== comp);
                setActiveEditingComponent(remaining.length > 0 ? remaining[0] : 'kpi');
            }
            return updated;
        });
    };

    const handleLoadTemplate = (type: 'avance-ppc' | 'calidad-subcontratos' | 'ejecutivo-dashboard') => {
        if (!confirm("¿Estás seguro de cargar esta plantilla? Reemplazará la configuración actual de gráficos, fórmulas y filtros del reporte.")) {
            return;
        }

        if (type === 'avance-ppc') {
            setReportTitle("Reporte de Planificación y PPC Semanal");
            setSelectedComponents(['scurve', 'ppc']);
            setWidgets([
                {
                    id: `widget-scurve-${Date.now()}`,
                    type: 'line',
                    componentId: 'scurve',
                    xAxisKey: 'name',
                    yAxisKey: 'plan',
                    width: '1/2'
                },
                {
                    id: `widget-ppc-${Date.now()}`,
                    type: 'bar-vertical',
                    componentId: 'ppc',
                    xAxisKey: 'Responsable',
                    yAxisKey: 'PPC',
                    width: '1/2'
                }
            ]);
            setCalculatedColumns([]);
            setFilterRules([]);
            setCfRules([
                {
                    column: 'PPC',
                    operator: 'less',
                    value: '80',
                    color: 'red'
                },
                {
                    column: 'PPC',
                    operator: 'greater',
                    value: '90',
                    color: 'green'
                }
            ]);
        } else if (type === 'calidad-subcontratos') {
            setReportTitle("Reporte de Calidad y Subcontratos");
            setSelectedComponents(['quality', 'subcontracts']);
            setWidgets([
                {
                    id: `widget-qual-${Date.now()}`,
                    type: 'column-stacked',
                    componentId: 'quality',
                    axisFields: [{ fieldKey: 'Responsable', aggregation: 'none' }],
                    valueFields: [{ fieldKey: 'Número', aggregation: 'count' }],
                    legendField: { fieldKey: 'Estado Firma', aggregation: 'none' },
                    xAxisKey: 'Responsable',
                    yAxisKey: 'Número',
                    width: '1/2'
                },
                {
                    id: `widget-subc-${Date.now()}`,
                    type: 'radar',
                    componentId: 'subcontracts',
                    xAxisKey: 'Subcontrato',
                    yAxisKey: 'Monto Contratado',
                    width: '1/2'
                }
            ]);
            setCalculatedColumns([]);
            setFilterRules([]);
            setCfRules([]);
        } else if (type === 'ejecutivo-dashboard') {
            setReportTitle("Dashboard Ejecutivo de Obra");
            setSelectedComponents(['kpi', 'scurve', 'ppc']);
            setWidgets([
                {
                    id: `widget-kpi-${Date.now()}`,
                    type: 'kpi-card',
                    componentId: 'kpi',
                    xAxisKey: 'General',
                    yAxisKey: 'Avance Físico',
                    width: '1/3'
                },
                {
                    id: `widget-gauge-${Date.now()}`,
                    type: 'gauge',
                    componentId: 'ppc',
                    xAxisKey: 'General',
                    yAxisKey: 'PPC',
                    width: '1/3'
                },
                {
                    id: `widget-trend-${Date.now()}`,
                    type: 'line',
                    componentId: 'scurve',
                    xAxisKey: 'name',
                    yAxisKey: 'real',
                    width: '1/3'
                },
                {
                    id: `widget-bar-${Date.now()}`,
                    type: 'bar-vertical',
                    componentId: 'ppc',
                    xAxisKey: 'Responsable',
                    yAxisKey: 'PPC',
                    width: 'full'
                }
            ]);
            setCalculatedColumns([]);
            setFilterRules([]);
            setCfRules([]);
        }
    };

    // --- PROCESAMIENTO ANALÍTICO LOCAL (DAX) ---
    const processCalculations = (componentId: string, sourceRows: any[]) => {
        let resultRows = [...sourceRows];

        // Lógica de Filtrado Cruzado interactivo (Cross-Filtering)
        if (crossFilteringActive && crossFilter && crossFilter.componentId === componentId) {
            resultRows = resultRows.filter(row => {
                const cellVal = String(row[crossFilter.key] || '');
                return cellVal === String(crossFilter.value);
            });
        }

        // Lógica de Filtrado por Jerarquía Temporal en Cascada (Año -> Mes -> Semana)
        if (selectedDateYear !== 'todos' || selectedDateMonth !== 'todos' || selectedDateWeekStart !== 'todos') {
            resultRows = resultRows.filter(row => {
                const rDate = extractRowDate(row);
                if (!rDate) return true; // Si la fila no tiene campo de fecha, no se excluye arbitrariamente
                const h = getDateHierarchyParts(rDate);
                if (!h) return true;

                if (selectedDateYear !== 'todos' && h.year !== selectedDateYear) {
                    return false;
                }
                if (selectedDateMonth !== 'todos' && h.monthKey !== selectedDateMonth) {
                    return false;
                }
                if (selectedDateWeekStart !== 'todos' && h.weekStart !== selectedDateWeekStart) {
                    return false;
                }
                return true;
            });
        }

        // Segmentación de Datos Interactiva (Slicer / Filtro Global de Dashboard)
        if (slicerFilters && Object.keys(slicerFilters).length > 0) {
            resultRows = resultRows.filter(row => {
                return Object.entries(slicerFilters).every(([filterKey, filterVal]) => {
                    if (filterVal === undefined || filterVal === null || filterVal === '' || (Array.isArray(filterVal) && filterVal.length === 0)) {
                        return true;
                    }
                    // Búsqueda flexible de clave (exacta o insensible a mayúsculas/minúsculas)
                    let matchedKey: string | undefined = undefined;
                    if (row[filterKey] !== undefined) {
                        matchedKey = filterKey;
                    } else {
                        const targetLower = filterKey.toLowerCase().trim();
                        matchedKey = Object.keys(row).find(k => k.toLowerCase().trim() === targetLower);
                    }

                    if (!matchedKey || row[matchedKey] === undefined) return true; // Si esta tabla no tiene el campo, no se filtra

                    const rawVal = row[matchedKey];
                    const cellVal = (rawVal !== undefined && rawVal !== null && String(rawVal).trim() !== '') ? String(rawVal).trim() : '(Vacío)';
                    const formattedCellDate = formatIfDate(cellVal);

                    // Verificar si alguno de los valores filtrados coincide por valor crudo, por formato dd-mm-aaaa, o por jerarquía (año, mes, semana)
                    const matchesFilterItem = (targetItem: any) => {
                        const targetStr = String(targetItem).trim();
                        if (cellVal.toLowerCase() === targetStr.toLowerCase()) return true;
                        if (formattedCellDate.toLowerCase() === targetStr.toLowerCase()) return true;

                        // Soporte para selección de nodo jerárquico (ej: "2026", "2026-03", "Lunes 02/03/2026", etc.)
                        const d = parseDateSafe(rawVal);
                        if (d) {
                            const h = getDateHierarchyParts(d);
                            if (h) {
                                if (targetStr === h.year) return true;
                                if (targetStr === h.monthKey || targetStr === `${h.month} ${h.year}`) return true;
                                if (targetStr === h.weekStart || targetStr === h.weekStartLabel) return true;
                                if (targetStr === h.dayLabel || targetStr === formatDateDDMMYYYY(d)) return true;
                            }
                        }
                        return false;
                    };

                    if (Array.isArray(filterVal)) {
                        return filterVal.some(fv => matchesFilterItem(fv));
                    }
                    return matchesFilterItem(filterVal);
                });
            });
        }

        // 0. Columnas calculadas / virtuales en caliente con gestión de tipos y valores nulos
        if (calculatedColumns.length > 0) {
            resultRows = resultRows.map(row => {
                const newRow = { ...row };
                calculatedColumns.forEach(calcCol => {
                    const cType = calcCol.calcType || 'math';

                    // 1. OPERACIÓN MATEMÁTICA
                    if (cType === 'math') {
                        const rawA = newRow[calcCol.colA || ''];
                        const cleanedA = (rawA !== undefined && rawA !== null) ? String(rawA).replace(/[^\d.-]/g, '') : '';
                        const valA = cleanedA !== '' && !isNaN(Number(cleanedA)) ? parseFloat(cleanedA) : 0; // Null-safe default 0
                        
                        let valB = 0;
                        if (calcCol.colB === 'constant') {
                            valB = calcCol.constantValue || 0;
                        } else {
                            const rawB = newRow[calcCol.colB || ''];
                            const cleanedB = (rawB !== undefined && rawB !== null) ? String(rawB).replace(/[^\d.-]/g, '') : '';
                            valB = cleanedB !== '' && !isNaN(Number(cleanedB)) ? parseFloat(cleanedB) : 0;
                        }

                        let res = 0;
                        if (calcCol.operator === '+') res = valA + valB;
                        else if (calcCol.operator === '-') res = valA - valB;
                        else if (calcCol.operator === '*') res = valA * valB;
                        else if (calcCol.operator === '/') {
                            res = valB !== 0 ? valA / valB : 0;
                        }

                        newRow[calcCol.name] = Number(res.toFixed(4));
                    }

                    // 2. CONCATENACIÓN DE TEXTO
                    else if (cType === 'text_concat') {
                        const fields = calcCol.concatFields || [];
                        const sep = calcCol.concatSeparator !== undefined ? calcCol.concatSeparator : ' - ';
                        const textParts = fields.map(f => {
                            const raw = newRow[f];
                            return (raw !== undefined && raw !== null && String(raw).trim() !== '') ? String(raw).trim() : 'Sin Datos';
                        });
                        newRow[calcCol.name] = textParts.join(sep);
                    }

                    // 3. TRANSFORMACIÓN DE TEXTO
                    else if (cType === 'text_transform') {
                        const raw = newRow[calcCol.transformField || ''];
                        const str = (raw !== undefined && raw !== null && String(raw).trim() !== '') ? String(raw).trim() : 'Sin Datos';
                        if (calcCol.transformType === 'uppercase') {
                            newRow[calcCol.name] = str.toUpperCase();
                        } else if (calcCol.transformType === 'lowercase') {
                            newRow[calcCol.name] = str.toLowerCase();
                        } else {
                            newRow[calcCol.name] = str.trim();
                        }
                    }

                    // 4. LÓGICA CONDICIONAL (SI / IF-THEN-ELSE)
                    else if (cType === 'conditional_if') {
                        const raw = newRow[calcCol.conditionField || ''];
                        const cellStr = (raw !== undefined && raw !== null) ? String(raw).trim() : '';
                        const targetStr = String(calcCol.conditionValue || '').trim();
                        const op = calcCol.conditionOperator || 'equals';
                        let conditionMet = false;

                        const numCell = parseFloat(cellStr.replace(/[^\d.-]/g, ''));
                        const numTarget = parseFloat(targetStr.replace(/[^\d.-]/g, ''));
                        const isNumericComparison = !isNaN(numCell) && !isNaN(numTarget) && (op === 'greater' || op === 'less');

                        if (isNumericComparison) {
                            if (op === 'greater') conditionMet = numCell > numTarget;
                            if (op === 'less') conditionMet = numCell < numTarget;
                        } else {
                            const cLower = cellStr.toLowerCase();
                            const tLower = targetStr.toLowerCase();
                            if (op === 'equals') conditionMet = cLower === tLower;
                            else if (op === 'not_equals') conditionMet = cLower !== tLower;
                            else if (op === 'contains') conditionMet = cLower.includes(tLower);
                            else if (op === 'not_contains') conditionMet = !cLower.includes(tLower);
                            else if (op === 'greater') conditionMet = cellStr > targetStr;
                            else if (op === 'less') conditionMet = cellStr < targetStr;
                        }

                        newRow[calcCol.name] = conditionMet ? (calcCol.thenValue ?? 'Cumple') : (calcCol.elseValue ?? 'No Cumple');
                    }

                    // 5. DIFERENCIA DE FECHAS EN DÍAS
                    else if (cType === 'date_diff') {
                        const rawA = newRow[calcCol.dateFieldA || ''];
                        const rawB = newRow[calcCol.dateFieldB || ''];
                        if (!rawA || !rawB) {
                            newRow[calcCol.name] = 0;
                        } else {
                            const dateA = new Date(rawA);
                            const dateB = new Date(rawB);
                            if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
                                newRow[calcCol.name] = 0;
                            } else {
                                const diffMs = dateA.getTime() - dateB.getTime();
                                const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
                                newRow[calcCol.name] = diffDays;
                            }
                        }
                    }
                });
                return newRow;
            });
        }

        // 1. Filtros avanzados AND/OR
        if (filterRules.length > 0) {
            resultRows = resultRows.filter(row => {
                const matches = filterRules.map(rule => {
                    const cellVal = String(row[rule.column] || '').toLowerCase();
                    const filterVal = String(rule.value).toLowerCase();
                    
                    if (rule.operator === 'equals') return cellVal === filterVal;
                    if (rule.operator === 'contains') return cellVal.includes(filterVal);
                    if (rule.operator === 'greater') {
                        const numCell = parseFloat(cellVal.replace(/[^\d.-]/g, ''));
                        const numFilter = parseFloat(filterVal);
                        return !isNaN(numCell) && !isNaN(numFilter) && numCell > numFilter;
                    }
                    if (rule.operator === 'less') {
                        const numCell = parseFloat(cellVal.replace(/[^\d.-]/g, ''));
                        const numFilter = parseFloat(filterVal);
                        return !isNaN(numCell) && !isNaN(numFilter) && numCell < numFilter;
                    }
                    if (rule.operator === 'between_dates') {
                        const parts = filterVal.split(',');
                        if (parts.length === 2) {
                            const dateCell = new Date(cellVal);
                            const dateMin = new Date(parts[0].trim());
                            const dateMax = new Date(parts[1].trim());
                            return !isNaN(dateCell.getTime()) && !isNaN(dateMin.getTime()) && !isNaN(dateMax.getTime()) && dateCell >= dateMin && dateCell <= dateMax;
                        }
                    }
                    if (rule.operator === 'between_numbers') {
                        const parts = filterVal.split(',');
                        if (parts.length === 2) {
                            const numCell = parseFloat(cellVal.replace(/[^\d.-]/g, ''));
                            const numMin = parseFloat(parts[0].trim());
                            const numMax = parseFloat(parts[1].trim());
                            return !isNaN(numCell) && !isNaN(numMin) && !isNaN(numMax) && numCell >= numMin && numCell <= numMax;
                        }
                    }
                    return true;
                });

                if (filterJoinType === 'AND') {
                    return matches.every(m => m === true);
                } else {
                    return matches.some(m => m === true);
                }
            });
        }

        // 2. Concatenador con Delimitador Personalizado
        if (concatColA && concatColB) {
            resultRows = resultRows.map(row => {
                const valA = row[concatColA] !== undefined ? String(row[concatColA]) : '';
                const valB = row[concatColB] !== undefined ? String(row[concatColB]) : '';
                return {
                    ...row,
                    'Concatenado': `${valA}${concatDelimiter}${valB}`
                };
            });
        }

        // 3. Suma Acumulada (Running Total)
        if (runningTotalCol) {
            let cumulative = 0;
            resultRows = resultRows.map(row => {
                const rawVal = String(row[runningTotalCol] || '').replace(/[^\d.-]/g, '');
                const num = parseFloat(rawVal);
                cumulative += isNaN(num) ? 0 : num;
                const isCurrency = runningTotalCol.includes('$') || runningTotalCol.toLowerCase().includes('valor') || runningTotalCol.toLowerCase().includes('monto') || runningTotalCol.toLowerCase().includes('aprobado') || runningTotalCol.toLowerCase().includes('contratado');
                return {
                    ...row,
                    'Acumulado': isCurrency ? formatCLP(cumulative) : cumulative.toLocaleString('es-CL')
                };
            });
        }

        // 4. Promedio Móvil (Moving Average)
        if (movingAverageCol) {
            resultRows = resultRows.map((row, idx) => {
                let sum = 0;
                let count = 0;
                const start = Math.max(0, idx - movingAveragePeriod + 1);
                for (let i = start; i <= idx; i++) {
                    const rawVal = String(resultRows[i][movingAverageCol] || '').replace(/[^\d.-]/g, '');
                    const num = parseFloat(rawVal);
                    if (!isNaN(num)) {
                        sum += num;
                        count++;
                    }
                }
                const avg = count > 0 ? sum / count : 0;
                const isCurrency = movingAverageCol.includes('$') || movingAverageCol.toLowerCase().includes('valor') || movingAverageCol.toLowerCase().includes('monto') || movingAverageCol.toLowerCase().includes('aprobado') || movingAverageCol.toLowerCase().includes('contratado');
                return {
                    ...row,
                    [`Promedio móvil (N=${movingAveragePeriod})`]: isCurrency ? formatCLP(avg) : `${avg.toFixed(1)}%`
                };
            });
        }

        // 5. Variación de Avance (Delta %)
        if (deltaCol) {
            resultRows = resultRows.map((row, idx) => {
                let delta = 0;
                const rawValCur = String(row[deltaCol] || '').replace(/[^\d.-]/g, '');
                const numCur = parseFloat(rawValCur);
                
                if (idx > 0 && !isNaN(numCur)) {
                    const rawValPrev = String(resultRows[idx - 1][deltaCol] || '').replace(/[^\d.-]/g, '');
                    const numPrev = parseFloat(rawValPrev);
                    if (!isNaN(numPrev) && numPrev !== 0) {
                        delta = ((numCur - numPrev) / numPrev) * 100;
                    } else if (numPrev === 0 && numCur > 0) {
                        delta = 100;
                    }
                }
                return {
                    ...row,
                    'Variación %': `${delta >= 0 ? '+' : ''}${delta.toFixed(1)}%`
                };
            });
        }

        return resultRows;
    };

    // --- MOTOR DE AGREGACIÓN MULTI-CAMPO POWER BI ---
    const applyFieldAggregation = (values: any[], aggregation: WidgetFieldConfig['aggregation'] = 'sum'): number | string => {
        if (!values || values.length === 0) return 0;
        
        if (aggregation === 'count') {
            return values.filter(v => v !== null && v !== undefined && v !== '').length;
        }
        if (aggregation === 'distinct_count') {
            const unique = new Set(values.map(v => String(v ?? '')));
            return unique.size;
        }

        const numericVals: number[] = values
            .map(v => {
                if (typeof v === 'number') return v;
                const cleaned = String(v ?? '').replace(/[^\d.-]/g, '');
                const parsed = parseFloat(cleaned);
                return isNaN(parsed) ? null : parsed;
            })
            .filter((v): v is number => v !== null);

        if (numericVals.length === 0) {
            return aggregation === 'none' ? String(values[0] ?? '-') : 0;
        }

        if (aggregation === 'avg') {
            const sum = numericVals.reduce((a, b) => a + b, 0);
            return parseFloat((sum / numericVals.length).toFixed(2));
        }
        if (aggregation === 'min') {
            return Math.min(...numericVals);
        }
        if (aggregation === 'max') {
            return Math.max(...numericVals);
        }
        if (aggregation === 'none') {
            return numericVals[0];
        }
        // Default: 'sum'
        const sum = numericVals.reduce((a, b) => a + b, 0);
        return parseFloat(sum.toFixed(2));
    };

    const aggregateWidgetData = (
        widget: WidgetConfig,
        rows: any[],
        activeCols: string[]
    ): {
        chartData: any[];
        axisKey: string;
        valueKeys: string[];
        legendValues: string[];
        isHierarchical: boolean;
        currentHierarchyLevel: number;
        hasRequiredFields: boolean;
    } => {
        // Validación de campos mínimos según tipo de gráfico
        const isCardOrGauge = widget.type === 'kpi-card' || widget.type === 'gauge' || widget.type === 'kpi-multi';
        const isMatrix = widget.type === 'matrix';
        const isTable = widget.type === 'table';
        const isSlicer = widget.type === 'slicer';

        const hasAxis = widget.axisFields && widget.axisFields.length > 0;
        const hasValues = widget.valueFields && widget.valueFields.length > 0;

        let hasRequiredFields = false;
        if (isSlicer || isTable) {
            hasRequiredFields = hasAxis || !!widget.xAxisKey;
        } else if (isCardOrGauge) {
            hasRequiredFields = hasValues || !!widget.yAxisKey;
        } else if (isMatrix) {
            hasRequiredFields = hasAxis || hasValues || !!widget.xAxisKey || !!widget.yAxisKey;
        } else {
            hasRequiredFields = (hasAxis && hasValues) || (!!widget.xAxisKey && !!widget.yAxisKey);
        }

        // Resolver campos con fallback retrocompatible
        const axisFields: WidgetFieldConfig[] = (widget.axisFields && widget.axisFields.length > 0)
            ? widget.axisFields
            : [{ fieldKey: widget.xAxisKey || activeCols[0] || 'name', aggregation: 'none' }];

        const valueFields: WidgetFieldConfig[] = (widget.valueFields && widget.valueFields.length > 0)
            ? widget.valueFields
            : [{ fieldKey: widget.yAxisKey || activeCols[activeCols.length - 1] || 'value', aggregation: 'sum' }];

        const hState = widgetHierarchyStates[widget.id] || { level: 0, path: [] };
        const currentHierarchyLevel = Math.min(hState.level, axisFields.length - 1);
        const currentAxisField = axisFields[currentHierarchyLevel] || axisFields[0];
        const axisKey = currentAxisField.fieldKey;

        // 1. Filtrar filas según las reglas de FILTROS LÓGICOS a nivel de widget (Visual-Level Filters)
        let filteredRows = [...rows];
        if (widget.logicalFilters && widget.logicalFilters.length > 0) {
            filteredRows = filteredRows.filter(row => {
                return widget.logicalFilters!.every(filter => {
                    const rawVal = row[filter.field];
                    const cellStr = (rawVal !== undefined && rawVal !== null) ? String(rawVal).trim() : '';
                    const cellLower = cellStr.toLowerCase();

                    if (filter.filterType === 'basic') {
                        // Modo básico: si hay lista de excluidos, no permitir
                        if (filter.excludedValues && filter.excludedValues.length > 0) {
                            if (filter.excludedValues.includes(cellStr)) return false;
                        }
                        // Si hay valores permitidos seleccionados en value (string[])
                        if (Array.isArray(filter.value) && filter.value.length > 0) {
                            return filter.value.includes(cellStr);
                        }
                        return true;
                    }

                    // Modo avanzado
                    const targetStr = (filter.value !== undefined && filter.value !== null) ? String(filter.value).trim() : '';
                    const targetLower = targetStr.toLowerCase();

                    if (filter.operator === 'equals') {
                        return cellLower === targetLower;
                    }
                    if (filter.operator === 'not_equals') {
                        return cellLower !== targetLower;
                    }
                    if (filter.operator === 'contains') {
                        return cellLower.includes(targetLower);
                    }
                    if (filter.operator === 'not_contains') {
                        return !cellLower.includes(targetLower);
                    }
                    if (filter.operator === 'is_null') {
                        return cellStr === '' || cellStr === '-' || cellStr === 'null' || cellStr === 'undefined';
                    }
                    if (filter.operator === 'is_not_null') {
                        return cellStr !== '' && cellStr !== '-' && cellStr !== 'null' && cellStr !== 'undefined';
                    }
                    if (filter.operator === 'greater') {
                        const numCell = parseFloat(cellStr.replace(/[^\d.-]/g, ''));
                        const numTarget = parseFloat(targetStr);
                        return !isNaN(numCell) && !isNaN(numTarget) && numCell > numTarget;
                    }
                    if (filter.operator === 'less') {
                        const numCell = parseFloat(cellStr.replace(/[^\d.-]/g, ''));
                        const numTarget = parseFloat(targetStr);
                        return !isNaN(numCell) && !isNaN(numTarget) && numCell < numTarget;
                    }
                    return true;
                });
            });
        }

        // 2. Filtrar filas según el camino de drill-down acumulado
        if (hState.level > 0 && hState.path.length > 0) {
            filteredRows = filteredRows.filter(row => {
                return hState.path.every((pathVal, idx) => {
                    const field = axisFields[idx];
                    if (!field) return true;
                    return String(row[field.fieldKey] ?? '') === String(pathVal);
                });
            });
        }

        const legendField = widget.legendField?.fieldKey;
        const legendValuesSet = new Set<string>();

        // Si hay leyenda, extraer valores únicos
        if (legendField) {
            filteredRows.forEach(r => {
                const lVal = String(r[legendField] ?? 'Otros');
                if (lVal) legendValuesSet.add(lVal);
            });
        }
        const legendValues = Array.from(legendValuesSet);

        // Agrupar filas por el eje actual (con agregación temporal dinámica si el eje es fecha o nivel activo)
        const isTemporalAxis = (
            axisKey.toLowerCase().includes('fecha') ||
            axisKey.toLowerCase().includes('date') ||
            axisKey.toLowerCase().includes('semana') ||
            axisKey.toLowerCase().includes('inicio')
        );

        const groupedMap = new Map<string, any[]>();
        filteredRows.forEach(r => {
            let groupKey = '(Vacío)';

            if (isTemporalAxis) {
                const rDate = extractRowDate(r);
                if (rDate) {
                    const h = getDateHierarchyParts(rDate);
                    if (h) {
                        // Nivel AÑO activo (o por defecto sin mes): agrupar por MESES
                        if (selectedDateMonth === 'todos') {
                            groupKey = `${h.month} ${h.year}`;
                        }
                        // Nivel MES activo (sin semana elegida): agrupar por INICIO DE SEMANA
                        else if (selectedDateWeekStart === 'todos') {
                            groupKey = h.weekStartLabel;
                        }
                        // Nivel SEMANA activo: desglose por DÍA o por tarea individual
                        else {
                            const taskOrSub = r['Tarea'] || r['Partida'] || r['Nombre Tarea'] || r['Restricción'] || r['Unidades Físicas'] || r['Subcontrato'];
                            groupKey = `${h.dayLabel}${taskOrSub ? ` - ${taskOrSub}` : ''}`;
                        }
                    }
                }
            }

            if (groupKey === '(Vacío)') {
                const rawAxisVal = r[axisKey];
                groupKey = (rawAxisVal !== undefined && rawAxisVal !== null && String(rawAxisVal).trim() !== '')
                    ? String(rawAxisVal)
                    : '(Vacío)';
            }

            if (!groupedMap.has(groupKey)) {
                groupedMap.set(groupKey, []);
            }
            groupedMap.get(groupKey)!.push(r);
        });

        // Construir arreglo de datos agregados
        const chartData: any[] = [];
        const valueKeys: string[] = [];

        groupedMap.forEach((groupRows, groupKey) => {
            const item: any = {
                name: groupKey.length > 20 ? groupKey.substring(0, 18) + '...' : groupKey,
                fullName: groupKey,
                __rawRows: groupRows
            };

            if (legendField && legendValues.length > 0) {
                // Caso con leyenda: una serie por cada valor de leyenda
                legendValues.forEach(lVal => {
                    const rowsForLegend = groupRows.filter(r => String(r[legendField] ?? 'Otros') === lVal);
                    valueFields.forEach(vField => {
                        const seriesKey = valueFields.length > 1 
                            ? `${lVal} - ${vField.fieldKey}` 
                            : lVal;
                        if (!valueKeys.includes(seriesKey)) valueKeys.push(seriesKey);
                        const vals = rowsForLegend.map(r => r[vField.fieldKey]);
                        item[seriesKey] = applyFieldAggregation(vals, vField.aggregation || 'sum');
                    });
                });
            } else {
                // Caso estándar: una serie por cada campo de valor
                valueFields.forEach(vField => {
                    const vKey = vField.fieldKey;
                    if (!valueKeys.includes(vKey)) valueKeys.push(vKey);
                    const vals = groupRows.map(r => r[vKey]);
                    item[vKey] = applyFieldAggregation(vals, vField.aggregation || 'sum');
                });
            }

            // Scatter details/size
            if (widget.detailFields && widget.detailFields[0]) {
                item.detail = String(groupRows[0]?.[widget.detailFields[0].fieldKey] ?? groupKey);
            }
            if (widget.sizeField) {
                const sizeVals = groupRows.map(r => r[widget.sizeField!.fieldKey]);
                item.size = applyFieldAggregation(sizeVals, widget.sizeField.aggregation || 'sum');
            }

            chartData.push(item);
        });

        return {
            chartData,
            axisKey,
            valueKeys: valueKeys.length > 0 ? valueKeys : [valueFields[0]?.fieldKey || 'value'],
            legendValues,
            isHierarchical: axisFields.length > 1,
            currentHierarchyLevel,
            hasRequiredFields
        };
    };

    // --- RENDERIZADOR DE GRÁFICOS DEL WIDGET (SOPORTE MULTI-CAMPO / SERIES / LEYENDA POWER BI) ---
    const renderWidgetChart = (widget: any, rows: any[], cols: string[], componentId: string) => {
        const { chartData, axisKey, valueKeys, isHierarchical, hasRequiredFields } = aggregateWidgetData(widget, rows, cols);

        // PLACEHOLDER ESQUELÉTICO GRIS ESTILO POWER BI CUANDO FALTAN CAMPOS
        if (!hasRequiredFields) {
            return (
                <div className="flex-1 w-full h-full min-h-[70px] flex flex-col items-center justify-between p-3 bg-slate-50/40 dark:bg-slate-900/20 rounded-2xl border border-dashed border-slate-200/80 dark:border-slate-800 select-none animate-fade-in group">
                    <div className="flex-1 w-full flex items-center justify-center relative min-h-0">
                        {(widget.type === 'pie' || widget.type === 'pie-solid' || widget.type === 'donut') && (
                            <svg className="w-24 h-24 max-h-[80%] opacity-35 dark:opacity-25" viewBox="0 0 100 100">
                                {widget.type === 'pie-solid' ? (
                                    <circle cx="50" cy="50" r="38" fill="currentColor" className="text-slate-300 dark:text-slate-600" />
                                ) : (
                                    <>
                                        <circle cx="50" cy="50" r="38" fill="none" stroke="currentColor" strokeWidth="18" className="text-slate-300 dark:text-slate-600" />
                                        <circle cx="50" cy="50" r="38" fill="none" stroke="currentColor" strokeWidth="18" strokeDasharray="65 180" className="text-slate-400 dark:text-slate-500" />
                                        <circle cx="50" cy="50" r="38" fill="none" stroke="currentColor" strokeWidth="18" strokeDasharray="45 180" strokeDashoffset="-75" className="text-slate-350 dark:text-slate-550" />
                                    </>
                                )}
                            </svg>
                        )}

                        {(widget.type === 'bar-vertical' || widget.type === 'column-grouped-vertical' || widget.type === 'column-stacked') && (
                            <div className="flex items-end justify-center gap-2.5 h-full max-h-[80px] w-40 opacity-35 dark:opacity-25">
                                <div className="w-5 h-8 bg-slate-300 dark:bg-slate-600 rounded-t-md"></div>
                                <div className="w-5 h-16 bg-slate-400 dark:bg-slate-500 rounded-t-md"></div>
                                <div className="w-5 h-11 bg-slate-300 dark:bg-slate-600 rounded-t-md"></div>
                                <div className="w-5 h-6 bg-slate-400 dark:bg-slate-500 rounded-t-md"></div>
                            </div>
                        )}

                        {(widget.type === 'bar-horizontal' || widget.type === 'bar-grouped-horizontal') && (
                            <div className="flex flex-col justify-center gap-1.5 h-full max-h-[80px] w-40 opacity-35 dark:opacity-25">
                                <div className="h-3 w-28 bg-slate-300 dark:bg-slate-600 rounded-r-md"></div>
                                <div className="h-3 w-36 bg-slate-400 dark:bg-slate-500 rounded-r-md"></div>
                                <div className="h-3 w-20 bg-slate-300 dark:bg-slate-600 rounded-r-md"></div>
                                <div className="h-3 w-32 bg-slate-400 dark:bg-slate-500 rounded-r-md"></div>
                            </div>
                        )}

                        {widget.type === 'combo-chart' && (
                            <div className="relative flex items-end justify-center gap-2.5 h-full max-h-[80px] w-40 opacity-35 dark:opacity-25">
                                <div className="w-4 h-8 bg-slate-300 dark:bg-slate-600 rounded-t-md"></div>
                                <div className="w-4 h-16 bg-slate-400 dark:bg-slate-500 rounded-t-md"></div>
                                <div className="w-4 h-11 bg-slate-300 dark:bg-slate-600 rounded-t-md"></div>
                                <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 60">
                                    <path d="M 10 40 Q 50 10 90 25" fill="none" stroke="currentColor" strokeWidth="3" className="text-slate-500" strokeDasharray="3 2" />
                                </svg>
                            </div>
                        )}

                        {widget.type === 'slicer' && (
                            <div className="w-40 p-2 rounded-lg border border-slate-300 dark:border-slate-600 opacity-40 space-y-1">
                                <div className="h-2 bg-slate-400 rounded w-1/2"></div>
                                <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-full flex items-center px-2">
                                    <div className="h-1 bg-slate-400 rounded w-2/3"></div>
                                </div>
                            </div>
                        )}

                        {(widget.type === 'line' || widget.type === 'area-stacked') && (
                            <svg className="w-40 h-full max-h-[80px] opacity-35 dark:opacity-25" viewBox="0 0 160 80">
                                {widget.type === 'area-stacked' && (
                                    <path d="M 10 70 L 10 45 Q 45 15 80 50 T 150 25 L 150 70 Z" fill="currentColor" className="text-slate-200 dark:text-slate-700" />
                                )}
                                <path d="M 10 45 Q 45 15 80 50 T 150 25" fill="none" stroke="currentColor" strokeWidth="3" className="text-slate-400 dark:bg-slate-500" strokeDasharray="4 2" />
                                <circle cx="10" cy="45" r="3" className="fill-slate-400" />
                                <circle cx="80" cy="50" r="3" className="fill-slate-400" />
                                <circle cx="150" cy="25" r="3" className="fill-slate-400" />
                            </svg>
                        )}

                        {widget.type === 'scatter' && (
                            <svg className="w-40 h-full max-h-[80px] opacity-35 dark:opacity-25" viewBox="0 0 120 70">
                                <circle cx="20" cy="50" r="5" className="fill-slate-400" />
                                <circle cx="45" cy="25" r="8" className="fill-slate-500" />
                                <circle cx="70" cy="40" r="6" className="fill-slate-400" />
                                <circle cx="95" cy="20" r="10" className="fill-slate-500" />
                            </svg>
                        )}

                        {widget.type === 'radar' && (
                            <svg className="w-24 h-24 max-h-[80%] opacity-35 dark:opacity-25" viewBox="0 0 100 100">
                                <polygon points="50,10 90,38 75,82 25,82 10,38" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-slate-300 dark:text-slate-600" />
                                <polygon points="50,25 75,44 65,70 35,70 25,44" fill="currentColor" className="text-slate-400 dark:text-slate-500" fillOpacity="0.4" />
                            </svg>
                        )}

                        {(widget.type === 'kpi-card' || widget.type === 'kpi-multi') && (
                            <div className="flex flex-col items-center justify-center gap-1">
                                <div className="h-2.5 w-14 bg-slate-300 dark:bg-slate-600 rounded"></div>
                                <span className="text-2xl font-black text-slate-400 font-mono tracking-tighter">123</span>
                                <div className="h-1.5 w-20 bg-slate-200 dark:bg-slate-700 rounded"></div>
                            </div>
                        )}

                        {widget.type === 'gauge' && (
                            <svg className="w-28 h-full max-h-[70px] opacity-35 dark:opacity-25" viewBox="0 0 100 60">
                                <path d="M 15 50 A 35 35 0 0 1 85 50" fill="none" stroke="currentColor" strokeWidth="10" className="text-slate-200 dark:text-slate-700" />
                                <path d="M 15 50 A 35 35 0 0 1 60 17" fill="none" stroke="currentColor" strokeWidth="10" className="text-slate-400 dark:text-slate-500" />
                                <text x="50" y="48" textAnchor="middle" className="text-[12px] font-bold fill-slate-400 font-mono">0%</text>
                            </svg>
                        )}

                        {widget.type === 'table' && (
                            <div className="w-44 space-y-1.5 opacity-35 dark:opacity-25">
                                <div className="grid grid-cols-3 gap-1">
                                    <div className="h-2.5 bg-slate-400 rounded"></div>
                                    <div className="h-2.5 bg-slate-400 rounded"></div>
                                    <div className="h-2.5 bg-slate-400 rounded"></div>
                                </div>
                                <div className="grid grid-cols-3 gap-1">
                                    <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded"></div>
                                    <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded"></div>
                                    <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded"></div>
                                </div>
                                <div className="grid grid-cols-3 gap-1">
                                    <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded"></div>
                                    <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded"></div>
                                    <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded"></div>
                                </div>
                            </div>
                        )}

                        {widget.type === 'matrix' && (
                            <div className="w-44 space-y-1 opacity-35 dark:opacity-25">
                                <div className="h-2.5 bg-slate-400 dark:bg-slate-500 rounded w-full"></div>
                                <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded w-5/6 ml-2"></div>
                                <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded w-3/4 ml-4"></div>
                                <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded w-5/6 ml-2"></div>
                            </div>
                        )}
                    </div>

                    {/* Guía Discreta Inferior */}
                    <div className="text-center pb-0.5 shrink-0">
                        <span className="text-[9px] text-slate-400 dark:text-slate-500 font-medium tracking-tight truncate block max-w-full">
                            {widget.type === 'slicer'
                                ? 'Arrastra un campo para segmentar'
                                : widget.type === 'kpi-card' || widget.type === 'kpi-multi' || widget.type === 'gauge'
                                ? 'Arrastra un campo a Valores'
                                : widget.type === 'matrix'
                                ? 'Arrastra campos a Filas / Eje X'
                                : 'Arrastra campos a Eje X y Valores desde el panel Datos'}
                        </span>
                    </div>
                </div>
            );
        }

        if (rows.length === 0 || chartData.length === 0) {
            return (
                <div className="flex-1 flex flex-col items-center justify-center p-6 text-center text-slate-400 dark:text-slate-500 italic bg-slate-50/30 dark:bg-slate-900/10 rounded-2xl">
                    <span className="material-symbols-outlined text-[28px] text-slate-300 dark:text-slate-600 mb-1">filter_alt_off</span>
                    <span className="text-xs">Sin registros para graficar.</span>
                </div>
            );
        }
        const hierarchy = COMPONENT_HIERARCHIES[componentId] || [];
        const hState = widgetHierarchyStates[widget.id] || { level: 0, path: [] };
        const canDrillDown = isHierarchical || hierarchy.length > 0;

        const handleElementClick = (val: string) => {
            if (isHierarchical && widget.axisFields && hState.level < widget.axisFields.length - 1) {
                setWidgetHierarchyStates(prev => ({
                    ...prev,
                    [widget.id]: {
                        level: hState.level + 1,
                        path: [...hState.path, val]
                    }
                }));
            } else if (!isHierarchical && hState.level < hierarchy.length - 1) {
                setWidgetHierarchyStates(prev => ({
                    ...prev,
                    [widget.id]: {
                        level: hState.level + 1,
                        path: [...hState.path, val]
                    }
                }));
            } else if (crossFilteringActive) {
                setCrossFilter(prev => (prev?.value === val && prev?.key === axisKey) ? null : { key: axisKey, value: val, componentId });
            }
        };

        const wStyle = widget.style || {};
        const showLegend = wStyle.showLegend !== false;
        const legendPos = wStyle.legendPosition || 'bottom';
        const showDataLabels = wStyle.showDataLabels === true;
        const dataLabelContent = wStyle.dataLabelContent || 'category_value';
        const dataLabelDecimals = wStyle.dataLabelDecimals !== undefined ? wStyle.dataLabelDecimals : 2;
        const showXAxis = wStyle.showXAxis !== false;
        const showYAxis = wStyle.showYAxis !== false;
        const primaryColor = wStyle.primaryColor || '#3b82f6';
        const customPalette = wStyle.paletteColors && wStyle.paletteColors.length > 0 ? wStyle.paletteColors : null;
        const PALETTE = customPalette || [primaryColor, '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316', '#6366f1'];

        // Helper para posición de leyenda en recharts
        const legendAlign = legendPos === 'left' ? 'left' : legendPos === 'right' ? 'right' : 'center';
        const legendVerticalAlign = legendPos === 'top' ? 'top' : 'bottom';
        const legendLayout = (legendPos === 'left' || legendPos === 'right') ? 'vertical' : 'horizontal';

        // Helper para formatear valores agregados reales en etiquetas
        const formatMetricValue = (val: any, fieldKeyName?: string) => {
            if (val === null || val === undefined || val === '') return '-';
            const num = typeof val === 'number' ? val : parseFloat(String(val).replace(/[^\d.-]/g, ''));
            if (isNaN(num)) return String(val);

            const labelFormat = wStyle.dataLabelFormat || 'auto';

            // Override manual desde Formato Visual
            if (labelFormat === 'integer') {
                return Math.round(num).toLocaleString('es-CL');
            }
            if (labelFormat === 'decimal') {
                return num.toFixed(dataLabelDecimals);
            }
            if (labelFormat === 'percent') {
                return `${num.toFixed(dataLabelDecimals)}%`;
            }
            if (labelFormat === 'currency') {
                return formatCLP(num);
            }

            // Inferencia Inteligente ('auto')
            const targetField = (widget.valueFields || []).find(f => f.fieldKey === (fieldKeyName || valueKeys[0])) 
                || (widget.valueFields?.[0]);
            const agg = targetField?.aggregation || 'sum';

            // 1. Si es Recuento o Recuento Distintivo -> Número Entero sin %
            if (agg === 'count' || agg === 'distinct_count') {
                return Math.round(num).toLocaleString('es-CL');
            }

            const key = (fieldKeyName || valueKeys[0] || '').toLowerCase();
            const isPct = key.includes('%') || key.includes('ppc') || key.includes('avance') || key.includes('cumplimiento');
            const isCurr = key.includes('$') || key.includes('monto') || key.includes('valor') || key.includes('costo') || key.includes('aprobado') || key.includes('contratado');

            if (isCurr) return formatCLP(num);
            if (isPct) return `${num.toFixed(dataLabelDecimals)}%`;
            return num % 1 === 0 ? num.toLocaleString('es-CL') : num.toFixed(dataLabelDecimals);
        };

        if (widget.type === 'bar-vertical' || widget.type === 'column-grouped-vertical' || widget.type === 'column-stacked') {
            const displayData = chartData.slice(0, 15);
            const isStacked = widget.type === 'column-stacked';

            return (
                <div className="flex-1 w-full h-full min-h-0">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={displayData} margin={{ top: 15, right: 10, left: -20, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                            {showXAxis && <XAxis dataKey="name" tick={{ fontSize: 8 }} stroke="#888888" />}
                            {showYAxis && <YAxis tick={{ fontSize: 8 }} stroke="#888888" />}
                            <Tooltip wrapperStyle={{ fontSize: 10 }} />
                            {showLegend && (valueKeys.length > 1 || legendPos !== 'bottom') && (
                                <Legend 
                                    align={legendAlign} 
                                    verticalAlign={legendVerticalAlign} 
                                    layout={legendLayout} 
                                    wrapperStyle={{ fontSize: 9 }} 
                                    iconSize={8} 
                                />
                            )}
                            {valueKeys.map((vKey, idx) => (
                                <Bar 
                                    key={vKey}
                                    dataKey={vKey} 
                                    stackId={isStacked ? "stackedCol" : undefined}
                                    fill={PALETTE[idx % PALETTE.length]} 
                                    radius={isStacked ? [0, 0, 0, 0] : [3, 3, 0, 0]} 
                                    label={showDataLabels ? {
                                        position: isStacked ? 'inside' : 'top',
                                        fontSize: 8,
                                        fill: isStacked ? '#ffffff' : '#475569',
                                        formatter: (v: any) => formatMetricValue(v, vKey)
                                    } : false}
                                    onClick={(data: any) => {
                                        if (data && (data.fullName || data.name)) {
                                            handleElementClick(data.fullName || data.name);
                                        }
                                    }}
                                    style={{ cursor: (crossFilteringActive || canDrillDown) ? 'pointer' : 'default' }}
                                />
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            );
        }

        if (widget.type === 'bar-horizontal' || widget.type === 'bar-grouped-horizontal') {
            const displayData = chartData.slice(0, 10);
            return (
                <div className="flex-1 w-full h-full min-h-0">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart layout="vertical" data={displayData} margin={{ top: 10, right: 25, left: 10, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                            {showXAxis && <XAxis type="number" tick={{ fontSize: 8 }} stroke="#888888" />}
                            {showYAxis && <YAxis type="category" dataKey="name" tick={{ fontSize: 8 }} stroke="#888888" width={65} />}
                            <Tooltip wrapperStyle={{ fontSize: 10 }} />
                            {showLegend && (valueKeys.length > 1 || legendPos !== 'bottom') && (
                                <Legend 
                                    align={legendAlign} 
                                    verticalAlign={legendVerticalAlign} 
                                    layout={legendLayout} 
                                    wrapperStyle={{ fontSize: 9 }} 
                                    iconSize={8} 
                                />
                            )}
                            {valueKeys.map((vKey, idx) => (
                                <Bar 
                                    key={vKey}
                                    dataKey={vKey} 
                                    fill={PALETTE[idx % PALETTE.length]} 
                                    radius={[0, 3, 3, 0]} 
                                    label={showDataLabels ? {
                                        position: 'right',
                                        fontSize: 8,
                                        fill: '#475569',
                                        formatter: (v: any) => formatMetricValue(v, vKey)
                                    } : false}
                                    onClick={(data: any) => {
                                        if (data && (data.fullName || data.name)) {
                                            handleElementClick(data.fullName || data.name);
                                        }
                                    }}
                                    style={{ cursor: (crossFilteringActive || canDrillDown) ? 'pointer' : 'default' }}
                                />
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            );
        }

        if (widget.type === 'combo-chart') {
            const displayData = chartData.slice(0, 15);
            // Column key is first value key; Line key is detail field or second value key
            const colKey = valueKeys[0] || 'Columnas';
            const lineKey = widget.detailFields?.[0]?.fieldKey || (valueKeys.length > 1 ? valueKeys[1] : null);

            return (
                <div className="flex-1 w-full h-full min-h-0">
                    <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={displayData} margin={{ top: 15, right: 25, left: -20, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                            {showXAxis && <XAxis dataKey="name" tick={{ fontSize: 8 }} stroke="#888888" />}
                            {showYAxis && <YAxis yAxisId="left" tick={{ fontSize: 8 }} stroke="#888888" />}
                            {showYAxis && lineKey && (
                                <YAxis 
                                    yAxisId="right" 
                                    orientation="right" 
                                    tick={{ fontSize: 8 }} 
                                    stroke={PALETTE[1] || '#10b981'} 
                                    unit="%" 
                                />
                            )}
                            <Tooltip wrapperStyle={{ fontSize: 10 }} />
                            {showLegend && (
                                <Legend 
                                    align={legendAlign} 
                                    verticalAlign={legendVerticalAlign} 
                                    layout={legendLayout} 
                                    wrapperStyle={{ fontSize: 9 }} 
                                    iconSize={8} 
                                />
                            )}
                            <Bar 
                                yAxisId="left"
                                dataKey={colKey} 
                                fill={PALETTE[0] || '#3b82f6'} 
                                radius={[3, 3, 0, 0]} 
                                label={showDataLabels ? {
                                    position: 'top',
                                    fontSize: 8,
                                    fill: '#475569',
                                    formatter: (v: any) => formatMetricValue(v, colKey)
                                } : false}
                                onClick={(data: any) => {
                                    if (data && (data.fullName || data.name)) {
                                        handleElementClick(data.fullName || data.name);
                                    }
                                }}
                                style={{ cursor: (crossFilteringActive || canDrillDown) ? 'pointer' : 'default' }}
                            />
                            {lineKey && (
                                <Line 
                                    yAxisId="right"
                                    type="monotone" 
                                    dataKey={lineKey} 
                                    stroke={PALETTE[1] || '#10b981'} 
                                    strokeWidth={2.5} 
                                    dot={{ r: 3 }} 
                                    label={showDataLabels ? {
                                        position: 'top',
                                        fontSize: 8,
                                        fill: '#10b981',
                                        formatter: (v: any) => `${parseFloat(String(v || 0)).toFixed(1)}%`
                                    } : false}
                                />
                            )}
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            );
        }

        if (widget.type === 'slicer') {
            // Para el slicer, obtenemos los valores únicos a partir de las filas base del componente
            // de modo que seleccionar un filtro no haga desaparecer las otras opciones disponibles.
            const slicerSourceRows = getComponentRows(widget.componentId) || rows;
            const isDateSlicerField = (
                axisKey.toLowerCase().includes('fecha') ||
                axisKey.toLowerCase().includes('date') ||
                axisKey.toLowerCase().includes('inicio') ||
                axisKey.toLowerCase().includes('termino')
            );

            // Determinar si aplica el modo jerárquico (Año -> Mes -> Semana)
            // Si es un campo de fecha, por defecto es Jerárquico a menos que el usuario lo cambie a Plano (false)
            const isDateHierarchyActive = isDateSlicerField 
                ? (wStyle.slicerDateHierarchy !== false) 
                : !!wStyle.slicerDateHierarchy;

            // Extraer y ordenar valores formateados en dd-mm-aaaa
            const rawUniqueVals = Array.from(new Set(slicerSourceRows.map(r => {
                const v = r[axisKey];
                if (v === undefined || v === null || String(v).trim() === '') return '(Vacío)';
                if (isDateSlicerField) {
                    const d = parseDateSafe(v);
                    if (d) return formatDateDDMMYYYY(d);
                }
                return String(v).trim();
            }))).sort((a, b) => {
                // Orden cronológico si son fechas dd-mm-aaaa
                const da = parseDateSafe(a);
                const db = parseDateSafe(b);
                if (da && db) return da.getTime() - db.getTime();
                return a.localeCompare(b);
            });

            // Estructurar árbol jerárquico (Año -> Mes -> Fechas) para campos de fecha
            interface SlicerHierarchyYear {
                year: string;
                months: {
                    monthKey: string;
                    monthLabel: string;
                    dates: string[];
                }[];
            }

            const hierarchyTree: SlicerHierarchyYear[] = (() => {
                if (!isDateHierarchyActive) return [];
                const yearsMap = new Map<string, Map<string, { monthLabel: string; dates: Set<string> }>>();

                slicerSourceRows.forEach(r => {
                    const v = r[axisKey];
                    if (!v) return;
                    const d = parseDateSafe(v);
                    if (!d) return;
                    const h = getDateHierarchyParts(d);
                    if (!h) return;

                    const formattedD = formatDateDDMMYYYY(d);

                    if (!yearsMap.has(h.year)) {
                        yearsMap.set(h.year, new Map());
                    }
                    const monthMap = yearsMap.get(h.year)!;
                    if (!monthMap.has(h.monthKey)) {
                        monthMap.set(h.monthKey, {
                            monthLabel: `${h.month} ${h.year}`,
                            dates: new Set()
                        });
                    }
                    monthMap.get(h.monthKey)!.dates.add(formattedD);
                });

                const sortedTree: SlicerHierarchyYear[] = [];
                Array.from(yearsMap.keys()).sort().forEach(yr => {
                    const mObj = yearsMap.get(yr)!;
                    const months = Array.from(mObj.keys()).sort().map(mKey => {
                        const mVal = mObj.get(mKey)!;
                        return {
                            monthKey: mKey,
                            monthLabel: mVal.monthLabel,
                            dates: Array.from(mVal.dates).sort((da, db) => {
                                const pA = parseDateSafe(da);
                                const pB = parseDateSafe(db);
                                return (pA && pB) ? pA.getTime() - pB.getTime() : da.localeCompare(db);
                            })
                        };
                    });
                    sortedTree.push({ year: yr, months });
                });

                return sortedTree;
            })();

            const slicerMode = wStyle.slicerVisualMode || 'dropdown';
            const selectedVal = slicerFilters[axisKey];
            const isDropdownOpen = !!openDropdownSlicers[widget.id];

            // Obtener lista normalizada de valores actualmente seleccionados (array)
            const currentSelectedList: string[] = Array.isArray(selectedVal)
                ? selectedVal.map(item => isDateSlicerField ? formatIfDate(item) : String(item))
                : (selectedVal !== undefined && selectedVal !== null && selectedVal !== '')
                    ? [isDateSlicerField ? formatIfDate(selectedVal) : String(selectedVal)]
                    : [];

            // Manejador interactivo con Click (Selección Única / Toggle) vs Ctrl/Cmd+Click (Selección Múltiple)
            const handleOptionClick = (val: string, e: React.MouseEvent) => {
                const isMultiMode = e.ctrlKey || e.metaKey;

                setSlicerFilters(prev => {
                    const prevVal = prev[axisKey];
                    const prevList: string[] = Array.isArray(prevVal) 
                        ? prevVal.map(v => isDateSlicerField ? formatIfDate(v) : String(v))
                        : (prevVal !== undefined && prevVal !== null && prevVal !== '') ? [isDateSlicerField ? formatIfDate(prevVal) : String(prevVal)] : [];

                    if (isMultiMode) {
                        // Modo Selección Múltiple (Ctrl+Click / Cmd+Click)
                        let nextList: string[];
                        if (prevList.includes(val)) {
                            nextList = prevList.filter(v => v !== val);
                        } else {
                            nextList = [...prevList, val];
                        }
                        if (nextList.length === 0) {
                            const next = { ...prev };
                            delete next[axisKey];
                            return next;
                        }
                        return { ...prev, [axisKey]: nextList };
                    } else {
                        // Modo Selección Única (Click Normal)
                        if (prevList.length === 1 && prevList[0] === val) {
                            const next = { ...prev };
                            delete next[axisKey];
                            return next;
                        }
                        return { ...prev, [axisKey]: [val] };
                    }
                });
            };

            const handleClearSlicer = (e?: React.MouseEvent) => {
                if (e) e.stopPropagation();
                setSlicerFilters(prev => {
                    const next = { ...prev };
                    delete next[axisKey];
                    return next;
                });
            };

            // Cálculo dinámico de etiqueta de cabecera del desplegable
            const getDropdownHeaderText = () => {
                if (currentSelectedList.length === 0) {
                    return `Seleccionar ${axisKey || 'Opción'}... (Todos)`;
                }
                if (currentSelectedList.length === 1) {
                    return currentSelectedList[0];
                }
                if (currentSelectedList.length <= 2) {
                    return currentSelectedList.join(', ');
                }
                return `(${currentSelectedList.length}) Seleccionados`;
            };

            const isCompactSingleRow = (widget.rowSpan === 1) || (wStyle.showTitle === false);

            return (
                <div className={`flex-1 w-full h-full min-h-0 flex flex-col justify-center ${isCompactSingleRow ? 'p-0 bg-transparent border-0' : 'p-3 bg-white dark:bg-slate-800/40 rounded-xl border border-slate-200 dark:border-slate-700/60'} overflow-visible relative`}>
                    {/* Encabezado del Slicer: solo se muestra si el usuario no ha desactivado el título */}
                    {wStyle.showTitle !== false && (
                        <div className="flex items-center justify-between pb-2 border-b border-slate-100 dark:border-slate-700/50 mb-2 shrink-0">
                            <span className="text-[10px] font-black text-slate-700 dark:text-slate-200 uppercase tracking-wider flex items-center gap-1">
                                <span className="material-symbols-outlined text-[13px] text-primary">
                                    {isDateHierarchyActive ? 'account_tree' : 'filter_alt'}
                                </span>
                                {wStyle.customTitle || axisKey}
                                {isDateHierarchyActive && (
                                    <span className="text-[7.5px] font-extrabold uppercase tracking-tight bg-primary/10 text-primary px-1.5 py-0.5 rounded ml-1">
                                        Jerárquico
                                    </span>
                                )}
                            </span>
                            {currentSelectedList.length > 0 && (
                                <button 
                                    type="button"
                                    onClick={handleClearSlicer}
                                    className="text-[9px] text-primary hover:underline font-bold flex items-center gap-0.5 cursor-pointer"
                                    title="Borrar filtro"
                                >
                                    <span className="material-symbols-outlined text-[12px]">filter_alt_off</span>
                                    Limpiar
                                </button>
                            )}
                        </div>
                    )}

                    {/* MODO 1: DROPDOWN INTERACTIVO SENIOR (CLICK vs CTRL+CLICK) */}
                    {slicerMode === 'dropdown' && (
                        <div data-slicer-dropdown="true" className="relative w-full shrink-0" onClick={e => e.stopPropagation()}>
                            {/* Botón Principal del Desplegable */}
                            <div 
                                onClick={() => setOpenDropdownSlicers(prev => ({ ...prev, [widget.id]: !prev[widget.id] }))}
                                className={`w-full p-2 bg-slate-50 dark:bg-slate-900 border rounded-xl flex items-center justify-between gap-2 cursor-pointer transition-all ${
                                    isDropdownOpen 
                                        ? 'border-primary ring-2 ring-primary/20 shadow-md' 
                                        : currentSelectedList.length > 0 
                                            ? 'border-primary/60 bg-primary/5 text-primary' 
                                            : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                                }`}
                            >
                                <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                    <span className="material-symbols-outlined text-[14px] text-slate-400 shrink-0">
                                        {currentSelectedList.length > 0 ? 'check_circle' : 'expand_circle_down'}
                                    </span>
                                    <span className={`text-xs truncate font-bold ${currentSelectedList.length > 0 ? 'text-primary font-black' : 'text-slate-600 dark:text-slate-300'}`}>
                                        {getDropdownHeaderText()}
                                    </span>
                                </div>

                                <div className="flex items-center gap-1 shrink-0">
                                    {currentSelectedList.length > 0 && (
                                        <button
                                            type="button"
                                            onClick={handleClearSlicer}
                                            className="size-5 rounded-full hover:bg-slate-200 dark:hover:bg-slate-800 flex items-center justify-center text-slate-400 hover:text-red-500 transition-colors"
                                            title="Limpiar selección"
                                        >
                                            <span className="material-symbols-outlined text-[12px]">close</span>
                                        </button>
                                    )}
                                    <span className={`material-symbols-outlined text-[16px] text-slate-400 transition-transform ${isDropdownOpen ? 'rotate-180 text-primary' : ''}`}>
                                        keyboard_arrow_down
                                    </span>
                                </div>
                            </div>

                            {/* Menú Flotante de Opciones */}
                            {isDropdownOpen && (
                                <div className="absolute top-full left-0 right-0 mt-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-2xl z-50 p-1.5 max-h-64 overflow-y-auto space-y-1 animate-in fade-in zoom-in-95 duration-150">
                                    {/* Indicador de ayuda */}
                                    <div className="px-2 py-1 bg-slate-100/80 dark:bg-slate-800/80 rounded-lg text-[9px] text-slate-500 dark:text-slate-400 flex items-center justify-between mb-1">
                                        <span className="flex items-center gap-1">
                                            <span className="material-symbols-outlined text-[12px] text-primary">check_box</span>
                                            Casillas: Selección múltiple de Mes / Año
                                        </span>
                                        <span className="font-mono font-bold bg-slate-200 dark:bg-slate-700 px-1 py-0.5 rounded text-[8px]">Ctrl + Click: Opcional</span>
                                    </div>

                                    {/* Opción Todos */}
                                    <div
                                        onClick={handleClearSlicer}
                                        className={`px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center justify-between cursor-pointer transition-colors ${
                                            currentSelectedList.length === 0 
                                                ? 'bg-primary/10 text-primary font-black' 
                                                : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                                        }`}
                                    >
                                        <span className="flex items-center gap-1.5">
                                            <span className="material-symbols-outlined text-[14px]">select_all</span>
                                            (Todos los registros)
                                        </span>
                                        {currentSelectedList.length === 0 && (
                                            <span className="material-symbols-outlined text-[14px] text-primary">done</span>
                                        )}
                                    </div>

                                    {/* VISTA 1: JERARQUÍA DE FECHAS ESTILO POWER BI (Año -> Mes -> Semana) */}
                                    {isDateHierarchyActive && hierarchyTree.length > 0 ? (
                                        <div className="space-y-1.5 pt-1 border-t border-slate-100 dark:border-slate-800">
                                            {hierarchyTree.map(yrNode => {
                                                const isYearSelected = currentSelectedList.includes(yrNode.year);
                                                // Check if all months or some months are selected
                                                const allMonthsInYear = yrNode.months.map(m => m.monthKey);
                                                const hasAnyMonthSelected = yrNode.months.some(m => 
                                                    currentSelectedList.includes(m.monthKey) || currentSelectedList.includes(m.monthLabel)
                                                );

                                                return (
                                                    <details key={yrNode.year} open className="group/year bg-slate-50/60 dark:bg-slate-800/40 rounded-lg border border-slate-200/70 dark:border-slate-700/70 overflow-hidden">
                                                        <summary className="px-2.5 py-1.5 flex items-center justify-between cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800/80 select-none text-xs font-black text-slate-800 dark:text-slate-200">
                                                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                                                <span className="material-symbols-outlined text-[13px] text-slate-400 group-open/year:rotate-90 transition-transform shrink-0">
                                                                    chevron_right
                                                                </span>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isYearSelected}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        // Checkbox toggle always multi-selects without needing Ctrl
                                                                        setSlicerFilters(prev => {
                                                                            const prevVal = prev[axisKey];
                                                                            const prevList: string[] = Array.isArray(prevVal) 
                                                                                ? prevVal.map(v => isDateSlicerField ? formatIfDate(v) : String(v))
                                                                                : (prevVal !== undefined && prevVal !== null && prevVal !== '') ? [isDateSlicerField ? formatIfDate(prevVal) : String(prevVal)] : [];
                                                                            
                                                                            let nextList: string[];
                                                                            if (prevList.includes(yrNode.year)) {
                                                                                nextList = prevList.filter(v => v !== yrNode.year);
                                                                            } else {
                                                                                // Also remove child months/dates to keep filter clean
                                                                                const childDates = new Set<string>();
                                                                                yrNode.months.forEach(m => {
                                                                                    childDates.add(m.monthKey);
                                                                                    childDates.add(m.monthLabel);
                                                                                    m.dates.forEach(d => childDates.add(d));
                                                                                });
                                                                                nextList = [...prevList.filter(v => !childDates.has(v)), yrNode.year];
                                                                            }

                                                                            if (nextList.length === 0) {
                                                                                const next = { ...prev };
                                                                                delete next[axisKey];
                                                                                return next;
                                                                            }
                                                                            return { ...prev, [axisKey]: nextList };
                                                                        });
                                                                    }}
                                                                    className="rounded text-primary focus:ring-primary h-3.5 w-3.5 cursor-pointer shrink-0"
                                                                    title="Seleccionar / deseleccionar año completo"
                                                                />
                                                                <span className="material-symbols-outlined text-[14px] text-primary shrink-0">calendar_today</span>
                                                                <span className="truncate">Año {yrNode.year}</span>
                                                            </div>
                                                            <div 
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleOptionClick(yrNode.year, e);
                                                                }}
                                                                className={`px-2 py-0.5 rounded text-[8px] uppercase tracking-wider font-bold transition-all cursor-pointer ${
                                                                    isYearSelected ? 'bg-primary text-white font-black shadow-xs' : 'hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500'
                                                                }`}
                                                                title="Filtrar únicamente este año (o Ctrl+Click para sumar)"
                                                            >
                                                                {isYearSelected ? '✓ Todo el Año' : 'Solo este año'}
                                                            </div>
                                                        </summary>

                                                        {/* Meses */}
                                                        <div className="pl-4 pr-1.5 pb-1 space-y-1 border-t border-slate-100 dark:border-slate-800/60">
                                                            {yrNode.months.map(mNode => {
                                                                const isMonthSelected = currentSelectedList.includes(mNode.monthKey) || currentSelectedList.includes(mNode.monthLabel);
                                                                return (
                                                                    <details key={mNode.monthKey} open className="group/month rounded border-l-2 border-primary/40 pl-2 py-0.5">
                                                                        <summary className="py-1 flex items-center justify-between cursor-pointer hover:text-primary select-none text-[11px] font-bold text-slate-700 dark:text-slate-300">
                                                                            <div className="flex items-center gap-1.5 flex-1 min-w-0">
                                                                                <span className="material-symbols-outlined text-[12px] text-slate-400 group-open/month:rotate-90 transition-transform shrink-0">
                                                                                    chevron_right
                                                                                </span>
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={isMonthSelected || isYearSelected}
                                                                                    disabled={isYearSelected}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        // Direct checkbox toggle for month
                                                                                        setSlicerFilters(prev => {
                                                                                            const prevVal = prev[axisKey];
                                                                                            const prevList: string[] = Array.isArray(prevVal) 
                                                                                                ? prevVal.map(v => isDateSlicerField ? formatIfDate(v) : String(v))
                                                                                                : (prevVal !== undefined && prevVal !== null && prevVal !== '') ? [isDateSlicerField ? formatIfDate(prevVal) : String(prevVal)] : [];

                                                                                            let nextList: string[];
                                                                                            if (prevList.includes(mNode.monthKey) || prevList.includes(mNode.monthLabel)) {
                                                                                                nextList = prevList.filter(v => v !== mNode.monthKey && v !== mNode.monthLabel);
                                                                                            } else {
                                                                                                // Remove child individual dates if present
                                                                                                const mDates = new Set(mNode.dates);
                                                                                                nextList = [...prevList.filter(v => !mDates.has(v)), mNode.monthKey];
                                                                                            }

                                                                                            if (nextList.length === 0) {
                                                                                                const next = { ...prev };
                                                                                                delete next[axisKey];
                                                                                                return next;
                                                                                            }
                                                                                            return { ...prev, [axisKey]: nextList };
                                                                                        });
                                                                                    }}
                                                                                    className="rounded text-primary focus:ring-primary h-3.5 w-3.5 cursor-pointer shrink-0 disabled:opacity-50"
                                                                                    title="Casilla para seleccionar mes (permite múltiples meses sin Ctrl)"
                                                                                />
                                                                                <span className="truncate">{mNode.monthLabel}</span>
                                                                            </div>
                                                                            <div 
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleOptionClick(mNode.monthKey, e);
                                                                                }}
                                                                                className={`px-1.5 py-0.5 rounded text-[8px] uppercase tracking-wider font-bold transition-all cursor-pointer ${
                                                                                    isMonthSelected ? 'bg-primary text-white font-black shadow-xs' : 'hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500'
                                                                                }`}
                                                                                title="Filtrar solo este mes"
                                                                            >
                                                                                {isMonthSelected ? '✓ Mes' : 'Solo mes'}
                                                                            </div>
                                                                        </summary>

                                                                        {/* Fechas exactas del campo dd-mm-aaaa */}
                                                                        <div className="pl-3 space-y-0.5 pt-0.5">
                                                                            {mNode.dates.map(dateStr => {
                                                                                const isDateSelected = currentSelectedList.includes(dateStr);
                                                                                const isInherited = isYearSelected || isMonthSelected;
                                                                                return (
                                                                                    <div
                                                                                        key={dateStr}
                                                                                        onClick={(e) => handleOptionClick(dateStr, e)}
                                                                                        className={`px-2 py-1 rounded text-[10px] flex items-center justify-between cursor-pointer select-none transition-colors ${
                                                                                            isDateSelected
                                                                                                ? 'bg-primary text-white font-black shadow-xs'
                                                                                                : isInherited
                                                                                                    ? 'bg-primary/10 text-primary font-bold'
                                                                                                    : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 font-semibold'
                                                                                        }`}
                                                                                    >
                                                                                        <span className="flex items-center gap-1.5">
                                                                                            <input
                                                                                                type="checkbox"
                                                                                                checked={isDateSelected || isInherited}
                                                                                                disabled={isInherited}
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    // Toggle date directly in multi mode
                                                                                                    setSlicerFilters(prev => {
                                                                                                        const prevVal = prev[axisKey];
                                                                                                        const prevList: string[] = Array.isArray(prevVal) 
                                                                                                            ? prevVal.map(v => isDateSlicerField ? formatIfDate(v) : String(v))
                                                                                                            : (prevVal !== undefined && prevVal !== null && prevVal !== '') ? [isDateSlicerField ? formatIfDate(prevVal) : String(prevVal)] : [];
                                                                                                        
                                                                                                        let nextList: string[];
                                                                                                        if (prevList.includes(dateStr)) {
                                                                                                            nextList = prevList.filter(v => v !== dateStr);
                                                                                                        } else {
                                                                                                            nextList = [...prevList, dateStr];
                                                                                                        }

                                                                                                        if (nextList.length === 0) {
                                                                                                            const next = { ...prev };
                                                                                                            delete next[axisKey];
                                                                                                            return next;
                                                                                                        }
                                                                                                        return { ...prev, [axisKey]: nextList };
                                                                                                    });
                                                                                                }}
                                                                                                className="rounded text-primary focus:ring-primary h-3 w-3 cursor-pointer shrink-0 disabled:opacity-40"
                                                                                            />
                                                                                            <span className="material-symbols-outlined text-[12px] opacity-70">event</span>
                                                                                            <span>{dateStr}</span>
                                                                                        </span>
                                                                                        {isDateSelected && (
                                                                                            <span className="material-symbols-outlined text-[12px]">check</span>
                                                                                        )}
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </details>
                                                                );
                                                            })}
                                                        </div>
                                                    </details>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        /* VISTA 2: LISTA PLANA (CON FORMATO dd-mm-aaaa SI APLICA) */
                                        rawUniqueVals.map(val => {
                                            const isSelected = currentSelectedList.includes(val);
                                            return (
                                                <div
                                                    key={val}
                                                    onClick={(e) => handleOptionClick(val, e)}
                                                    className={`px-2.5 py-1.5 rounded-lg text-xs flex items-center justify-between cursor-pointer select-none transition-colors ${
                                                        isSelected 
                                                            ? 'bg-primary text-white font-black shadow-xs' 
                                                            : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 font-medium'
                                                    }`}
                                                >
                                                    <span className={`truncate flex-1 mr-2 ${isDateSlicerField ? 'font-mono' : ''}`}>{val}</span>
                                                    <div className="flex items-center gap-1">
                                                        {isSelected && (
                                                            <span className="material-symbols-outlined text-[14px]">check</span>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* MODO 2: PILLS / PASTILLAS */}
                    {slicerMode === 'pills' && (
                        <div className="flex-1 overflow-y-auto flex flex-wrap gap-1.5 p-1 content-start min-h-0">
                            {rawUniqueVals.map(val => {
                                const isSelected = currentSelectedList.includes(val);
                                return (
                                    <button
                                        key={val}
                                        type="button"
                                        onClick={(e) => handleOptionClick(val, e)}
                                        className={`px-2.5 py-1 rounded-full text-[10px] font-bold transition-all ${
                                            isSelected 
                                                ? 'bg-primary text-white shadow-xs scale-[1.02]' 
                                                : 'bg-slate-100 dark:bg-slate-700/60 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                                        } ${isDateSlicerField ? 'font-mono' : ''}`}
                                    >
                                        {val}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* MODO 3: CASILLAS DE VERIFICACIÓN (CHECKBOX) */}
                    {slicerMode === 'checkbox' && (
                        <div className="flex-1 overflow-y-auto space-y-1 pr-1 min-h-0">
                            {rawUniqueVals.map(val => {
                                const isChecked = currentSelectedList.includes(val);
                                return (
                                    <label 
                                        key={val} 
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleOptionClick(val, e);
                                        }}
                                        className="flex items-center gap-2 p-1 rounded hover:bg-slate-50 dark:hover:bg-slate-700/40 cursor-pointer text-[10px] text-slate-700 dark:text-slate-200 select-none"
                                    >
                                        <input 
                                            type="checkbox" 
                                            checked={isChecked}
                                            readOnly
                                            className="rounded text-primary h-3.5 w-3.5 pointer-events-none"
                                        />
                                        <span className={`truncate flex-1 ${isChecked ? 'font-black text-primary' : 'font-medium'} ${isDateSlicerField ? 'font-mono' : ''}`}>{val}</span>
                                    </label>
                                );
                            })}
                        </div>
                    )}
                </div>
            );
        }

        if (widget.type === 'pie' || widget.type === 'pie-solid' || widget.type === 'donut') {
            const primaryKey = valueKeys[0] || 'value';
            const rawPieData = chartData.slice(0, 12).map(d => {
                const rawV = d[primaryKey];
                const num = typeof rawV === 'number' ? rawV : parseFloat(String(rawV ?? '').replace(/[^\d.-]/g, '')) || 0;
                return {
                    name: d.name,
                    fullName: d.fullName,
                    value: num
                };
            });

            const totalSum = rawPieData.reduce((acc, cur) => acc + cur.value, 0);
            const pieData = rawPieData.map(d => ({
                ...d,
                percentRatio: totalSum > 0 ? d.value / totalSum : 0
            }));

            const COLORS = PALETTE;
            const isInsideLabel = (wStyle.dataLabelPosition === 'inside');

            // Responsive typography & dimension configuration
            const isFullWidth = widget.width === 'full';
            const isHalfWidth = widget.width === '1/2' || !widget.width;
            const isThirdWidth = widget.width === '1/3';

            const outerFontSize = isFullWidth ? 13 : isHalfWidth ? 10 : 8;
            const maxCategoryChars = isFullWidth ? 18 : isHalfWidth ? 10 : 6;

            // Formateador de texto con truncado seguro para nombres largos asegurando siempre el valor
            const formatPieText = (entry: any) => {
                const realVal = formatMetricValue(entry.value, primaryKey);
                const rawName = String(entry.name || '');
                const truncatedName = rawName.length > maxCategoryChars ? `${rawName.substring(0, maxCategoryChars)}...` : rawName;
                const ratioPct = `${((entry.percentRatio || entry.percent || 0) * 100).toFixed(1)}%`;

                if (dataLabelContent === 'value') return realVal;
                if (dataLabelContent === 'percent_total') return ratioPct;
                if (dataLabelContent === 'both') return `${truncatedName}: ${realVal} (${ratioPct})`;
                // 'category_value' por defecto
                return `${truncatedName}: ${realVal}`;
            };

            // Custom renderer para etiquetas internas (respetando Categoría y Valor con salto de línea)
            const renderCustomizedInsideLabel = (props: any) => {
                const { cx, cy, midAngle, innerRadius, outerRadius, percent, name, value, fullName, percentRatio } = props;
                const pVal = percentRatio !== undefined ? percentRatio : percent;
                if (pVal < 0.04) return null; // No saturar rebanadas diminutas (<4%)
                const RADIAN = Math.PI / 180;
                const radius = innerRadius + (outerRadius - innerRadius) * 0.52;
                const x = cx + radius * Math.cos(-midAngle * RADIAN);
                const y = cy + radius * Math.sin(-midAngle * RADIAN);

                const realVal = formatMetricValue(value, primaryKey);
                const rawName = String(name || '');
                const truncatedName = rawName.length > maxCategoryChars ? `${rawName.substring(0, maxCategoryChars)}...` : rawName;
                const ratioPct = `${((pVal || 0) * 100).toFixed(1)}%`;
                const fullTooltip = `${fullName || rawName}: ${realVal} (${ratioPct})`;

                // Si el modo es solo valor o solo porcentaje
                if (dataLabelContent === 'value') {
                    return (
                        <text x={x} y={y} fill="#ffffff" textAnchor="middle" dominantBaseline="central" className="font-extrabold drop-shadow-sm select-none pointer-events-none" style={{ fontSize: `${outerFontSize}px` }}>
                            <title>{fullTooltip}</title>
                            {realVal}
                        </text>
                    );
                }
                if (dataLabelContent === 'percent_total') {
                    return (
                        <text x={x} y={y} fill="#ffffff" textAnchor="middle" dominantBaseline="central" className="font-extrabold drop-shadow-sm select-none pointer-events-none" style={{ fontSize: `${outerFontSize}px` }}>
                            <title>{fullTooltip}</title>
                            {ratioPct}
                        </text>
                    );
                }

                // Si es Categoría y Valor (o Todo), dibujar multilínea con salto
                const insideFontSize = isThirdWidth ? 7.5 : isHalfWidth ? 9 : 11;

                return (
                    <text
                        x={x}
                        y={y}
                        fill="#ffffff"
                        textAnchor="middle"
                        dominantBaseline="central"
                        className="font-black drop-shadow-sm select-none pointer-events-none cursor-help"
                        style={{ fontSize: `${insideFontSize}px` }}
                    >
                        <title>{fullTooltip}</title>
                        <tspan x={x} dy="-0.55em">{truncatedName}</tspan>
                        <tspan x={x} dy="1.15em" fill="#f8fafc" className="font-bold">{realVal}</tspan>
                    </text>
                );
            };

            // Custom renderer para etiquetas externas con SVG Title integrado y tipografía responsive
            const renderCustomizedOutsideLabel = (props: any) => {
                const { x, y, cx, name, value, fullName, percentRatio, percent } = props;
                const textAnchor = x > cx ? 'start' : 'end';
                const labelText = formatPieText(props);
                const realVal = formatMetricValue(value, primaryKey);
                const pVal = percentRatio !== undefined ? percentRatio : percent;
                const ratioPct = `${((pVal || 0) * 100).toFixed(1)}%`;
                const fullTooltip = `${fullName || name}: ${realVal} (${ratioPct})`;

                return (
                    <text
                        x={x}
                        y={y}
                        fill="#475569"
                        textAnchor={textAnchor}
                        dominantBaseline="central"
                        className="font-bold select-none cursor-help"
                        style={{ fontSize: `${outerFontSize}px` }}
                    >
                        <title>{fullTooltip}</title>
                        {labelText}
                    </text>
                );
            };

            // Radios según tipo específico: pie-solid = 0, donut = hueco 45%+, pie tradicional
            const isSolid = widget.type === 'pie-solid';
            const isDonut = widget.type === 'donut';

            const outerRad = isInsideLabel 
                ? (isThirdWidth ? 72 : isHalfWidth ? 82 : 92)
                : (isThirdWidth ? 44 : isHalfWidth ? 60 : 74);

            let innerRad = 0;
            if (isSolid) {
                innerRad = 0;
            } else if (isDonut) {
                innerRad = isInsideLabel 
                    ? (isThirdWidth ? 36 : isHalfWidth ? 46 : 54)
                    : (isThirdWidth ? 26 : isHalfWidth ? 36 : 46);
            } else {
                // Pie estándar
                innerRad = isInsideLabel 
                    ? (isThirdWidth ? 28 : isHalfWidth ? 34 : 40)
                    : (isThirdWidth ? 20 : isHalfWidth ? 28 : 34);
            }

            const chartMargin = isThirdWidth 
                ? (isInsideLabel ? { top: 5, right: 10, left: 10, bottom: 5 } : { top: 8, right: 26, left: 26, bottom: 8 })
                : isHalfWidth
                    ? (isInsideLabel ? { top: 8, right: 15, left: 15, bottom: 8 } : { top: 10, right: 34, left: 34, bottom: 10 })
                    : (isInsideLabel ? { top: 10, right: 20, left: 20, bottom: 10 } : { top: 12, right: 40, left: 40, bottom: 12 });

            return (
                <div className="flex-1 w-full h-full min-h-0 flex items-center justify-center relative">
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart margin={chartMargin}>
                            <Pie
                                data={pieData}
                                cx="50%"
                                cy="45%"
                                innerRadius={innerRad}
                                outerRadius={outerRad}
                                paddingAngle={isSolid ? 0 : 2}
                                dataKey="value"
                                label={showDataLabels ? (isInsideLabel ? renderCustomizedInsideLabel : renderCustomizedOutsideLabel) : false}
                                labelLine={showDataLabels && !isInsideLabel ? { strokeWidth: 1, stroke: '#94a3b8' } : false}
                                onClick={(data: any) => {
                                    if (data && data.fullName) {
                                        handleElementClick(data.fullName);
                                    }
                                }}
                                style={{ cursor: (crossFilteringActive || canDrillDown) ? 'pointer' : 'default' }}
                            >
                                {pieData.map((_, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip 
                                formatter={(val: any, _name: any, itemProps: any) => {
                                    const formattedVal = formatMetricValue(val, primaryKey);
                                    const pRatio = itemProps?.payload?.percentRatio;
                                    const pctStr = pRatio !== undefined ? ` (${(pRatio * 100).toFixed(1)}% del Total)` : '';
                                    return [`${formattedVal}${pctStr}`, primaryKey];
                                }}
                                labelFormatter={(label: any) => `Elemento: ${label}`}
                                wrapperStyle={{ fontSize: 10, zIndex: 50 }} 
                            />
                            {showLegend && (
                                <Legend 
                                    align={legendAlign} 
                                    verticalAlign={legendVerticalAlign} 
                                    layout={legendLayout} 
                                    wrapperStyle={{ fontSize: 9 }} 
                                    iconType="circle" 
                                />
                            )}
                        </PieChart>
                    </ResponsiveContainer>

                    {/* Donut Center Total Overlay */}
                    {isDonut && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none pb-4">
                            <span className="text-[8px] font-black uppercase text-slate-400 dark:text-slate-500 tracking-wider">
                                Total
                            </span>
                            <span className="text-xs font-black text-slate-700 dark:text-slate-200">
                                {formatMetricValue(totalSum, primaryKey)}
                            </span>
                        </div>
                    )}
                </div>
            );
        }

        if (widget.type === 'line') {
            const displayData = chartData.slice(0, 20);
            return (
                <div className="flex-1 w-full h-full min-h-0">
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={displayData} margin={{ top: 15, right: 10, left: -20, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                            {showXAxis && <XAxis dataKey="name" tick={{ fontSize: 8 }} stroke="#888888" />}
                            {showYAxis && <YAxis tick={{ fontSize: 8 }} stroke="#888888" />}
                            <Tooltip wrapperStyle={{ fontSize: 10 }} />
                            {showLegend && (valueKeys.length > 1 || legendPos !== 'bottom') && (
                                <Legend 
                                    align={legendAlign} 
                                    verticalAlign={legendVerticalAlign} 
                                    layout={legendLayout} 
                                    wrapperStyle={{ fontSize: 9 }} 
                                    iconSize={8} 
                                />
                            )}
                            {valueKeys.map((vKey, idx) => (
                                <Line 
                                    key={vKey}
                                    type="monotone" 
                                    dataKey={vKey} 
                                    stroke={PALETTE[idx % PALETTE.length]} 
                                    strokeWidth={2} 
                                    dot={{ r: 3 }} 
                                    label={showDataLabels ? {
                                        position: 'top',
                                        fontSize: 8,
                                        fill: '#475569',
                                        formatter: (v: any) => formatMetricValue(v, vKey)
                                    } : false}
                                    onClick={(data: any) => {
                                        if (data && (data.fullName || data.name)) {
                                            handleElementClick(data.fullName || data.name);
                                        }
                                    }}
                                    style={{ cursor: (crossFilteringActive || canDrillDown) ? 'pointer' : 'default' }}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            );
        }

        if (widget.type === 'area-stacked') {
            const displayData = chartData.slice(0, 20);
            return (
                <div className="flex-1 w-full h-full min-h-0">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={displayData} margin={{ top: 15, right: 10, left: -20, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                            {showXAxis && <XAxis dataKey="name" tick={{ fontSize: 8 }} stroke="#888888" />}
                            {showYAxis && <YAxis tick={{ fontSize: 8 }} stroke="#888888" />}
                            <Tooltip wrapperStyle={{ fontSize: 10 }} />
                            {showLegend && (valueKeys.length > 1 || legendPos !== 'bottom') && (
                                <Legend 
                                    align={legendAlign} 
                                    verticalAlign={legendVerticalAlign} 
                                    layout={legendLayout} 
                                    wrapperStyle={{ fontSize: 9 }} 
                                    iconSize={8} 
                                />
                            )}
                            {valueKeys.map((vKey, idx) => (
                                <Area 
                                    key={vKey}
                                    type="monotone" 
                                    dataKey={vKey} 
                                    stackId="1" 
                                    stroke={PALETTE[idx % PALETTE.length]} 
                                    fill={PALETTE[idx % PALETTE.length]} 
                                    fillOpacity={0.4} 
                                    label={showDataLabels ? {
                                        position: 'top',
                                        fontSize: 8,
                                        fill: '#475569',
                                        formatter: (v: any) => formatMetricValue(v, vKey)
                                    } : false}
                                    onClick={(data: any) => {
                                        if (data && (data.fullName || data.name)) {
                                            handleElementClick(data.fullName || data.name);
                                        }
                                    }}
                                    style={{ cursor: (crossFilteringActive || canDrillDown) ? 'pointer' : 'default' }}
                                />
                            ))}
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            );
        }

        if (widget.type === 'scatter') {
            const primaryKey = valueKeys[0] || 'value';
            const scatterData = chartData.slice(0, 25).map((d, idx) => ({
                x: idx + 1,
                y: typeof d[primaryKey] === 'number' ? d[primaryKey] : 0,
                z: typeof d.size === 'number' ? d.size : 100,
                name: d.fullName || d.name,
                detail: d.detail || d.name
            }));

            return (
                <div className="flex-1 w-full h-full min-h-0">
                    <ResponsiveContainer width="100%" height="100%">
                        <ScatterChart margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                            {showXAxis && <XAxis type="number" dataKey="x" name="Índice" tick={{ fontSize: 8 }} stroke="#888888" />}
                            {showYAxis && <YAxis type="number" dataKey="y" name={primaryKey} tick={{ fontSize: 8 }} stroke="#888888" />}
                            {widget.sizeField && <ZAxis type="number" dataKey="z" range={[50, 400]} name={widget.sizeField.fieldKey} />}
                            <Tooltip cursor={{ strokeDasharray: '3 3' }} wrapperStyle={{ fontSize: 10 }} />
                            <Scatter 
                                name="Registros" 
                                data={scatterData} 
                                fill={primaryColor || "#f59e0b"} 
                                onClick={(data: any) => {
                                    if (data && data.name) {
                                        handleElementClick(data.name);
                                    }
                                }}
                                style={{ cursor: (crossFilteringActive || canDrillDown) ? 'pointer' : 'default' }}
                            />
                        </ScatterChart>
                    </ResponsiveContainer>
                </div>
            );
        }

        if (widget.type === 'radar') {
            const primaryKey = valueKeys[0] || 'value';
            const radarData = chartData.slice(0, 8).map(d => ({
                subject: d.name,
                fullName: d.fullName,
                value: typeof d[primaryKey] === 'number' ? d[primaryKey] : 0
            }));

            return (
                <div className="flex-1 w-full h-full min-h-0">
                    <ResponsiveContainer width="100%" height="100%">
                        <RadarChart cx="50%" cy="50%" outerRadius="70%" data={radarData}>
                            <PolarGrid stroke="#cccccc" opacity={0.4} />
                            <PolarAngleAxis dataKey="subject" tick={{ fontSize: 7 }} />
                            <PolarRadiusAxis tick={{ fontSize: 6 }} stroke="#888888" />
                            <Radar 
                                name={primaryKey} 
                                dataKey="value" 
                                stroke={primaryColor || "#8b5cf6"} 
                                fill={primaryColor || "#8b5cf6"} 
                                fillOpacity={0.5} 
                                onClick={(data: any) => {
                                    if (data && (data.fullName || data.subject)) {
                                        handleElementClick(data.fullName || data.subject);
                                    }
                                }}
                                style={{ cursor: (crossFilteringActive || canDrillDown) ? 'pointer' : 'default' }}
                            />
                            <Tooltip wrapperStyle={{ fontSize: 10 }} />
                        </RadarChart>
                    </ResponsiveContainer>
                </div>
            );
        }

        if (widget.type === 'kpi-card') {
            const primaryField = widget.valueFields?.[0] || { fieldKey: widget.yAxisKey || cols[cols.length - 1] || 'value', aggregation: 'sum' };
            const vKey = primaryField.fieldKey;
            const agg = primaryField.aggregation || 'sum';

            const rawVals = rows.map(r => r[vKey]);
            const calculatedVal = applyFieldAggregation(rawVals, agg);

            const isCurrency = vKey.includes('$') || vKey.toLowerCase().includes('valor') || vKey.toLowerCase().includes('monto') || vKey.toLowerCase().includes('aprobado') || vKey.toLowerCase().includes('contratado');
            let displayVal = formatMetricValue(calculatedVal, vKey);

            const aggLabel = agg === 'avg' ? 'Promedio' : agg === 'count' ? 'Recuento' : agg === 'distinct_count' ? 'Recuento Dist.' : agg === 'min' ? 'Mínimo' : agg === 'max' ? 'Máximo' : 'Suma';

            return (
                <div className="flex-1 w-full h-full min-h-0 flex flex-col items-center justify-center p-3 text-center select-none">
                    <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest block mb-1">
                        {aggLabel} de {vKey}
                    </span>
                    <h2 className="text-3xl font-black text-primary tracking-tight">
                        {displayVal}
                    </h2>
                    <span className="text-[9px] text-slate-400 mt-1 block font-medium">
                        Total consolidado ({rows.length} registros)
                    </span>
                </div>
            );
        }

        if (widget.type === 'kpi-multi') {
            const primaryField = widget.valueFields?.[0] || { fieldKey: widget.yAxisKey || cols[cols.length - 1] || 'value', aggregation: 'sum' };
            const vKey = primaryField.fieldKey;
            const agg = primaryField.aggregation || 'sum';

            const rawVals = rows.map(r => r[vKey]);
            const calculatedVal = applyFieldAggregation(rawVals, agg);
            const numVal = typeof calculatedVal === 'number' ? calculatedVal : parseFloat(String(calculatedVal).replace(/[^\d.-]/g, '')) || 0;

            // Target from detail field or from style config (kpiTargetValue)
            let targetVal: number | null = null;
            if (widget.detailFields && widget.detailFields[0]) {
                const targetField = widget.detailFields[0].fieldKey;
                const rawTargetVals = rows.map(r => r[targetField]);
                const aggTarget = applyFieldAggregation(rawTargetVals, widget.detailFields[0].aggregation || 'avg');
                targetVal = typeof aggTarget === 'number' ? aggTarget : parseFloat(String(aggTarget).replace(/[^\d.-]/g, '')) || null;
            } else if (wStyle.kpiTargetValue !== undefined && wStyle.kpiTargetValue !== null) {
                targetVal = wStyle.kpiTargetValue;
            }

            let deviationPct: number | null = null;
            if (targetVal !== null && targetVal !== 0) {
                deviationPct = ((numVal - targetVal) / targetVal) * 100;
            }

            const isPositive = (deviationPct !== null && deviationPct >= 0);
            const displayVal = formatMetricValue(calculatedVal, vKey);
            const targetDisplay = targetVal !== null ? formatMetricValue(targetVal, vKey) : null;
            const aggLabel = agg === 'avg' ? 'Promedio' : agg === 'count' ? 'Recuento' : 'Total';

            const valueFontSize = wStyle.kpiValueFontSize || 30;
            const labelFontSize = wStyle.kpiLabelFontSize || 10;

            return (
                <div className="flex-1 w-full h-full min-h-0 flex flex-col items-center justify-center p-2 text-center select-none space-y-1">
                    <span 
                        style={{ fontSize: `${labelFontSize}px` }} 
                        className="font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest block truncate max-w-full"
                    >
                        {aggLabel} de {vKey}
                    </span>
                    <h2 
                        style={{ fontSize: `${valueFontSize}px`, lineHeight: 1.1 }} 
                        className="font-black text-slate-800 dark:text-white tracking-tight truncate max-w-full"
                    >
                        {displayVal}
                    </h2>
                    
                    {targetVal !== null && (
                        <div className="flex items-center gap-2 pt-0.5">
                            <span style={{ fontSize: `${Math.max(9, labelFontSize - 1)}px` }} className="font-bold text-slate-400">
                                Meta: <span className="text-slate-600 dark:text-slate-300 font-black">{targetDisplay}</span>
                            </span>
                            {deviationPct !== null && (
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-black flex items-center gap-0.5 ${
                                    isPositive 
                                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800' 
                                        : 'bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-400 border border-rose-200 dark:border-rose-800'
                                }`}>
                                    <span className="material-symbols-outlined text-[12px]">
                                        {isPositive ? 'trending_up' : 'trending_down'}
                                    </span>
                                    {isPositive ? '+' : ''}{deviationPct.toFixed(1)}%
                                </span>
                            )}
                        </div>
                    )}
                </div>
            );
        }

        if (widget.type === 'gauge') {
            const primaryField = widget.valueFields?.[0] || { fieldKey: widget.yAxisKey || cols[cols.length - 1] || 'value', aggregation: 'avg' };
            const vKey = primaryField.fieldKey;
            const agg = primaryField.aggregation || 'avg';

            const rawVals = rows.map(r => r[vKey]);
            const calculatedVal = applyFieldAggregation(rawVals, agg);
            const numVal = typeof calculatedVal === 'number' ? calculatedVal : parseFloat(String(calculatedVal).replace(/[^\d.-]/g, '')) || 0;
            const cappedPercent = Math.min(100, Math.max(0, numVal));

            const data = [
                { value: cappedPercent, color: '#3b82f6' },
                { value: 100 - cappedPercent, color: '#e2e8f0' }
            ];

            return (
                <div className="flex-1 w-full h-full min-h-0 flex flex-col items-center justify-center relative select-none">
                    <span className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest block mb-1">
                        {vKey} (Cumplimiento)
                    </span>
                    <div className="w-full h-full max-h-[140px] max-w-[180px] relative flex items-center justify-center">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={data}
                                    cx="50%"
                                    cy="60%"
                                    startAngle={180}
                                    endAngle={0}
                                    innerRadius={35}
                                    outerRadius={55}
                                    dataKey="value"
                                >
                                    {data.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="absolute inset-x-0 bottom-[15%] text-center">
                            <span className="text-xl font-black text-slate-800 dark:text-white">
                                {numVal.toFixed(1)}%
                            </span>
                        </div>
                    </div>
                </div>
            );
        }

        // 12. WIDGET: TABLA PLANA (FLAT TABLE)
        if (widget.type === 'table') {
            const tableCols: WidgetFieldConfig[] = (widget.axisFields && widget.axisFields.length > 0)
                ? widget.axisFields
                : (widget.xAxisKey ? [{ fieldKey: widget.xAxisKey, aggregation: 'none' }] : []);

            if (tableCols.length === 0) {
                return (
                    <div className="flex-1 w-full h-full flex items-center justify-center text-[10px] text-slate-400 italic">
                        Arrastra campos a "Columnas" para poblar la tabla.
                    </div>
                );
            }

            const currentSort = tableSortConfig[widget.id] || { field: tableCols[0].fieldKey, direction: 'asc' };

            // Filtrar filas según filtros lógicos del widget
            let displayRows = [...rows];
            if (widget.logicalFilters && widget.logicalFilters.length > 0) {
                displayRows = displayRows.filter(row => {
                    return widget.logicalFilters!.every(filter => {
                        const rawVal = row[filter.field];
                        const cellStr = (rawVal !== undefined && rawVal !== null) ? String(rawVal).trim() : '';
                        const cellLower = cellStr.toLowerCase();
                        if (filter.filterType === 'basic' && Array.isArray(filter.value)) {
                            return (filter.value as string[]).includes(cellStr);
                        }
                        const targetStr = String(filter.value || '').trim().toLowerCase();
                        if (filter.operator === 'equals') return cellLower === targetStr;
                        if (filter.operator === 'not_equals') return cellLower !== targetStr;
                        if (filter.operator === 'contains') return cellLower.includes(targetStr);
                        if (filter.operator === 'not_contains') return !cellLower.includes(targetStr);
                        const numVal = parseFloat(cellStr) || 0;
                        const targetNum = parseFloat(targetStr) || 0;
                        if (filter.operator === 'greater') return numVal > targetNum;
                        if (filter.operator === 'less') return numVal < targetNum;
                        if (filter.operator === 'is_null') return cellStr === '';
                        if (filter.operator === 'is_not_null') return cellStr !== '';
                        return true;
                    });
                });
            }

            // Ordenamiento interactivo por columna
            if (currentSort.field) {
                displayRows.sort((a, b) => {
                    const valA = a[currentSort.field];
                    const valB = b[currentSort.field];
                    if (valA === valB) return 0;
                    if (valA === undefined || valA === null) return 1;
                    if (valB === undefined || valB === null) return -1;
                    const numA = typeof valA === 'number' ? valA : parseFloat(String(valA).replace(/[^\d.-]/g, ''));
                    const numB = typeof valB === 'number' ? valB : parseFloat(String(valB).replace(/[^\d.-]/g, ''));
                    if (!isNaN(numA) && !isNaN(numB)) {
                        return currentSort.direction === 'asc' ? numA - numB : numB - numA;
                    }
                    const cmp = String(valA).localeCompare(String(valB));
                    return currentSort.direction === 'asc' ? cmp : -cmp;
                });
            }

            const showTotals = widget.style?.showTableTotals !== false;

            return (
                <div className="flex-1 w-full h-full min-h-0 flex flex-col overflow-hidden text-[10px] font-sans">
                    <div className="flex-1 min-h-0 overflow-auto border border-slate-200/80 dark:border-slate-700/60 rounded-xl bg-white dark:bg-slate-900/30">
                        <table className="w-full border-collapse text-left">
                            <thead className="sticky top-0 z-10 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-b border-slate-200 dark:border-slate-700 select-none">
                                <tr>
                                    {tableCols.map(col => {
                                        const isSorted = currentSort.field === col.fieldKey;
                                        return (
                                            <th 
                                                key={col.fieldKey}
                                                onClick={() => {
                                                    setTableSortConfig(prev => {
                                                        const existing = prev[widget.id];
                                                        if (existing?.field === col.fieldKey) {
                                                            return { ...prev, [widget.id]: { field: col.fieldKey, direction: existing.direction === 'asc' ? 'desc' : 'asc' } };
                                                        }
                                                        return { ...prev, [widget.id]: { field: col.fieldKey, direction: 'asc' } };
                                                    });
                                                }}
                                                className="p-2 font-black tracking-tight whitespace-nowrap cursor-pointer hover:bg-slate-200/70 dark:hover:bg-slate-750 transition-colors border-r last:border-r-0 border-slate-200 dark:border-slate-700"
                                            >
                                                <div className="flex items-center justify-between gap-1.5">
                                                    <span className="truncate">{col.fieldKey}</span>
                                                    <span className="material-symbols-outlined text-[13px] text-slate-400">
                                                        {isSorted ? (currentSort.direction === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more'}
                                                    </span>
                                                </div>
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {displayRows.map((r, rowIdx) => (
                                    <tr key={rowIdx} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                                        {tableCols.map(col => {
                                            const cellVal = r[col.fieldKey];
                                            const isNum = typeof cellVal === 'number';
                                            const isColDate = col.fieldKey.toLowerCase().includes('fecha') || col.fieldKey.toLowerCase().includes('date') || col.fieldKey.toLowerCase().includes('inicio') || col.fieldKey.toLowerCase().includes('termino');
                                            const formattedVal = isNum 
                                                ? formatMetricValue(cellVal, col.fieldKey) 
                                                : isColDate 
                                                    ? formatIfDate(cellVal) 
                                                    : String(cellVal !== undefined && cellVal !== null ? cellVal : '-');

                                            return (
                                                <td key={col.fieldKey} className={`p-2 whitespace-nowrap border-r last:border-r-0 border-slate-100 dark:border-slate-800/50 ${isNum ? 'text-right font-mono font-semibold' : isColDate ? 'text-left font-mono font-medium text-slate-700 dark:text-slate-300' : 'text-left font-medium text-slate-700 dark:text-slate-300'}`}>
                                                    {cellVal !== undefined && cellVal !== null ? formattedVal : '-'}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                            {showTotals && displayRows.length > 0 && (
                                <tfoot className="sticky bottom-0 bg-slate-100/90 dark:bg-slate-800/95 font-black border-t-2 border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-100">
                                    <tr>
                                        {tableCols.map((col, idx) => {
                                            const rawVals = displayRows.map(r => r[col.fieldKey]);
                                            const agg = col.aggregation || (rawVals.some(v => typeof v === 'number') ? 'sum' : 'count');
                                            const totalVal = applyFieldAggregation(rawVals, agg);
                                            return (
                                                <td key={col.fieldKey} className={`p-2 whitespace-nowrap border-r last:border-r-0 border-slate-200 dark:border-slate-700 ${typeof totalVal === 'number' ? 'text-right font-mono' : 'text-left'}`}>
                                                    {idx === 0 ? (
                                                        <div className="flex items-center justify-between gap-1">
                                                            <span className="text-[8px] uppercase tracking-wider text-slate-500">Total:</span>
                                                            <span>{typeof totalVal === 'number' ? formatMetricValue(totalVal, col.fieldKey) : totalVal}</span>
                                                        </div>
                                                    ) : (
                                                        typeof totalVal === 'number' ? formatMetricValue(totalVal, col.fieldKey) : (agg === 'count' ? `${totalVal} reg` : String(totalVal))
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                </div>
            );
        }

        // 13. WIDGET: MATRIZ (PIVOT TABLE / TABLA DINÁMICA JERÁRQUICA)
        if (widget.type === 'matrix') {
            const rowDims: WidgetFieldConfig[] = (widget.axisFields && widget.axisFields.length > 0)
                ? widget.axisFields
                : (widget.xAxisKey ? [{ fieldKey: widget.xAxisKey, aggregation: 'none' }] : []);
            
            const colDims: WidgetFieldConfig[] = widget.legendField ? [widget.legendField] : [];
            const valMetrics: WidgetFieldConfig[] = (widget.valueFields && widget.valueFields.length > 0)
                ? widget.valueFields
                : (widget.yAxisKey ? [{ fieldKey: widget.yAxisKey, aggregation: 'sum' }] : []);

            if (rowDims.length === 0 && valMetrics.length === 0) {
                return (
                    <div className="flex-1 w-full h-full flex items-center justify-center text-[10px] text-slate-400 italic">
                        Arrastra campos a "Filas" o "Valores" para estructurar la matriz.
                    </div>
                );
            }

            // Filtrar filas según filtros lógicos del widget
            let filteredRows = [...rows];
            if (widget.logicalFilters && widget.logicalFilters.length > 0) {
                filteredRows = filteredRows.filter(row => {
                    return widget.logicalFilters!.every(filter => {
                        const rawVal = row[filter.field];
                        const cellStr = (rawVal !== undefined && rawVal !== null) ? String(rawVal).trim() : '';
                        const cellLower = cellStr.toLowerCase();
                        if (filter.filterType === 'basic' && Array.isArray(filter.value)) {
                            return (filter.value as string[]).includes(cellStr);
                        }
                        const targetStr = String(filter.value || '').trim().toLowerCase();
                        if (filter.operator === 'equals') return cellLower === targetStr;
                        if (filter.operator === 'not_equals') return cellLower !== targetStr;
                        if (filter.operator === 'contains') return cellLower.includes(targetStr);
                        if (filter.operator === 'not_contains') return !cellLower.includes(targetStr);
                        const numVal = parseFloat(cellStr) || 0;
                        const targetNum = parseFloat(targetStr) || 0;
                        if (filter.operator === 'greater') return numVal > targetNum;
                        if (filter.operator === 'less') return numVal < targetNum;
                        if (filter.operator === 'is_null') return cellStr === '';
                        if (filter.operator === 'is_not_null') return cellStr !== '';
                        return true;
                    });
                });
            }

            // Pivot Column Headers (Valores únicos de la dimensión en Columnas)
            const pivotColKey = colDims[0]?.fieldKey;
            const pivotColValues = pivotColKey 
                ? Array.from(new Set(filteredRows.map(r => String(r[pivotColKey] !== undefined && r[pivotColKey] !== null ? r[pivotColKey] : '(En blanco)'))))
                : [];

            // Construir Árbol Jerárquico de Filas
            interface MatrixTreeNode {
                key: string;
                path: string;
                label: string;
                level: number;
                rows: any[];
                children: MatrixTreeNode[];
            }

            const buildTree = (dataRows: any[], depth: number, currentPath: string): MatrixTreeNode[] => {
                if (depth >= rowDims.length) return [];
                const currentField = rowDims[depth].fieldKey;
                const groups: Record<string, any[]> = {};
                dataRows.forEach(r => {
                    const raw = r[currentField];
                    const val = (raw !== undefined && raw !== null) ? String(raw) : '(En blanco)';
                    if (!groups[val]) groups[val] = [];
                    groups[val].push(r);
                });

                return Object.keys(groups).map(groupVal => {
                    const nodePath = currentPath ? `${currentPath} > ${groupVal}` : groupVal;
                    const nodeRows = groups[groupVal];
                    const children = buildTree(nodeRows, depth + 1, nodePath);
                    return {
                        key: nodePath,
                        path: nodePath,
                        label: groupVal,
                        level: depth,
                        rows: nodeRows,
                        children
                    };
                });
            };

            const rootNodes = rowDims.length > 0 ? buildTree(filteredRows, 0, '') : [];
            const expandedMap = matrixExpandedNodes[widget.id] || {};

            const toggleExpand = (nodeKey: string) => {
                setMatrixExpandedNodes(prev => {
                    const currentWidgetMap = prev[widget.id] || {};
                    const isExp = currentWidgetMap[nodeKey] !== false; // Default expanded if level 0
                    return {
                        ...prev,
                        [widget.id]: {
                            ...currentWidgetMap,
                            [nodeKey]: !isExp
                        }
                    };
                });
            };

            // Aplanar filas respetando la expansión
            const flatTreeRows: { node: MatrixTreeNode; isLeaf: boolean }[] = [];
            const flatten = (nodes: MatrixTreeNode[]) => {
                nodes.forEach(node => {
                    const isLeaf = node.children.length === 0;
                    flatTreeRows.push({ node, isLeaf });
                    const isExpanded = expandedMap[node.key] ?? (node.level === 0);
                    if (isExpanded && !isLeaf) {
                        flatten(node.children);
                    }
                });
            };
            flatten(rootNodes);

            const showGrandTotals = widget.style?.showMatrixGrandTotals !== false;

            return (
                <div className="flex-1 w-full h-full min-h-0 flex flex-col overflow-hidden text-[10px] font-sans">
                    <div className="flex-1 min-h-0 overflow-auto border border-slate-200/80 dark:border-slate-700/60 rounded-xl bg-white dark:bg-slate-900/30">
                        <table className="w-full border-collapse text-left">
                            <thead className="sticky top-0 z-10 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-b border-slate-200 dark:border-slate-700 select-none">
                                <tr>
                                    {/* Cabecera de Filas Jerárquicas */}
                                    <th className="p-2 font-black tracking-tight min-w-[140px] border-r border-slate-200 dark:border-slate-700">
                                        {rowDims.map(d => d.fieldKey).join(' > ') || 'Filas'}
                                    </th>

                                    {/* Cabeceras de Columnas Pivotadas y Métricas */}
                                    {pivotColKey ? (
                                        pivotColValues.map(pVal => (
                                            <th key={pVal} colSpan={Math.max(1, valMetrics.length)} className="p-2 text-center font-black tracking-tight border-r border-slate-200 dark:border-slate-700">
                                                <span>{pVal}</span>
                                                {valMetrics.length > 1 && (
                                                    <div className="flex justify-around text-[8px] font-mono text-slate-400 mt-0.5 border-t border-slate-200 dark:border-slate-700 pt-0.5">
                                                        {valMetrics.map(vm => (
                                                            <span key={vm.fieldKey} className="px-1">{vm.fieldKey}</span>
                                                        ))}
                                                    </div>
                                                )}
                                            </th>
                                        ))
                                    ) : (
                                        valMetrics.map(vm => (
                                            <th key={vm.fieldKey} className="p-2 text-right font-black tracking-tight border-r last:border-r-0 border-slate-200 dark:border-slate-700">
                                                {vm.fieldKey} ({vm.aggregation || 'sum'})
                                            </th>
                                        ))
                                    )}

                                    {/* Total General de Columna si hay pivot */}
                                    {pivotColKey && valMetrics.length > 0 && showGrandTotals && (
                                        <th className="p-2 text-right font-black tracking-tight bg-slate-200/50 dark:bg-slate-750">
                                            Total
                                        </th>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {flatTreeRows.map(({ node, isLeaf }) => {
                                    const isExpanded = expandedMap[node.key] ?? (node.level === 0);
                                    const indentPadding = node.level * 16 + 8;

                                    return (
                                        <tr key={node.key} className={`hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors ${node.level === 0 ? 'bg-slate-50/30 dark:bg-slate-900/10 font-semibold' : ''}`}>
                                            {/* Celda de Fila con Icono Expandir/Colapsar */}
                                            <td 
                                                style={{ paddingLeft: `${indentPadding}px` }} 
                                                className="p-2 border-r border-slate-100 dark:border-slate-800 text-slate-800 dark:text-slate-200 whitespace-nowrap"
                                            >
                                                <div className="flex items-center gap-1.5">
                                                    {!isLeaf ? (
                                                        <button 
                                                            type="button"
                                                            onClick={() => toggleExpand(node.key)}
                                                            className="size-4 rounded flex items-center justify-center hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500 cursor-pointer"
                                                        >
                                                            <span className="material-symbols-outlined text-[13px]">
                                                                {isExpanded ? 'expand_more' : 'chevron_right'}
                                                            </span>
                                                        </button>
                                                    ) : (
                                                        <span className="w-4 inline-block" />
                                                    )}
                                                    <span className="truncate">{node.label}</span>
                                                    {valMetrics.length === 0 && (
                                                        <span className="ml-auto text-[8px] px-1 py-0.2 rounded-full bg-primary/10 text-primary">
                                                            {node.rows.length} {node.rows.length === 1 ? 'reg' : 'regs'}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Celdas de Métricas Pivotadas o Directas */}
                                            {pivotColKey ? (
                                                <>
                                                    {pivotColValues.map(pVal => {
                                                        const matchingRows = node.rows.filter(r => String(r[pivotColKey] !== undefined && r[pivotColKey] !== null ? r[pivotColKey] : '(En blanco)') === pVal);
                                                        return valMetrics.map(vm => {
                                                            const rawVals = matchingRows.map(r => r[vm.fieldKey]);
                                                            const aggVal = rawVals.length > 0 ? applyFieldAggregation(rawVals, vm.aggregation || 'sum') : '-';
                                                            return (
                                                                <td key={`${pVal}-${vm.fieldKey}`} className="p-2 text-right font-mono border-r border-slate-100 dark:border-slate-800">
                                                                    {typeof aggVal === 'number' ? formatMetricValue(aggVal, vm.fieldKey) : aggVal}
                                                                </td>
                                                            );
                                                        });
                                                    })}
                                                    {valMetrics.length > 0 && showGrandTotals && (
                                                        <td className="p-2 text-right font-mono font-bold bg-slate-50/50 dark:bg-slate-800/30">
                                                            {(() => {
                                                                const rawVals = node.rows.map(r => r[valMetrics[0].fieldKey]);
                                                                const total = applyFieldAggregation(rawVals, valMetrics[0].aggregation || 'sum');
                                                                return typeof total === 'number' ? formatMetricValue(total, valMetrics[0].fieldKey) : total;
                                                            })()}
                                                        </td>
                                                    )}
                                                </>
                                            ) : (
                                                valMetrics.map(vm => {
                                                    const rawVals = node.rows.map(r => r[vm.fieldKey]);
                                                    const aggVal = applyFieldAggregation(rawVals, vm.aggregation || 'sum');
                                                    return (
                                                        <td key={vm.fieldKey} className="p-2 text-right font-mono border-r last:border-r-0 border-slate-100 dark:border-slate-800">
                                                            {typeof aggVal === 'number' ? formatMetricValue(aggVal, vm.fieldKey) : aggVal}
                                                        </td>
                                                    );
                                                })
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                            {showGrandTotals && valMetrics.length > 0 && (
                                <tfoot className="sticky bottom-0 bg-slate-100/90 dark:bg-slate-800/95 font-black border-t-2 border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-100">
                                    <tr>
                                        <td className="p-2 text-left border-r border-slate-200 dark:border-slate-700 uppercase tracking-wider text-[9px]">
                                            Total General
                                        </td>
                                        {pivotColKey ? (
                                            <>
                                                {pivotColValues.map(pVal => {
                                                    const matchingRows = filteredRows.filter(r => String(r[pivotColKey] !== undefined && r[pivotColKey] !== null ? r[pivotColKey] : '(En blanco)') === pVal);
                                                    return valMetrics.map(vm => {
                                                        const rawVals = matchingRows.map(r => r[vm.fieldKey]);
                                                        const totalVal = rawVals.length > 0 ? applyFieldAggregation(rawVals, vm.aggregation || 'sum') : '-';
                                                        return (
                                                            <td key={`total-${pVal}-${vm.fieldKey}`} className="p-2 text-right font-mono border-r border-slate-200 dark:border-slate-700">
                                                                {typeof totalVal === 'number' ? formatMetricValue(totalVal, vm.fieldKey) : totalVal}
                                                            </td>
                                                        );
                                                    });
                                                })}
                                                <td className="p-2 text-right font-mono bg-slate-200/50 dark:bg-slate-750 font-black">
                                                    {(() => {
                                                        const rawVals = filteredRows.map(r => r[valMetrics[0].fieldKey]);
                                                        const grand = applyFieldAggregation(rawVals, valMetrics[0].aggregation || 'sum');
                                                        return typeof grand === 'number' ? formatMetricValue(grand, valMetrics[0].fieldKey) : grand;
                                                    })()}
                                                </td>
                                            </>
                                        ) : (
                                            valMetrics.map(vm => {
                                                const rawVals = filteredRows.map(r => r[vm.fieldKey]);
                                                const totalVal = applyFieldAggregation(rawVals, vm.aggregation || 'sum');
                                                return (
                                                    <td key={`total-${vm.fieldKey}`} className="p-2 text-right font-mono border-r last:border-r-0 border-slate-200 dark:border-slate-700">
                                                        {typeof totalVal === 'number' ? formatMetricValue(totalVal, vm.fieldKey) : totalVal}
                                                    </td>
                                                );
                                            })
                                        )}
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                </div>
            );
        }

        // 14. WIDGET: IMAGEN (FOTOS DE OBRA Y CARGA LOCAL)
        if (widget.type === 'image') {
            const imgUrl = widget.style?.imageUrl;
            const imgFit = widget.style?.imageFit || 'cover';
            const caption = widget.style?.imageCaption;

            if (!imgUrl) {
                return (
                    <div 
                        onClick={() => {
                            setTargetImageWidgetId(widget.id);
                            setShowImagePickerModal(true);
                        }}
                        className="flex-1 w-full h-full min-h-0 flex flex-col items-center justify-center p-4 border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-2xl bg-slate-50/50 dark:bg-slate-900/30 text-center cursor-pointer hover:border-primary transition-all group"
                    >
                        <div className="size-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-2 group-hover:scale-110 transition-transform shadow-xs">
                            <span className="material-symbols-outlined text-[22px]">add_photo_alternate</span>
                        </div>
                        <span className="text-xs font-black text-slate-700 dark:text-slate-200 block mb-0.5">
                            Cargar o Seleccionar Imagen
                        </span>
                        <p className="text-[9px] text-slate-400 max-w-xs leading-relaxed">
                            Haz clic para subir una foto desde tu equipo o elegir un registro fotográfico.
                        </p>
                    </div>
                );
            }

            return (
                <div className="flex-1 w-full h-full min-h-0 flex flex-col items-center justify-center overflow-hidden rounded-xl relative group">
                    <img 
                        src={imgUrl} 
                        alt={caption || 'Registro de obra'} 
                        className={`w-full h-full object-${imgFit} rounded-xl transition-transform duration-300 group-hover:scale-[1.01]`}
                    />
                    {caption && (
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-2 pt-4 text-white text-[10px] font-semibold rounded-b-xl">
                            {caption}
                        </div>
                    )}
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setTargetImageWidgetId(widget.id);
                            setShowImagePickerModal(true);
                        }}
                        className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white p-1.5 rounded-lg text-xs opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 shadow-md"
                        title="Cambiar imagen"
                    >
                        <span className="material-symbols-outlined text-[14px]">edit</span>
                        <span className="text-[9px] font-bold">Cambiar</span>
                    </button>
                </div>
            );
        }

        // 15. WIDGET: FRANJA / BANNER DE ENCABEZADO
        if (widget.type === 'header-banner') {
            const style = widget.style || {};
            const bTitle = style.bannerTitle || reportTitle || 'INFORME DE AVANCE Y GESTIÓN OPERATIVA';
            const bSubtitle = style.bannerSubtitle || 'Control Semanal de Producción y Calidad';
            const bProject = style.bannerProjectName || activeProjectId || 'Proyecto K-LyDApp';
            const bDate = style.bannerDate || new Date().toLocaleDateString('es-CL', { year: 'numeric', month: 'long', day: 'numeric' });
            const bEvaluator = style.bannerEvaluator || currentUser?.name || 'Administración de Obra';
            const logo = style.bannerLogoUrl;

            return (
                <div className={`w-full p-4 md:p-5 rounded-2xl flex flex-wrap items-center justify-between gap-4 select-none ${style.bannerGradient !== false ? 'bg-gradient-to-r from-slate-900 via-primary/95 to-slate-900 text-white shadow-lg' : 'bg-slate-900 text-white'}`}>
                    <div className="flex items-center gap-3.5">
                        {logo ? (
                            <img src={logo} alt="Logo" className="h-10 w-auto max-w-[120px] object-contain rounded bg-white/10 p-1" />
                        ) : (
                            <div className="size-11 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center font-black text-lg text-white shadow-xs">
                                <span className="material-symbols-outlined text-[24px]">corporate_fare</span>
                            </div>
                        )}
                        <div>
                            <h2 className="text-sm md:text-base font-black tracking-tight uppercase">{bTitle}</h2>
                            <p className="text-[10px] text-slate-300 font-medium">{bSubtitle}</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-4 text-right">
                        <div className="border-r border-white/20 pr-4">
                            <span className="text-[9px] text-slate-300 font-bold uppercase tracking-wider block">Proyecto</span>
                            <span className="text-xs font-black text-white">{bProject}</span>
                        </div>
                        <div className="border-r border-white/20 pr-4">
                            <span className="text-[9px] text-slate-300 font-bold uppercase tracking-wider block">Fecha</span>
                            <span className="text-xs font-bold text-slate-100">{bDate}</span>
                        </div>
                        <div>
                            <span className="text-[9px] text-slate-300 font-bold uppercase tracking-wider block">Evaluador</span>
                            <span className="text-xs font-bold text-slate-100">{bEvaluator}</span>
                        </div>
                    </div>
                </div>
            );
        }

        // 16. WIDGET: TARJETA CONTENEDORA (BACKGROUND CARD / GROUPER)
        if (widget.type === 'container-card') {
            const style = widget.style || {};
            const shadowClass = style.containerShadow === 'xl' ? 'shadow-xl' : style.containerShadow === 'lg' ? 'shadow-lg' : style.containerShadow === 'none' ? '' : 'shadow-md';
            return (
                <div className={`w-full min-h-[140px] p-4 rounded-2xl flex flex-col justify-start border border-slate-200/80 dark:border-slate-800 ${shadowClass}`} style={{ backgroundColor: style.backgroundColor || 'rgba(255,255,255,0.7)', opacity: style.containerOpacity !== undefined ? style.containerOpacity : 1 }}>
                    <div className="flex items-center gap-1.5 text-slate-400 dark:text-slate-500 mb-2">
                        <span className="material-symbols-outlined text-[16px]">select_all</span>
                        <span className="text-[9px] font-black uppercase tracking-wider">{style.customTitle || 'Contenedor / Grupo Visual'}</span>
                    </div>
                    <div className="flex-1 border-2 border-dashed border-slate-200/70 dark:border-slate-700/60 rounded-xl flex items-center justify-center p-4 text-center">
                        <span className="text-[10px] text-slate-400 italic">Agrupa tus gráficos y filtros dentro de este contenedor visual</span>
                    </div>
                </div>
            );
        }

        // 17. WIDGET: CAJA DE TEXTO LIBRE
        if (widget.type === 'text-box') {
            const style = widget.style || {};
            return (
                <div className="w-full p-4 rounded-xl select-none" style={{ textAlign: style.textAlign || 'left' }}>
                    <p 
                        style={{ 
                            fontSize: `${style.textFontSize || 12}px`, 
                            color: style.textColor || undefined,
                            fontWeight: style.textBold ? 'bold' : 'normal',
                            fontStyle: style.textItalic ? 'italic' : 'normal'
                        }} 
                        className="text-slate-700 dark:text-slate-200 leading-relaxed whitespace-pre-wrap"
                    >
                        {style.textContent || 'Escribe aquí observaciones, conclusiones ejecutivas o notas del reporte...'}
                    </p>
                </div>
            );
        }

        return null;
    };

    // --- ACCIONES POWER BI PARA WIDGETS Y BUCKETS REACTIVOS ---
    const handleInsertEmptyWidget = (visType: WidgetConfig['type']) => {
        let defaultColSpan = 8;
        let defaultRowSpan = 4;

        if (visType === 'header-banner') {
            defaultColSpan = 16;
            defaultRowSpan = 1;
        } else if (visType === 'kpi-card' || visType === 'gauge') {
            defaultColSpan = 4;
            defaultRowSpan = 2;
        } else if (visType === 'kpi-multi') {
            defaultColSpan = 4;
            defaultRowSpan = 3;
        } else if (visType === 'slicer') {
            defaultColSpan = 4;
            defaultRowSpan = 3;
        } else if (visType === 'image') {
            defaultColSpan = 8;
            defaultRowSpan = 4;
        } else if (visType === 'container-card') {
            defaultColSpan = 16;
            defaultRowSpan = 3;
        } else if (visType === 'text-box') {
            defaultColSpan = 8;
            defaultRowSpan = 2;
        }

        const newWidget: WidgetConfig = {
            id: `widget-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
            pageId: activePageId,
            type: visType,
            componentId: activeEditingComponent,
            colSpan: defaultColSpan,
            rowSpan: defaultRowSpan,
            gridColStart: 1,
            gridRowStart: 1,
            minHeightPx: 120,
            axisFields: [],
            valueFields: [],
            legendField: undefined,
            detailFields: [],
            sizeField: undefined,
            logicalFilters: [],
            style: {
                showBackground: visType !== 'text-box',
                showBorder: visType !== 'header-banner',
                borderRadius: 16,
                ...(visType === 'slicer' ? { slicerVisualMode: 'dropdown' as const } : {})
            }
        };

        setWidgets(prev => [...prev, newWidget]);
        setSelectedWidgetId(newWidget.id);
        setSelectedVisualType(visType);
    };

    // --- GENERADOR DE PLANTILLAS PREDISEÑADAS (SLIDE PRESETS) DESDE EL MAESTRO DE REPORTES ---
    const handleApplySlidePreset = (template: MasterReportTemplate) => {
        const pageId = activePageId;
        const now = Date.now();

        if (!template.widgets || template.widgets.length === 0) {
            setWidgets(prev => prev.filter(w => (w.pageId || 'page-1') !== pageId));
            setShowPresetModal(false);
            return;
        }

        // Clonar widgets con IDs únicos asignados a la página activa
        const clonedWidgets: WidgetConfig[] = template.widgets.map((w, idx) => ({
            ...JSON.parse(JSON.stringify(w)),
            id: `widget-${now}-${idx + 1}`,
            pageId
        }));

        // Reemplazar widgets de la página activa
        setWidgets(prev => [
            ...prev.filter(w => (w.pageId || 'page-1') !== pageId),
            ...clonedWidgets
        ]);
        if (template.themeId) {
            setActiveThemeId(template.themeId);
        }
        if (!reportTitle.trim() && template.name) {
            setReportTitle(template.name);
        }
        if (!reportDescription.trim() && template.description) {
            setReportDescription(template.description);
        }
        if (template.category) {
            setReportCategory(template.category);
        }
        if (template.badge) {
            setReportBadge(template.badge);
        }
        setShowPresetModal(false);
    };

    const handleDropFieldToBucket = (
        widgetId: string,
        bucketType: 'axis' | 'value' | 'legend' | 'detail' | 'size',
        fieldKey: string,
        sourceComponentId?: string
    ) => {
        if (sourceComponentId) {
            setActiveEditingComponent(sourceComponentId);
        }
        setWidgets(prev => prev.map(w => {
            if (w.id !== widgetId) return w;

            const targetComponentId = sourceComponentId || w.componentId || activeEditingComponent;
            const isPct = fieldKey.includes('%') || fieldKey.toLowerCase().includes('ppc') || fieldKey.toLowerCase().includes('avance') || fieldKey.toLowerCase().includes('cumplimiento');
            const defaultAgg: WidgetFieldConfig['aggregation'] = isPct ? 'avg' : 'sum';

            let updatedWidget = { ...w, componentId: targetComponentId };

            if (bucketType === 'axis') {
                const current = w.axisFields || [];
                if (current.some(f => f.fieldKey === fieldKey)) return updatedWidget;
                return { ...updatedWidget, axisFields: [...current, { fieldKey, aggregation: 'none' }] };
            }
            if (bucketType === 'value') {
                const current = w.valueFields || [];
                if (current.some(f => f.fieldKey === fieldKey)) return updatedWidget;
                return { ...updatedWidget, valueFields: [...current, { fieldKey, aggregation: defaultAgg }] };
            }
            if (bucketType === 'legend') {
                return { ...updatedWidget, legendField: { fieldKey, aggregation: 'none' } };
            }
            if (bucketType === 'detail') {
                const current = w.detailFields || [];
                if (current.some(f => f.fieldKey === fieldKey)) return updatedWidget;
                return { ...updatedWidget, detailFields: [...current, { fieldKey, aggregation: 'none' }] };
            }
            if (bucketType === 'size') {
                return { ...updatedWidget, sizeField: { fieldKey, aggregation: defaultAgg } };
            }
            return updatedWidget;
        }));
    };

    const handleRemoveFieldFromBucket = (
        widgetId: string,
        bucketType: 'axis' | 'value' | 'legend' | 'detail' | 'size',
        fieldIndex?: number
    ) => {
        setWidgets(prev => prev.map(w => {
            if (w.id !== widgetId) return w;

            if (bucketType === 'axis') {
                const current = w.axisFields || [];
                return { ...w, axisFields: current.filter((_, idx) => idx !== fieldIndex) };
            }
            if (bucketType === 'value') {
                const current = w.valueFields || [];
                return { ...w, valueFields: current.filter((_, idx) => idx !== fieldIndex) };
            }
            if (bucketType === 'legend') {
                return { ...w, legendField: undefined };
            }
            if (bucketType === 'detail') {
                const current = w.detailFields || [];
                return { ...w, detailFields: current.filter((_, idx) => idx !== fieldIndex) };
            }
            if (bucketType === 'size') {
                return { ...w, sizeField: undefined };
            }
            return w;
        }));
    };


    const handleUpdateFieldAggregation = (
        widgetId: string,
        bucketType: 'axis' | 'value' | 'legend' | 'detail' | 'size',
        fieldIndex: number,
        newAgg: WidgetFieldConfig['aggregation']
    ) => {
        setWidgets(prev => prev.map(w => {
            if (w.id !== widgetId) return w;

            if (bucketType === 'value') {
                const current = [...(w.valueFields || [])];
                if (current[fieldIndex]) {
                    current[fieldIndex] = { ...current[fieldIndex], aggregation: newAgg };
                }
                return { ...w, valueFields: current };
            }
            if (bucketType === 'axis') {
                const current = [...(w.axisFields || [])];
                if (current[fieldIndex]) {
                    current[fieldIndex] = { ...current[fieldIndex], aggregation: newAgg };
                }
                return { ...w, axisFields: current };
            }
            if (bucketType === 'size' && w.sizeField) {
                return { ...w, sizeField: { ...w.sizeField, aggregation: newAgg } };
            }
            return w;
        }));
    };

    // --- RENDERIZADOR DE TABLA SPREADSHEET (EXCEL INDEPENDIENTE) ---
    const renderExcelSpreadsheet = (rows: any[], columns: string[]) => {
        if (rows.length === 0) {
            return (
                <div className="p-8 text-center text-xs text-slate-450 dark:text-slate-500 italic bg-slate-50/50 dark:bg-slate-900/10 rounded-2xl border border-dashed border-slate-200 dark:border-slate-700 py-12">
                    No hay registros de datos para mostrar.
                </div>
            );
        }

        return (
            <div className="w-full overflow-x-auto rounded-2xl border border-slate-200/60 dark:border-slate-700/60 bg-white/50 dark:bg-slate-900/40 shadow-sm max-h-[400px] overflow-y-auto">
                <table className="w-full border-collapse text-left text-xs">
                    <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800 border-b border-slate-200/60 dark:border-slate-700/60 z-10">
                        <tr>
                            {columns.map((col, idx) => (
                                <th key={idx} className="px-4 py-3 font-extrabold uppercase tracking-wider text-[10px] text-slate-400 dark:text-slate-500">
                                    {col}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50 text-slate-700 dark:text-slate-200">
                        {rows.map((row, rowIdx) => (
                            <tr key={rowIdx} className="hover:bg-slate-50/40 dark:hover:bg-slate-800/20 transition-colors">
                                {columns.map((col, colIdx) => {
                                    const val = row[col];
                                    
                                    // Evaluar formato condicional para esta celda
                                    let cellStyle = "";
                                    const matchingRule = cfRules.find(rule => {
                                        if (rule.column !== col) return false;
                                        const cellValStr = String(val || '').toLowerCase();
                                        const ruleValStr = String(rule.value).toLowerCase();

                                        if (rule.operator === 'less') {
                                            const cellNum = parseFloat(cellValStr.replace(/[^\d.-]/g, ''));
                                            const ruleNum = parseFloat(ruleValStr);
                                            return !isNaN(cellNum) && !isNaN(ruleNum) && cellNum < ruleNum;
                                        }
                                        if (rule.operator === 'greater') {
                                            const cellNum = parseFloat(cellValStr.replace(/[^\d.-]/g, ''));
                                            const ruleNum = parseFloat(ruleValStr);
                                            return !isNaN(cellNum) && !isNaN(ruleNum) && cellNum > ruleNum;
                                        }
                                        if (rule.operator === 'equal') {
                                            return cellValStr === ruleValStr;
                                        }
                                        if (rule.operator === 'contains') {
                                            return cellValStr.includes(ruleValStr);
                                        }
                                        if (rule.operator === 'between_dates') {
                                            const parts = ruleValStr.split(',');
                                            if (parts.length === 2) {
                                                const dateCell = new Date(cellValStr);
                                                const dateMin = new Date(parts[0].trim());
                                                const dateMax = new Date(parts[1].trim());
                                                return !isNaN(dateCell.getTime()) && !isNaN(dateMin.getTime()) && !isNaN(dateMax.getTime()) && dateCell >= dateMin && dateCell <= dateMax;
                                            }
                                        }
                                        if (rule.operator === 'between_numbers') {
                                            const parts = ruleValStr.split(',');
                                            if (parts.length === 2) {
                                                const cellNum = parseFloat(cellValStr.replace(/[^\d.-]/g, ''));
                                                const ruleMin = parseFloat(parts[0].trim());
                                                const ruleMax = parseFloat(parts[1].trim());
                                                return !isNaN(cellNum) && !isNaN(ruleMin) && !isNaN(ruleMax) && cellNum >= ruleMin && cellNum <= ruleMax;
                                            }
                                        }
                                        return false;
                                    });

                                    if (matchingRule) {
                                        if (matchingRule.color === 'red') cellStyle = "bg-red-100 dark:bg-red-955/40 text-red-800 dark:text-red-300 font-bold";
                                        if (matchingRule.color === 'green') cellStyle = "bg-green-100 dark:bg-green-955/40 text-green-800 dark:text-green-300 font-bold";
                                        if (matchingRule.color === 'blue') cellStyle = "bg-blue-100 dark:bg-blue-955/40 text-blue-800 dark:text-blue-300 font-bold";
                                        if (matchingRule.color === 'yellow') cellStyle = "bg-yellow-100 dark:bg-yellow-955/40 text-yellow-800 dark:text-yellow-300 font-bold";
                                    }

                                    const displayVal = val !== undefined ? String(val) : '-';
                                    return (
                                        <td 
                                            key={colIdx} 
                                            title={displayVal !== '-' ? displayVal : undefined}
                                            className={`px-4 py-2.5 whitespace-nowrap max-w-[240px] truncate transition-colors cursor-default ${cellStyle}`}
                                        >
                                            {displayVal}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const hasAccess = isAdmin || canView || canDesign;
    if (!hasAccess) {
        return (
            <div className="flex flex-col items-center justify-center h-full p-8 text-center bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm min-h-[500px]">
                <div className="size-16 rounded-2xl bg-red-500/10 text-red-500 border border-red-500/20 flex items-center justify-center mx-auto mb-4">
                    <span className="material-symbols-outlined text-[36px]">security</span>
                </div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Acceso Denegado</h3>
                <p className="text-xs text-slate-600 dark:text-slate-400 max-w-sm mx-auto">
                    No cuenta con los permisos necesarios para visualizar o diseñar reportes. Contacte a un administrador.
                </p>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full w-full bg-slate-50/90 dark:bg-slate-900/90 rounded-2xl overflow-hidden shadow-sm transition-all glow-blue">
            {config.isMocked && (
                <style dangerouslySetInnerHTML={{ __html: `
                    @media print {
                        body * {
                            visibility: hidden;
                        }
                        #print-local-report-content, #print-local-report-content * {
                            visibility: visible;
                        }
                        #print-local-report-content {
                            position: absolute;
                            left: 0;
                            top: 0;
                            width: 100% !important;
                            height: auto !important;
                            background: white !important;
                            color: black !important;
                        }
                        .recharts-responsive-container {
                            width: 100% !important;
                            height: 320px !important;
                            display: block !important;
                        }
                        .recharts-wrapper {
                            width: 100% !important;
                            height: 320px !important;
                        }
                        svg {
                            width: 100% !important;
                            height: 100% !important;
                            display: block !important;
                        }
                        .no-print {
                            display: none !important;
                        }
                    }
                ` }} />
            )}

            {/* Header Control Bar */}
            {!isSlideEmbed && (
                <div className="flex flex-wrap items-center justify-between gap-4 px-5 py-2.5 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 no-print shrink-0">
                    <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 text-[9px] font-black tracking-wider uppercase rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400 border border-emerald-200/60 dark:border-emerald-800/60">
                            Capa SQL bi Activa
                        </span>
                        {config.isMocked && (
                            <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400">
                                Reporte local interactivo
                            </span>
                        )}
                        {isMasterCorporateDesigner && (
                            <span 
                                className="flex items-center gap-1 px-2.5 py-0.5 text-[10px] font-bold rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/20 shadow-xs"
                                title="Datos referenciales utilizados únicamente para previsualizar los gráficos durante el diseño. Al usar la plantilla en cualquier obra, los gráficos se generarán exclusivamente con los datos de esa obra."
                            >
                                <span className="material-symbols-outlined text-[13px]">visibility</span>
                                Datos referenciales: {referentialProject?.name || '318 - 94 Viviendas Las Quilas'} (Solo previsualización)
                            </span>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        {/* Botón Cancelar y Guardar en Modo Edición */}
                        {mode === 'edit' && (
                            <div className="flex items-center gap-2 mr-2">
                                {onCancel && (
                                    <button
                                        type="button"
                                        onClick={() => setShowCancelWarningModal(true)}
                                        className="px-3 py-1.5 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-750 text-slate-600 dark:text-slate-300 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all flex items-center gap-1 cursor-pointer"
                                    >
                                        <span className="material-symbols-outlined text-[14px]">cancel</span>
                                        Cancelar
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => setShowSaveConfirmModal(true)}
                                    disabled={savingLocal}
                                    className="px-3.5 py-1.5 bg-primary hover:bg-primary/95 text-white rounded-xl text-[10px] font-black uppercase tracking-wider transition-all flex items-center gap-1.5 shadow-sm shadow-primary/20 disabled:opacity-50 cursor-pointer"
                                >
                                    <span className="material-symbols-outlined text-[14px]">save</span>
                                    {savingLocal ? 'Guardando...' : 'Guardar Reporte'}
                                </button>
                            </div>
                        )}

                        {/* Botón Plantillas Prediseñadas */}
                        {mode === 'edit' && (
                            <button
                                type="button"
                                onClick={() => setShowPresetModal(true)}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 hover:bg-primary/20 border border-primary/30 rounded-lg text-[10px] font-black uppercase tracking-wider text-primary transition-all cursor-pointer mr-1"
                            >
                                <span className="material-symbols-outlined text-[14px]">auto_stories</span>
                                Plantillas Diapositiva
                            </button>
                        )}

                        {/* Botón Modo Presentación (Full Screen) */}
                        <button
                            type="button"
                            onClick={() => setIsPresentationMode(true)}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 hover:bg-black text-white dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all shadow-sm cursor-pointer mr-1"
                            title="Proyectar reporte en pantalla completa (16:9)"
                        >
                            <span className="material-symbols-outlined text-[14px]">slideshow</span>
                            Presentar
                        </button>

                        <button
                            onClick={() => setShowDiagPanel(!showDiagPanel)}
                            className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-extrabold uppercase tracking-wider border transition-all ${showDiagPanel
                                    ? 'bg-primary text-white border-primary shadow-md'
                                    : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700'
                                }`}
                            title="Ver diagnóstico de conexión y variables"
                        >
                            <span className="material-symbols-outlined text-[14px]">settings_ethernet</span>
                            Diagnóstico BD
                        </button>
                    </div>
                </div>
            )}

            {/* Diagnostic Drawer */}
            {showDiagPanel && (
                <div className="bg-slate-100 dark:bg-slate-950/80 border-b border-slate-200 dark:border-slate-800 p-5 space-y-4 animate-fade-in no-print">
                    {config.status && (
                        <div className="px-4 py-2.5 rounded-xl text-xs bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-900 flex items-center gap-2">
                            <span className="material-symbols-outlined text-[18px]">info</span>
                            <span><strong>Estado de la API:</strong> {config.status}</span>
                        </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                        <div className="bg-white dark:bg-slate-900 p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
                            <span className="font-bold text-slate-700 dark:text-slate-300 block mb-1">Esquema Analítico SQL</span>
                            <p className="text-emerald-600 dark:text-emerald-400 font-mono font-medium">{status.sqlSchemaStatus}</p>
                            <p className="text-[11px] text-slate-500 mt-1">7 vistas analíticas + Rol NOLOGIN bi_reader</p>
                        </div>
                        <div className="bg-white dark:bg-slate-900 p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
                            <span className="font-bold text-slate-700 dark:text-slate-300 block mb-1">Métricas PPC Reconciliadas</span>
                            <p className="text-primary font-bold">PPC_VALID: {ppcMetrics.ppcValid.toFixed(2)}%</p>
                            <p className="text-slate-500 dark:text-slate-400">PPC_GROSS: {ppcMetrics.ppcGross.toFixed(2)}%</p>
                        </div>
                        <div className="bg-white dark:bg-slate-900 p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
                            <span className="font-bold text-slate-700 dark:text-slate-300 block mb-1">Parámetros Reporte</span>
                            <p className="text-slate-600 dark:text-slate-400 font-mono text-[11px]">
                                ID: {config.reportId || reportId || 'No cargado'}
                            </p>
                            <p className="text-slate-600 dark:text-slate-400 font-mono text-[11px]">
                                Tipo de Render: Renderizador Local (HTML5/Canvas)
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Main Render Canvas */}
            <div className="flex-1 relative min-h-[600px] bg-slate-100 dark:bg-slate-950 flex flex-col">
                {loading ? (
                    <div className="flex flex-col items-center justify-center flex-1 gap-3 p-8">
                        <div className="size-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                        <p className="text-xs font-medium text-slate-600 dark:text-slate-400">
                            Cargando componentes analíticos...
                        </p>
                    </div>
                ) : error ? (
                    <div className="max-w-xl text-center p-8 space-y-5 animate-fade-in mx-auto mt-16">
                        <div className="size-16 rounded-2xl bg-red-500/10 text-red-500 border border-red-500/20 flex items-center justify-center mx-auto shadow-inner">
                            <span className="material-symbols-outlined text-[36px]">report_problem</span>
                        </div>
                        <div className="space-y-2">
                            <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                                Error de Conexión Analítica
                            </h3>
                            <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed max-w-sm mx-auto">
                                {error.message}
                            </p>
                        </div>

                        <div className="bg-slate-900 text-slate-200 text-left p-4 rounded-xl font-mono text-[11px] space-y-1 shadow-inner border border-slate-800 max-w-sm mx-auto">
                            <p><span className="text-red-400">Código:</span> {error.code}</p>
                            {error.requestId && <p><span className="text-amber-400">Solicitud ID:</span> {error.requestId}</p>}
                        </div>

                        <button
                            type="button"
                            onClick={() => {
                                setError(null);
                                setConfig({
                                    reportId: reportId || 'local-report-id',
                                    accessToken: 'local-analytics-token',
                                    embedUrl: '',
                                    tokenType: 'Embed',
                                    permissions: 'ReadWrite',
                                    status: 'OK',
                                    isMocked: true
                                });
                            }}
                            className="mt-4 px-4 py-2.5 bg-primary text-white text-xs font-bold rounded-xl shadow-md hover:bg-primary/90 transition-all flex items-center justify-center gap-2 mx-auto cursor-pointer"
                        >
                            <span className="material-symbols-outlined text-[16px]">analytics</span>
                            Reintentar Conexión
                        </button>
                    </div>
                ) : showPermissionError ? (
                    <div className="max-w-xl text-center p-8 space-y-4 animate-fade-in mx-auto mt-16">
                        <div className="size-16 rounded-2xl bg-red-500/10 text-red-500 border border-red-500/20 flex items-center justify-center mx-auto shadow-inner">
                            <span className="material-symbols-outlined text-[36px]">security</span>
                        </div>
                        <div className="space-y-2">
                            <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                                Modo Edición No Autorizado por el Servidor
                            </h3>
                            <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                                La sesión no cuenta con privilegios de edición.
                            </p>
                        </div>
                    </div>
                ) : config.isMocked ? (
                    // ==========================================
                    // POWER BI DESIGNER CANVAS & COLLAPSIBLE PANELS (SLIDE 16:9)
                    // ==========================================
                    <div className="flex flex-1 overflow-hidden h-full relative">
                        {/* 1. LIENZO CENTRAL EXPANDIBLE (CANVAS 16:9 + HOJA DE DATOS) */}
                        <div 
                            onClick={() => {
                                setSelectedWidgetId(null);
                            }}
                            className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6 h-full flex flex-col items-center bg-slate-200/70 dark:bg-slate-950 transition-all duration-300"
                        >
                            {/* Diapositiva 16:9 con Tematización Activa */}
                            {(() => {
                                const currentTheme = SLIDE_THEMES.find(t => t.id === activeThemeId) || SLIDE_THEMES[0];
                                const pageWidgets = widgets.filter(w => (w.pageId || 'page-1') === activePageId);

                                return (
                                    <div 
                                        style={{
                                            backgroundColor: currentTheme.bgSlideColor,
                                            borderColor: currentTheme.borderColor
                                        }}
                                        className="w-full max-w-[1480px] h-[780px] md:h-[820px] rounded-3xl border-2 shadow-2xl p-6 flex flex-col justify-between gap-4 relative transition-all duration-300 overflow-hidden shrink-0"
                                    >
                                        {/* Barra de cabecera de la diapositiva */}
                                        <div className="flex items-center justify-between border-b pb-3 border-slate-200/80 dark:border-slate-800 shrink-0">
                                            <div className="flex items-center gap-2">
                                                <span className="material-symbols-outlined text-[18px] text-primary">slideshow</span>
                                                <h4 className="text-xs font-black uppercase tracking-wider" style={{ color: currentTheme.textColor }}>
                                                    {reportPages.find(p => p.id === activePageId)?.name || 'Diapositiva Activa'}
                                                </h4>
                                                <span className="text-[9px] bg-primary/10 text-primary px-2.5 py-0.5 rounded-full font-black">
                                                    Proporción 16:9 • 16×9 Grilla • {pageWidgets.length} {pageWidgets.length === 1 ? 'Elemento' : 'Elementos'}
                                                </span>
                                                {isMasterCorporateDesigner && (
                                                    <span 
                                                        className="text-[9px] bg-amber-500/15 text-amber-800 dark:text-amber-300 border border-amber-500/30 px-2 py-0.5 rounded-full font-bold flex items-center gap-1"
                                                        title="Los datos en pantalla provienen de la obra referencial para diseñar los gráficos. Cada proyecto usará sus propios datos."
                                                    >
                                                        <span className="material-symbols-outlined text-[11px]">preview</span>
                                                        Previsualización: {referentialProject?.name || '318 - 94 Viviendas Las Quilas'}
                                                    </span>
                                                )}
                                            </div>

                                            <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                                                {crossFilter && (
                                                    <div className="flex items-center gap-1.5 bg-blue-500/10 text-primary border border-blue-500/20 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider animate-pulse">
                                                        <span>Filtro: {crossFilter.key} = {crossFilter.value}</span>
                                                        <button 
                                                            onClick={() => setCrossFilter(null)}
                                                            className="material-symbols-outlined text-[12px] font-black hover:text-red-500 transition-colors cursor-pointer"
                                                            title="Limpiar Filtro"
                                                        >
                                                            close
                                                        </button>
                                                    </div>
                                                )}
                                                {/* Botones de alternar paneles (solo en modo edición) */}
                                                {mode === 'edit' && (
                                                    <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-900 p-1 rounded-xl border border-slate-200 dark:border-slate-700 text-[10px] font-bold text-slate-600 dark:text-slate-400">
                                                        <button 
                                                            onClick={() => setRightPanelVisOpen(prev => !prev)}
                                                            className={`px-2 py-1 rounded-lg transition-colors flex items-center gap-1 ${rightPanelVisOpen ? 'bg-white dark:bg-slate-800 text-primary shadow-xs' : 'hover:text-slate-900 dark:hover:text-white'}`}
                                                        >
                                                            <span className="material-symbols-outlined text-[14px]">pie_chart</span>
                                                            Visuales
                                                        </button>
                                                        <button 
                                                            onClick={() => setRightPanelDataOpen(prev => !prev)}
                                                            className={`px-2 py-1 rounded-lg transition-colors flex items-center gap-1 ${rightPanelDataOpen ? 'bg-white dark:bg-slate-800 text-primary shadow-xs' : 'hover:text-slate-900 dark:hover:text-white'}`}
                                                        >
                                                            <span className="material-symbols-outlined text-[14px]">database</span>
                                                            Datos
                                                        </button>
                                                    </div>
                                                )}

                                                {/* Botón Exportar en Modo Visor */}
                                                {mode === 'view' && !isSlideEmbed && (
                                                    <div className="relative group">
                                                        <button className="flex items-center gap-1.5 px-3 py-1 text-[10px] font-black uppercase tracking-wider rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-all select-none cursor-pointer">
                                                            <span className="material-symbols-outlined text-[14px]">download</span>
                                                            Exportar
                                                            <span className="material-symbols-outlined text-[14px]">keyboard_arrow_down</span>
                                                        </button>
                                                        <div className="absolute right-0 mt-1.5 w-48 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 py-1 font-sans">
                                                            <button onClick={handleExportPDF} className="w-full text-left px-4 py-2.5 text-xs text-slate-700 dark:text-slate-350 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2 cursor-pointer">
                                                                <span className="material-symbols-outlined text-[16px] text-rose-500">picture_as_pdf</span>
                                                                Exportar a PDF
                                                            </button>
                                                            <button onClick={handleExportExcel} className="w-full text-left px-4 py-2.5 text-xs text-slate-700 dark:text-slate-350 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2 cursor-pointer">
                                                                <span className="material-symbols-outlined text-[16px] text-emerald-600">table_view</span>
                                                                Exportar a Excel
                                                            </button>
                                                            <button onClick={handleExportCSV} className="w-full text-left px-4 py-2.5 text-xs text-slate-700 dark:text-slate-350 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2 cursor-pointer">
                                                                <span className="material-symbols-outlined text-[16px] text-emerald-500">table_rows</span>
                                                                Descargar CSV
                                                            </button>
                                                            <button onClick={() => window.print()} className="w-full text-left px-4 py-2.5 text-xs text-slate-700 dark:text-slate-350 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2 cursor-pointer border-t border-slate-100 dark:border-slate-700/50">
                                                                <span className="material-symbols-outlined text-[16px] text-blue-500">print</span>
                                                                Imprimir Reporte
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Cuerpo de la Diapositiva: Grilla Inteligente 16 Columnas × 9 Filas */}
                                        <div 
                                            onDragOver={e => {
                                                e.preventDefault();
                                                e.dataTransfer.dropEffect = 'move';
                                            }}
                                            onDrop={e => {
                                                e.preventDefault();
                                                // 1. Arrastre de campo desde panel de datos
                                                if (draggedField) {
                                                    const rect = e.currentTarget.getBoundingClientRect();
                                                    const clientX = e.clientX - rect.left;
                                                    const clientY = e.clientY - rect.top;
                                                    const col = Math.max(1, Math.min(16, Math.floor((clientX / rect.width) * 16) + 1));
                                                    const row = Math.max(1, Math.min(9, Math.floor((clientY / rect.height) * 9) + 1));

                                                    const newId = `widget-${Date.now()}`;
                                                    const isPct = draggedField.fieldKey.includes('%') || draggedField.fieldKey.toLowerCase().includes('ppc') || draggedField.fieldKey.toLowerCase().includes('avance') || draggedField.fieldKey.toLowerCase().includes('cumplimiento');
                                                    const targetComp = draggedField.sourceComponentId || activeEditingComponent;
                                                    const isSmall = ['kpi-card', 'gauge', 'slicer', 'image', 'text-box', 'divider'].includes(selectedVisualType || '');
                                                    const colSpan = isSmall ? 4 : 8;
                                                    const rowSpan = isSmall ? 2 : 4;

                                                    const clampedCol = Math.min(col, 16 - colSpan + 1);
                                                    const clampedRow = Math.min(row, 9 - rowSpan + 1);

                                                    const newW: WidgetConfig = {
                                                        id: newId,
                                                        pageId: activePageId,
                                                        type: selectedVisualType || 'bar-vertical',
                                                        componentId: targetComp,
                                                        colSpan: colSpan,
                                                        rowSpan: rowSpan,
                                                        gridColStart: clampedCol,
                                                        gridRowStart: clampedRow,
                                                        minHeightPx: 120,
                                                        axisFields: [],
                                                        valueFields: [{ fieldKey: draggedField.fieldKey, aggregation: isPct ? 'avg' : 'sum' }]
                                                    };
                                                    setWidgets(prev => [...prev, newW]);
                                                    setSelectedWidgetId(newId);
                                                    setDraggedField(null);
                                                    setDragOverCell(null);
                                                }
                                                // 2. Reposicionamiento de widget existente con Snap a Grilla 16x9
                                                else if (draggingWidget) {
                                                    const rect = e.currentTarget.getBoundingClientRect();
                                                    const clientX = e.clientX - rect.left;
                                                    const clientY = e.clientY - rect.top;
                                                    
                                                    // Casilla exacta donde se soltó el cursor
                                                    const rawCursorCol = Math.max(1, Math.min(16, Math.floor((clientX / rect.width) * 16) + 1));
                                                    const rawCursorRow = Math.max(1, Math.min(9, Math.floor((clientY / rect.height) * 9) + 1));
                                                    
                                                    // Calcular nueva esquina superior izquierda respetando dónde se tomó el widget
                                                    const targetCol = rawCursorCol - (draggingWidget.grabColOffset || 0);
                                                    const targetRow = rawCursorRow - (draggingWidget.grabRowOffset || 0);

                                                    const clampedCol = Math.max(1, Math.min(16 - draggingWidget.colSpan + 1, targetCol));
                                                    const clampedRow = Math.max(1, Math.min(9 - draggingWidget.rowSpan + 1, targetRow));

                                                    setWidgets(prev => prev.map(w => {
                                                        if (w.id !== draggingWidget.id) return w;
                                                        return {
                                                            ...w,
                                                            gridColStart: clampedCol,
                                                            gridRowStart: clampedRow
                                                        };
                                                    }));

                                                    setDraggingWidget(null);
                                                    setDragOverCell(null);
                                                }
                                            }}
                                            data-designer-grid="true"
                                            style={{
                                                display: 'grid',
                                                gridTemplateColumns: 'repeat(16, minmax(0, 1fr))',
                                                gridTemplateRows: 'repeat(9, minmax(0, 1fr))'
                                            }}
                                            className="gap-3 md:gap-3.5 w-full h-[650px] md:h-[720px] min-h-0 relative transition-all duration-300 select-none overflow-hidden"
                                        >
                                            {/* Cuadrícula Visual de Fondo (16 Cols x 9 Filas = 144 celdas) en Modo Edición */}
                                            {mode === 'edit' && (
                                                <div 
                                                    style={{
                                                        display: 'grid',
                                                        gridTemplateColumns: 'repeat(16, minmax(0, 1fr))',
                                                        gridTemplateRows: 'repeat(9, minmax(0, 1fr))'
                                                    }}
                                                    className="absolute inset-0 gap-3 md:gap-3.5 pointer-events-none z-0"
                                                >
                                                    {Array.from({ length: 144 }).map((_, idx) => {
                                                        const col = (idx % 16) + 1;
                                                        const row = Math.floor(idx / 16) + 1;
                                                        const isHovered = dragOverCell && dragOverCell.col === col && dragOverCell.row === row;

                                                        return (
                                                            <div 
                                                                key={idx}
                                                                className={`rounded-xl border border-dashed transition-colors duration-150 ${isHovered ? 'bg-primary/15 border-primary ring-1 ring-primary' : 'border-slate-300/40 dark:border-slate-800/60 bg-slate-100/30 dark:bg-slate-900/20'}`}
                                                            />
                                                        );
                                                    })}
                                                </div>
                                            )}

                                            {/* Renderizado de Widgets sobre la Cuadrícula */}
                                            {pageWidgets.map(widget => {
                                                const isSelected = selectedWidgetId === widget.id;
                                                const colSpan = Math.max(1, Math.min(16, widget.colSpan || (widget.width === '1/3' ? 5 : widget.width === 'full' ? 16 : 8)));
                                                const rowSpan = Math.max(1, Math.min(9, widget.rowSpan || (widget.type === 'header-banner' ? 1 : widget.type === 'kpi-card' || widget.type === 'gauge' || widget.type === 'text-box' ? 2 : 4)));
                                                const colStart = widget.gridColStart || 1;
                                                const rowStart = widget.gridRowStart || 1;

                                                    const rawWidgetRows = getComponentRows(widget.componentId);
                                                    const widgetRows = processCalculations(widget.componentId, rawWidgetRows);

                                                    const widgetCols = selectedColumns[widget.componentId] || DEFAULT_COLUMNS[widget.componentId] || [];
                                                    const hState = widgetHierarchyStates[widget.id] || { level: 0, path: [] };
                                                    const currentXAxisKey = (widget.axisFields && widget.axisFields.length > hState.level)
                                                        ? widget.axisFields[hState.level].fieldKey
                                                        : (widget.xAxisKey || widgetCols[0] || 'Categoría');

                                                    const wStyle = widget.style || {};
                                                    const isSlicerWidget = widget.type === 'slicer';
                                                    const isSlicerDropdownOpen = isSlicerWidget && !!openDropdownSlicers[widget.id];
                                                    const isCompactSlicer = isSlicerWidget && (rowSpan === 1 || wStyle.showTitle === false);

                                                    // Para slicers compactos (1 fila o con título desmarcado), la cabecera externa de tarjeta se oculta para no duplicar ni empujar el desplegable
                                                    const showTitle = wStyle.showTitle !== false && widget.type !== 'header-banner' && widget.type !== 'text-box' && !isCompactSlicer;
                                                    const customTitle = wStyle.customTitle;
                                                    const titleColor = wStyle.titleColor;
                                                    const titleFontSize = wStyle.titleFontSize;
                                                    const titleAlign = wStyle.titleAlignment || 'left';
                                                    const showBg = wStyle.showBackground !== false;
                                                    const bgColor = wStyle.backgroundColor || (showBg ? currentTheme.cardBgColor : 'transparent');
                                                    const showBorder = wStyle.showBorder !== false;
                                                    const borderColor = wStyle.borderColor || currentTheme.borderColor;
                                                    const borderRadius = wStyle.borderRadius !== undefined ? `${wStyle.borderRadius}px` : '16px';

                                                    const showTitleDivider = wStyle.showTitleDivider !== false;
                                                    const titlePaddingBottom = wStyle.titlePadding !== undefined ? `${wStyle.titlePadding}px` : '4px';
                                                    const titleMarginBottom = wStyle.titleMarginBottom !== undefined ? `${wStyle.titleMarginBottom}px` : '6px';

                                                    const titleAlignClass = titleAlign === 'center' ? 'text-center' : titleAlign === 'right' ? 'text-right' : 'text-left';

                                                    return (
                                                        <div 
                                                            key={widget.id}
                                                            draggable={mode === 'edit'}
                                                            onDragStart={e => {
                                                                if (mode !== 'edit') return;
                                                                const cardRect = e.currentTarget.getBoundingClientRect();
                                                                const grabOffsetX = e.clientX - cardRect.left;
                                                                const grabOffsetY = e.clientY - cardRect.top;
                                                                
                                                                // Convertir el punto de agarre relativo dentro de la tarjeta a celdas offset (0-based)
                                                                const grabColOffset = Math.max(0, Math.min(colSpan - 1, Math.floor((grabOffsetX / cardRect.width) * colSpan)));
                                                                const grabRowOffset = Math.max(0, Math.min(rowSpan - 1, Math.floor((grabOffsetY / cardRect.height) * rowSpan)));

                                                                setDraggingWidget({
                                                                    id: widget.id,
                                                                    startCol: colStart,
                                                                    startRow: rowStart,
                                                                    colSpan: colSpan,
                                                                    rowSpan: rowSpan,
                                                                    grabColOffset,
                                                                    grabRowOffset
                                                                });
                                                                e.dataTransfer.effectAllowed = 'move';
                                                                e.dataTransfer.setData('text/plain', widget.id);
                                                            }}
                                                            onDragEnd={() => {
                                                                setDraggingWidget(null);
                                                                setDragOverCell(null);
                                                            }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (mode === 'edit') {
                                                                    setSelectedWidgetId(widget.id);
                                                                    setSelectedVisualType(widget.type);
                                                                    if (widget.componentId) {
                                                                        setActiveEditingComponent(widget.componentId);
                                                                    }
                                                                }
                                                            }}
                                                            style={{
                                                                gridColumnStart: colStart,
                                                                gridColumnEnd: `span ${colSpan}`,
                                                                gridRowStart: rowStart,
                                                                gridRowEnd: `span ${rowSpan}`,
                                                                backgroundColor: bgColor,
                                                                borderColor: borderColor,
                                                                borderRadius: borderRadius
                                                            }}
                                                            className={`${isCompactSlicer ? 'p-1.5 md:p-2' : 'p-3 md:p-4'} flex flex-col justify-center transition-all cursor-pointer relative ${isSlicerDropdownOpen ? 'z-50' : isSlicerWidget ? 'z-20' : 'z-10'} ${isSlicerWidget ? 'overflow-visible' : 'overflow-hidden'} ${draggingWidget?.id === widget.id ? 'opacity-40 ring-2 ring-primary/60 scale-[0.99]' : ''} ${mode === 'edit' ? 'cursor-grab active:cursor-grabbing' : ''} ${!showBorder ? 'border-transparent' : (mode === 'edit' && isSelected) ? 'border-primary ring-2 ring-primary/40 shadow-xl shadow-primary/10' : 'border shadow-md hover:border-slate-300 dark:hover:border-slate-700'}`}
                                                        >
                                                            {/* Cabecera del Widget con Espaciado y Línea Ajustables */}
                                                            {showTitle && (
                                                                <div 
                                                                    style={{ 
                                                                        marginBottom: titleMarginBottom, 
                                                                        paddingBottom: titlePaddingBottom 
                                                                    }} 
                                                                    className={`flex items-center justify-between shrink-0 ${showTitleDivider ? 'border-b border-slate-100 dark:border-slate-800/80' : ''}`}
                                                                >
                                                                    <div className={`truncate flex-1 ${titleAlignClass}`}>
                                                                        <h5 
                                                                            style={{
                                                                                color: titleColor || currentTheme.textColor,
                                                                                fontSize: titleFontSize ? `${titleFontSize}px` : undefined
                                                                            }}
                                                                            className="text-[11px] font-extrabold truncate"
                                                                        >
                                                                            {customTitle || (
                                                                                <>
                                                                                    {widget.type === 'bar-vertical' && 'Barras V'}
                                                                                    {widget.type === 'bar-horizontal' && 'Barras H'}
                                                                                    {widget.type === 'pie' && 'Gráfico Circular'}
                                                                                    {widget.type === 'matrix' && 'Matriz Jerárquica'}
                                                                                    {widget.type === 'line' && 'Líneas'}
                                                                                    {widget.type === 'area-stacked' && 'Áreas Ap.'}
                                                                                    {widget.type === 'scatter' && 'Dispersión'}
                                                                                    {widget.type === 'radar' && 'Radar'}
                                                                                    {widget.type === 'kpi-card' && 'Indicador'}
                                                                                    {widget.type === 'gauge' && 'Indicador de cumplimiento'}
                                                                                    {widget.type === 'image' && 'Registro Fotográfico'}
                                                                                    {widget.type === 'container-card' && 'Contenedor Visual'}
                                                                                    {` por ${currentXAxisKey}`}
                                                                                </>
                                                                            )}
                                                                        </h5>
                                                                        {hState.level > 0 && (
                                                                            <span className="text-[8px] text-primary/80 font-bold tracking-tight block truncate mt-0.5" title={hState.path.join(' > ')}>
                                                                                Ruta: {hState.path.join(' > ')}
                                                                            </span>
                                                                        )}
                                                                    </div>

                                                                    {/* Control para subir de nivel en jerarquía */}
                                                                    {hState.level > 0 && (
                                                                        <div className="flex items-center shrink-0 ml-2" onClick={e => e.stopPropagation()}>
                                                                            <button 
                                                                                onClick={() => {
                                                                                    setWidgetHierarchyStates(prev => ({
                                                                                        ...prev,
                                                                                        [widget.id]: {
                                                                                            level: hState.level - 1,
                                                                                            path: hState.path.slice(0, -1)
                                                                                        }
                                                                                    }));
                                                                                }}
                                                                                className="flex items-center gap-0.5 px-2 py-0.5 rounded-lg text-[9px] bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 transition-all font-black cursor-pointer"
                                                                                title="Volver al nivel anterior"
                                                                            >
                                                                                <span className="material-symbols-outlined text-[12px]">arrow_upward</span>
                                                                                Subir
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}

                                                            {/* Drop Zone y Cuerpo del Widget */}
                                                            <div 
                                                                onDragOver={e => {
                                                                    e.preventDefault();
                                                                }}
                                                                onDrop={e => {
                                                                    if (draggedField) {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        handleDropFieldToBucket(widget.id, 'value', draggedField.fieldKey, draggedField.sourceComponentId);
                                                                        setDraggedField(null);
                                                                    }
                                                                }}
                                                                className={`flex-1 w-full min-h-0 flex flex-col justify-center ${isSlicerWidget ? 'overflow-visible' : 'overflow-hidden'}`}
                                                            >
                                                                {renderWidgetChart(widget, widgetRows, widgetCols, widget.componentId)}
                                                            </div>

                                                            {/* TIRADORES DE REDIMENSIONAMIENTO INTERACTIVO (SNAP TO 12×7 GRID) */}
                                                            {mode === 'edit' && isSelected && (
                                                                <>
                                                                    {/* Borde Este / Derecha */}
                                                                    <div
                                                                        onPointerDown={e => {
                                                                            e.stopPropagation();
                                                                            e.preventDefault();
                                                                            const gridEl = (e.currentTarget.closest('[data-designer-grid="true"]') as HTMLElement);
                                                                            if (!gridEl) return;
                                                                            setResizingWidget({
                                                                                id: widget.id,
                                                                                direction: 'e',
                                                                                initialColStart: colStart,
                                                                                initialRowStart: rowStart,
                                                                                initialColSpan: colSpan,
                                                                                initialRowSpan: rowSpan,
                                                                                gridRect: gridEl.getBoundingClientRect()
                                                                            });
                                                                        }}
                                                                        className="absolute inset-y-2 -right-1 w-3 cursor-ew-resize hover:bg-primary/20 transition-colors z-30 flex items-center justify-center group/handle"
                                                                        title="Arrastra para cambiar ancho (columnas)"
                                                                    >
                                                                        <div className="w-1.5 h-6 bg-primary rounded-full shadow-sm group-hover/handle:scale-125 transition-transform" />
                                                                    </div>

                                                                    {/* Borde Oeste / Izquierda */}
                                                                    <div
                                                                        onPointerDown={e => {
                                                                            e.stopPropagation();
                                                                            e.preventDefault();
                                                                            const gridEl = (e.currentTarget.closest('[data-designer-grid="true"]') as HTMLElement);
                                                                            if (!gridEl) return;
                                                                            setResizingWidget({
                                                                                id: widget.id,
                                                                                direction: 'w',
                                                                                initialColStart: colStart,
                                                                                initialRowStart: rowStart,
                                                                                initialColSpan: colSpan,
                                                                                initialRowSpan: rowSpan,
                                                                                gridRect: gridEl.getBoundingClientRect()
                                                                            });
                                                                        }}
                                                                        className="absolute inset-y-2 -left-1 w-3 cursor-ew-resize hover:bg-primary/20 transition-colors z-30 flex items-center justify-center group/handle"
                                                                        title="Arrastra para cambiar posición/ancho"
                                                                    >
                                                                        <div className="w-1.5 h-6 bg-primary rounded-full shadow-sm group-hover/handle:scale-125 transition-transform" />
                                                                    </div>

                                                                    {/* Borde Sur / Inferior */}
                                                                    <div
                                                                        onPointerDown={e => {
                                                                            e.stopPropagation();
                                                                            e.preventDefault();
                                                                            const gridEl = (e.currentTarget.closest('[data-designer-grid="true"]') as HTMLElement);
                                                                            if (!gridEl) return;
                                                                            setResizingWidget({
                                                                                id: widget.id,
                                                                                direction: 's',
                                                                                initialColStart: colStart,
                                                                                initialRowStart: rowStart,
                                                                                initialColSpan: colSpan,
                                                                                initialRowSpan: rowSpan,
                                                                                gridRect: gridEl.getBoundingClientRect()
                                                                            });
                                                                        }}
                                                                        className="absolute inset-x-2 -bottom-1 h-3 cursor-ns-resize hover:bg-primary/20 transition-colors z-30 flex items-center justify-center group/handle"
                                                                        title="Arrastra para cambiar alto (filas)"
                                                                    >
                                                                        <div className="h-1.5 w-6 bg-primary rounded-full shadow-sm group-hover/handle:scale-125 transition-transform" />
                                                                    </div>

                                                                    {/* Borde Norte / Superior */}
                                                                    <div
                                                                        onPointerDown={e => {
                                                                            e.stopPropagation();
                                                                            e.preventDefault();
                                                                            const gridEl = (e.currentTarget.closest('[data-designer-grid="true"]') as HTMLElement);
                                                                            if (!gridEl) return;
                                                                            setResizingWidget({
                                                                                id: widget.id,
                                                                                direction: 'n',
                                                                                initialColStart: colStart,
                                                                                initialRowStart: rowStart,
                                                                                initialColSpan: colSpan,
                                                                                initialRowSpan: rowSpan,
                                                                                gridRect: gridEl.getBoundingClientRect()
                                                                            });
                                                                        }}
                                                                        className="absolute inset-x-2 -top-1 h-3 cursor-ns-resize hover:bg-primary/20 transition-colors z-30 flex items-center justify-center group/handle"
                                                                        title="Arrastra para cambiar posición/alto"
                                                                    >
                                                                        <div className="h-1.5 w-6 bg-primary rounded-full shadow-sm group-hover/handle:scale-125 transition-transform" />
                                                                    </div>

                                                                    {/* Esquina Inferior Derecha (SE) */}
                                                                    <div
                                                                        onPointerDown={e => {
                                                                            e.stopPropagation();
                                                                            e.preventDefault();
                                                                            const gridEl = (e.currentTarget.closest('[data-designer-grid="true"]') as HTMLElement);
                                                                            if (!gridEl) return;
                                                                            setResizingWidget({
                                                                                id: widget.id,
                                                                                direction: 'se',
                                                                                initialColStart: colStart,
                                                                                initialRowStart: rowStart,
                                                                                initialColSpan: colSpan,
                                                                                initialRowSpan: rowSpan,
                                                                                gridRect: gridEl.getBoundingClientRect()
                                                                            });
                                                                        }}
                                                                        className="absolute -bottom-1.5 -right-1.5 size-4 bg-primary text-white rounded-full border-2 border-white dark:border-slate-900 shadow-md cursor-nwse-resize z-40 hover:scale-125 transition-transform"
                                                                        title="Arrastra para cambiar ancho y alto simultáneamente"
                                                                    />

                                                                    {/* Esquina Inferior Izquierda (SW) */}
                                                                    <div
                                                                        onPointerDown={e => {
                                                                            e.stopPropagation();
                                                                            e.preventDefault();
                                                                            const gridEl = (e.currentTarget.closest('[data-designer-grid="true"]') as HTMLElement);
                                                                            if (!gridEl) return;
                                                                            setResizingWidget({
                                                                                id: widget.id,
                                                                                direction: 'sw',
                                                                                initialColStart: colStart,
                                                                                initialRowStart: rowStart,
                                                                                initialColSpan: colSpan,
                                                                                initialRowSpan: rowSpan,
                                                                                gridRect: gridEl.getBoundingClientRect()
                                                                            });
                                                                        }}
                                                                        className="absolute -bottom-1.5 -left-1.5 size-4 bg-primary text-white rounded-full border-2 border-white dark:border-slate-900 shadow-md cursor-nesw-resize z-40 hover:scale-125 transition-transform"
                                                                        title="Arrastra esquina"
                                                                    />

                                                                    {/* Esquina Superior Derecha (NE) */}
                                                                    <div
                                                                        onPointerDown={e => {
                                                                            e.stopPropagation();
                                                                            e.preventDefault();
                                                                            const gridEl = (e.currentTarget.closest('[data-designer-grid="true"]') as HTMLElement);
                                                                            if (!gridEl) return;
                                                                            setResizingWidget({
                                                                                id: widget.id,
                                                                                direction: 'ne',
                                                                                initialColStart: colStart,
                                                                                initialRowStart: rowStart,
                                                                                initialColSpan: colSpan,
                                                                                initialRowSpan: rowSpan,
                                                                                gridRect: gridEl.getBoundingClientRect()
                                                                            });
                                                                        }}
                                                                        className="absolute -top-1.5 -right-1.5 size-4 bg-primary text-white rounded-full border-2 border-white dark:border-slate-900 shadow-md cursor-nesw-resize z-40 hover:scale-125 transition-transform"
                                                                        title="Arrastra esquina"
                                                                    />

                                                                    {/* Esquina Superior Izquierda (NW) */}
                                                                    <div
                                                                        onPointerDown={e => {
                                                                            e.stopPropagation();
                                                                            e.preventDefault();
                                                                            const gridEl = (e.currentTarget.closest('[data-designer-grid="true"]') as HTMLElement);
                                                                            if (!gridEl) return;
                                                                            setResizingWidget({
                                                                                id: widget.id,
                                                                                direction: 'nw',
                                                                                initialColStart: colStart,
                                                                                initialRowStart: rowStart,
                                                                                initialColSpan: colSpan,
                                                                                initialRowSpan: rowSpan,
                                                                                gridRect: gridEl.getBoundingClientRect()
                                                                            });
                                                                        }}
                                                                        className="absolute -top-1.5 -left-1.5 size-4 bg-primary text-white rounded-full border-2 border-white dark:border-slate-900 shadow-md cursor-nwse-resize z-40 hover:scale-125 transition-transform"
                                                                        title="Arrastra esquina"
                                                                    />
                                                                </>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                        </div>
                                    </div>
                                        );
                                    })()}

                                    {/* BARRA INFERIOR DE NAVEGACIÓN POR HOJAS / PÁGINAS ESTILO POWER BI */}
                            <div className="bg-white dark:bg-slate-800 px-4 py-2 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm flex items-center justify-between shrink-0 select-none animate-fade-in" onClick={e => e.stopPropagation()}>
                                <div className="flex items-center gap-1.5 overflow-x-auto py-1">
                                    <div className="flex items-center gap-1 mr-2 text-slate-400 dark:text-slate-500">
                                        <span className="material-symbols-outlined text-[16px]">tab</span>
                                        <span className="text-[9px] font-black uppercase tracking-wider">Páginas</span>
                                    </div>

                                    {reportPages.map(page => {
                                        const isActive = page.id === activePageId;
                                        const isEditing = editingPageId === page.id;

                                        return (
                                            <div
                                                key={page.id}
                                                onDoubleClick={() => {
                                                    if (mode === 'edit') {
                                                        setEditingPageId(page.id);
                                                        setEditingPageName(page.name);
                                                    }
                                                }}
                                                onClick={() => {
                                                    setActivePageId(page.id);
                                                    setSelectedWidgetId(null);
                                                }}
                                                className={`group flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-[10px] font-black transition-all cursor-pointer ${isActive ? 'bg-primary text-white border-primary shadow-sm shadow-primary/20' : 'bg-slate-50 dark:bg-slate-900/60 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700/80 hover:bg-slate-100 dark:hover:bg-slate-750'}`}
                                                title={mode === 'edit' ? "Doble clic para renombrar" : undefined}
                                            >
                                                {isEditing && mode === 'edit' ? (
                                                    <input
                                                        type="text"
                                                        value={editingPageName}
                                                        onChange={e => setEditingPageName(e.target.value)}
                                                        onBlur={() => {
                                                            if (editingPageName.trim()) {
                                                                setReportPages(prev => prev.map(p => p.id === page.id ? { ...p, name: editingPageName.trim() } : p));
                                                            }
                                                            setEditingPageId(null);
                                                        }}
                                                        onKeyDown={e => {
                                                            if (e.key === 'Enter') {
                                                                if (editingPageName.trim()) {
                                                                    setReportPages(prev => prev.map(p => p.id === page.id ? { ...p, name: editingPageName.trim() } : p));
                                                                }
                                                                setEditingPageId(null);
                                                            } else if (e.key === 'Escape') {
                                                                setEditingPageId(null);
                                                            }
                                                        }}
                                                        autoFocus
                                                        className="bg-white dark:bg-slate-800 text-slate-900 dark:text-white px-1.5 py-0.5 rounded text-[10px] font-bold border border-primary outline-hidden"
                                                    />
                                                ) : (
                                                    <span>{page.name}</span>
                                                )}

                                                {/* Botón eliminar página (si hay más de 1 y en modo edición) */}
                                                {mode === 'edit' && reportPages.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const pageWidgetCount = widgets.filter(w => (w.pageId || 'page-1') === page.id).length;
                                                            if (pageWidgetCount > 0) {
                                                                if (!window.confirm(`¿Eliminar la página "${page.name}" y sus ${pageWidgetCount} gráfico(s)?`)) {
                                                                    return;
                                                                }
                                                            }
                                                            // Eliminar página y sus widgets asociados
                                                            setWidgets(prev => prev.filter(w => (w.pageId || 'page-1') !== page.id));
                                                            setReportPages(prev => {
                                                                const filtered = prev.filter(p => p.id !== page.id);
                                                                if (activePageId === page.id && filtered.length > 0) {
                                                                    setActivePageId(filtered[0].id);
                                                                }
                                                                return filtered;
                                                            });
                                                        }}
                                                        className={`opacity-0 group-hover:opacity-100 p-0.5 hover:bg-black/10 dark:hover:bg-white/20 rounded transition-opacity ${isActive ? 'text-white' : 'text-slate-400 hover:text-red-500'}`}
                                                        title="Eliminar página"
                                                    >
                                                        <span className="material-symbols-outlined text-[13px] block">close</span>
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })}

                                    {/* Botón + Añadir Nueva Página (solo en modo edición) */}
                                    {mode === 'edit' && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const newPageNum = reportPages.length + 1;
                                                const newPageId = `page-${Date.now()}`;
                                                const newPage: ReportPage = {
                                                    id: newPageId,
                                                    name: `Página ${newPageNum}`
                                                };
                                                setReportPages(prev => [...prev, newPage]);
                                                setActivePageId(newPageId);
                                                setSelectedWidgetId(null);
                                            }}
                                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl border border-dashed border-primary/40 text-primary hover:bg-primary/5 transition-all text-[10px] font-black cursor-pointer ml-1"
                                            title="Agregar nueva página al reporte"
                                        >
                                            <span className="material-symbols-outlined text-[14px]">add</span>
                                            <span>Nueva página</span>
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Hoja de Datos y Transformaciones Analíticas (solo en modo edición) */}
                            {mode === 'edit' && (
                                <div className="bg-white dark:bg-slate-800 p-6 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-sm flex flex-col min-h-[380px] shrink-0">
                                    {activeEditingComponent ? (() => {
                                        const sourceRows = getComponentRows(activeEditingComponent);

                                        const processedSpreadsheetRows = processCalculations(activeEditingComponent, sourceRows);
                                        let activeColumns = selectedColumns[activeEditingComponent] || DEFAULT_COLUMNS[activeEditingComponent] || [];
                                        if (activeEditingComponent === 'quality' && activeColumns.includes('Revisor 1')) {
                                            activeColumns = [...DEFAULT_COLUMNS.quality];
                                        }
                                        
                                        if (concatColA && concatColB && !activeColumns.includes('Concatenado')) {
                                            activeColumns = [...activeColumns, 'Concatenado'];
                                        }
                                        if (runningTotalCol && !activeColumns.includes('Acumulado')) {
                                            activeColumns = [...activeColumns, 'Acumulado'];
                                        }
                                        if (movingAverageCol && !activeColumns.includes(`Promedio móvil (N=${movingAveragePeriod})`)) {
                                            activeColumns = [...activeColumns, `Promedio móvil (N=${movingAveragePeriod})`];
                                        }
                                        if (deltaCol && !activeColumns.includes('Variación %')) {
                                            activeColumns = [...activeColumns, 'Variación %'];
                                        }

                                        return (
                                            <>
                                                <div className="flex items-center justify-between mb-4 border-b border-slate-100 dark:border-slate-750 pb-2">
                                                    <div>
                                                        <h4 className="text-xs font-black uppercase text-slate-800 dark:text-slate-250">
                                                            Hoja de datos K-LyDApp (Vista previa)
                                                        </h4>
                                                        <p className="text-[9px] text-slate-400 mt-0.5">
                                                            Visualización interactiva con transformaciones lógicas y matemáticas en tiempo real.
                                                        </p>
                                                    </div>
                                                    <span className="text-[10px] font-bold text-slate-450">
                                                        {processedSpreadsheetRows.length} registros filtrados
                                                    </span>
                                                </div>

                                                {renderExcelSpreadsheet(processedSpreadsheetRows, activeColumns)}
                                            </>
                                        );
                                    })() : (
                                        <div className="flex-1 flex flex-col items-center justify-center text-center p-8 bg-slate-50 dark:bg-slate-900/30 rounded-2xl border border-dashed border-slate-200 dark:border-slate-700 py-12">
                                            <span className="material-symbols-outlined text-[32px] block mb-2 text-slate-350">table_chart</span>
                                            Selecciona un componente del panel de Datos para desplegar su hoja asociada.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* 2. PANEL LATERAL: VISUALIZACIONES (ESTILO POWER BI) Y PANEL DATOS (SOLO EN MODO EDICIÓN) */}
                        {mode === 'edit' && (
                            <>
                                {rightPanelVisOpen ? (
                            <div className="w-80 bg-white dark:bg-slate-800 border-l border-slate-200 dark:border-slate-700 flex flex-col h-full shrink-0 shadow-lg z-20 transition-all duration-300">
                                {/* Cabecera Panel Visualizaciones con selector de Pestañas (Campos vs Formato Pincel) */}
                                <div className="p-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-850 flex flex-col gap-2">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <span className="material-symbols-outlined text-[18px] text-primary">pie_chart</span>
                                            <span className="text-xs font-black uppercase tracking-wider text-slate-800 dark:text-white">Visualizaciones</span>
                                        </div>
                                        <button 
                                            onClick={() => setRightPanelVisOpen(false)}
                                            className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg text-slate-400 hover:text-slate-600 transition-colors"
                                            title="Colapsar panel"
                                        >
                                            <span className="material-symbols-outlined text-[16px]">chevron_right</span>
                                        </button>
                                    </div>

                                    {/* Iconos de Pestaña: [Campos/Ejes] y [Formato Visual (Pincel)] */}
                                    <div className="flex items-center gap-1 bg-slate-200/80 dark:bg-slate-900/80 p-1 rounded-xl">
                                        <button
                                            type="button"
                                            onClick={() => setVisPanelMainTab('fields')}
                                            className={`flex-1 py-1 px-2 rounded-lg flex items-center justify-center gap-1.5 text-[10px] font-black transition-all ${visPanelMainTab === 'fields' ? 'bg-white dark:bg-slate-800 text-primary shadow-xs' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}
                                            title="Configurar Campos y Ejes"
                                        >
                                            <span className="material-symbols-outlined text-[15px]">tune</span>
                                            <span>Campos</span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setVisPanelMainTab('format')}
                                            className={`flex-1 py-1 px-2 rounded-lg flex items-center justify-center gap-1.5 text-[10px] font-black transition-all ${visPanelMainTab === 'format' ? 'bg-white dark:bg-slate-800 text-primary shadow-xs' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}
                                            title="Dar formato visual al gráfico (Pincel)"
                                        >
                                            <span className="material-symbols-outlined text-[15px]">brush</span>
                                            <span>Formato visual</span>
                                        </button>
                                    </div>
                                </div>

                                <div className="flex-1 overflow-y-auto p-4 space-y-4 text-xs">
                                    {/* PESTAÑA 1: CAMPOS Y EJES */}
                                    {visPanelMainTab === 'fields' && (
                                        <>
                                            {/* Matriz de Tipos de Gráficos */}
                                            <div className="space-y-2">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Tipo de Visual</span>
                                                    <label className="flex items-center gap-1 cursor-pointer select-none text-[9px] font-bold text-slate-500 hover:text-primary transition-colors">
                                                        <input 
                                                            type="checkbox" 
                                                            checked={crossFilteringActive}
                                                            onChange={e => {
                                                                setCrossFilteringActive(e.target.checked);
                                                                setCrossFilter(null);
                                                            }}
                                                            className="rounded text-primary h-3 w-3 cursor-pointer"
                                                        />
                                                        Filtro cruzado
                                                    </label>
                                                </div>

                                                <div className="grid grid-cols-4 gap-1.5 bg-slate-50 dark:bg-slate-900/60 p-2 rounded-xl border border-slate-200/60 dark:border-slate-700/60 max-h-[165px] overflow-y-auto">
                                                    {[
                                                        { id: 'header-banner', icon: 'splitscreen_top', label: 'Encabezado' },
                                                        { id: 'image', icon: 'image', label: 'Imagen' },
                                                        { id: 'container-card', icon: 'select_all', label: 'Contenedor' },
                                                        { id: 'text-box', icon: 'text_fields', label: 'Texto' },
                                                        { id: 'table', icon: 'table_chart', label: 'Tabla' },
                                                        { id: 'matrix', icon: 'schema', label: 'Matriz' },
                                                        { id: 'column-grouped-vertical', icon: 'bar_chart', label: 'Col. Agrup.' },
                                                        { id: 'column-stacked', icon: 'stacked_bar_chart', label: 'Col. Apiladas' },
                                                        { id: 'bar-grouped-horizontal', icon: 'align_horizontal_left', label: 'Barras Agrup.' },
                                                        { id: 'combo-chart', icon: 'auto_graph', label: 'Combinado' },
                                                        { id: 'pie-solid', icon: 'pie_chart', label: 'Torta' },
                                                        { id: 'donut', icon: 'donut_large', label: 'Anillos' },
                                                        { id: 'slicer', icon: 'filter_alt', label: 'Segmentación' },
                                                        { id: 'kpi-multi', icon: 'analytics', label: 'KPI Multi' },
                                                        { id: 'bar-vertical', icon: 'bar_chart', label: 'Barras V' },
                                                        { id: 'bar-horizontal', icon: 'notes', label: 'Barras H' },
                                                        { id: 'pie', icon: 'pie_chart_outline', label: 'Circular' },
                                                        { id: 'line', icon: 'show_chart', label: 'Líneas' },
                                                        { id: 'area-stacked', icon: 'area_chart', label: 'Áreas Ap.' },
                                                        { id: 'scatter', icon: 'scatter_plot', label: 'Dispersión' },
                                                        { id: 'radar', icon: 'radar', label: 'Radar' },
                                                        { id: 'kpi-card', icon: 'counter_1', label: 'Tarjeta' },
                                                        { id: 'gauge', icon: 'speed', label: 'Medidor' }
                                                    ].map(vis => {
                                                        const activeWidget = widgets.find(w => w.id === selectedWidgetId);
                                                        const isSelected = activeWidget ? activeWidget.type === vis.id : selectedVisualType === vis.id;

                                                        return (
                                                            <button
                                                                key={vis.id}
                                                                onClick={() => {
                                                                    if (activeWidget) {
                                                                        setWidgets(prev => prev.map(w => w.id === activeWidget.id ? { ...w, type: vis.id as any } : w));
                                                                        setSelectedVisualType(vis.id as any);
                                                                    } else {
                                                                        handleInsertEmptyWidget(vis.id as any);
                                                                    }
                                                                }}
                                                                className={`flex flex-col items-center justify-center p-1.5 rounded-lg border transition-all ${isSelected ? 'border-primary bg-primary text-white shadow-sm shadow-primary/20 scale-[1.04]' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                                                                title={vis.label}
                                                            >
                                                                <span className="material-symbols-outlined text-[16px]">{vis.icon}</span>
                                                                <span className="text-[7px] font-bold mt-0.5 tracking-tighter truncate w-full text-center">{vis.label}</span>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>

                                            {/* BUCKETS / CONTENEDORES DE CAMPOS ESTILO POWER BI */}
                                            {(() => {
                                                const currentWidget = selectedWidgetId ? widgets.find(w => w.id === selectedWidgetId) : null;
                                                if (!currentWidget) {
                                                    return (
                                                        <div className="p-6 bg-slate-50 dark:bg-slate-900/40 rounded-2xl border border-dashed border-slate-200 dark:border-slate-700/80 text-center space-y-2">
                                                            <span className="material-symbols-outlined text-[28px] text-slate-300 dark:text-slate-600 block mx-auto">ads_click</span>
                                                            <p className="font-bold text-[11px] text-slate-700 dark:text-slate-300">Ningún gráfico seleccionado</p>
                                                            <p className="text-[9px] text-slate-400 leading-relaxed">
                                                                Haz clic en un gráfico del lienzo para configurar sus ejes y campos, o selecciona un tipo de visual arriba para agregar uno nuevo.
                                                            </p>
                                                        </div>
                                                    );
                                                }

                                                const bucketDefs = WIDGET_BUCKET_DEFS[currentWidget.type] || [
                                                    { id: 'axis', label: 'Eje X (Categorías)', placeholder: 'Arrastra campos aquí' },
                                                    { id: 'value', label: 'Valores (Métricas)', placeholder: 'Arrastra campos numéricos' }
                                                ];

                                                return (
                                                    <div className="space-y-3 animate-fade-in">
                                                        <div className="flex items-center justify-between pb-1 border-b border-slate-100 dark:border-slate-700/50">
                                                            <span className="text-[10px] font-black text-primary uppercase tracking-wider flex items-center gap-1">
                                                                <span className="material-symbols-outlined text-[14px]">tune</span>
                                                                Ejes del Gráfico Activo
                                                            </span>
                                                            <span className="text-[8px] bg-primary/10 text-primary font-black px-1.5 py-0.5 rounded-full">
                                                                {currentWidget.type}
                                                            </span>
                                                        </div>

                                                        {bucketDefs.map(bDef => {
                                                            let currentFields: WidgetFieldConfig[] = [];
                                                            if (bDef.id === 'axis') currentFields = currentWidget.axisFields || [];
                                                            else if (bDef.id === 'value') currentFields = currentWidget.valueFields || [];
                                                            else if (bDef.id === 'legend') currentFields = currentWidget.legendField ? [currentWidget.legendField] : [];
                                                            else if (bDef.id === 'detail') currentFields = currentWidget.detailFields || [];
                                                            else if (bDef.id === 'size') currentFields = currentWidget.sizeField ? [currentWidget.sizeField] : [];

                                                            const isFull = bDef.max ? currentFields.length >= bDef.max : false;

                                                            return (
                                                                <div 
                                                                    key={bDef.id}
                                                                    onDragOver={e => {
                                                                        if (!isFull) e.preventDefault();
                                                                    }}
                                                                    onDrop={e => {
                                                                        e.preventDefault();
                                                                        if (draggedField && !isFull) {
                                                                            handleDropFieldToBucket(currentWidget.id, bDef.id, draggedField.fieldKey, draggedField.sourceComponentId);
                                                                            setDraggedField(null);
                                                                        }
                                                                    }}
                                                                    className="p-2.5 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-200/80 dark:border-slate-700/80 space-y-1.5 transition-colors"
                                                                >
                                                                    <div className="flex justify-between items-center">
                                                                        <span className="text-[9px] font-black uppercase text-slate-600 dark:text-slate-300 flex items-center gap-1">
                                                                            {bDef.label}
                                                                            {bDef.max && <span className="text-[7px] text-slate-400">(Máx {bDef.max})</span>}
                                                                        </span>
                                                                        <span className="text-[8px] text-slate-400 font-bold">{currentFields.length}</span>
                                                                    </div>

                                                                    {currentFields.length === 0 ? (
                                                                        <div className="p-2.5 text-center text-[8px] text-slate-400 dark:text-slate-500 italic border border-dashed border-slate-200 dark:border-slate-700 rounded-lg">
                                                                            {bDef.placeholder}
                                                                        </div>
                                                                    ) : (
                                                                        <div className="space-y-1">
                                                                            {currentFields.map((f, idx) => (
                                                                                <div 
                                                                                    key={`${f.fieldKey}-${idx}`}
                                                                                    className="flex items-center justify-between p-1.5 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-2xs text-[9px]"
                                                                                >
                                                                                    <div className="flex items-center gap-1 truncate flex-1 pr-1">
                                                                                        <span className="material-symbols-outlined text-[12px] text-slate-400">drag_indicator</span>
                                                                                        <span className="truncate font-bold text-slate-700 dark:text-slate-200">{f.fieldKey}</span>
                                                                                    </div>

                                                                                    <div className="flex items-center gap-1">
                                                                                        {bDef.id === 'value' && (
                                                                                            <select
                                                                                                value={f.aggregation || 'sum'}
                                                                                                onChange={e => handleUpdateFieldAggregation(currentWidget.id, bDef.id, idx, e.target.value as any)}
                                                                                                className="text-[8px] font-bold py-0.5 px-1 bg-slate-100 dark:bg-slate-700 border-none rounded text-slate-600 dark:text-slate-300 cursor-pointer"
                                                                                            >
                                                                                                <option value="sum">Suma</option>
                                                                                                <option value="avg">Promedio</option>
                                                                                                <option value="count">Recuento</option>
                                                                                                <option value="distinct_count">Recuento Dist.</option>
                                                                                                <option value="min">Mínimo</option>
                                                                                                <option value="max">Máximo</option>
                                                                                                <option value="none">Sin Agregación</option>
                                                                                            </select>
                                                                                        )}

                                                                                        {/* Indicador / Alternador de Jerarquía de Fecha en el Campo */}
                                                                                        {(bDef.id === 'axis' && currentWidget.type === 'slicer' && (f.fieldKey.toLowerCase().includes('fecha') || f.fieldKey.toLowerCase().includes('date') || f.fieldKey.toLowerCase().includes('inicio') || f.fieldKey.toLowerCase().includes('termino'))) && (() => {
                                                                                            const isHActive = currentWidget.style?.slicerDateHierarchy !== false;
                                                                                            return (
                                                                                                <button
                                                                                                    type="button"
                                                                                                    onClick={() => {
                                                                                                        setWidgets(prev => prev.map(w => w.id === currentWidget.id ? {
                                                                                                            ...w,
                                                                                                            style: { ...(w.style || {}), slicerDateHierarchy: !isHActive }
                                                                                                        } : w));
                                                                                                    }}
                                                                                                    className={`px-1.5 py-0.5 rounded text-[8px] font-bold flex items-center gap-0.5 transition-colors ${isHActive ? 'bg-primary text-white shadow-2xs' : 'bg-slate-100 dark:bg-slate-700 text-slate-500 hover:text-primary'}`}
                                                                                                    title={isHActive ? 'Modo Jerárquico activo (Año -> Mes -> Fechas). Clic para cambiar a Lista Plana' : 'Modo Lista Plana activo. Clic para activar Jerarquía'}
                                                                                                >
                                                                                                    <span className="material-symbols-outlined text-[11px]">account_tree</span>
                                                                                                    <span>{isHActive ? 'Jerarquía' : 'Plano'}</span>
                                                                                                </button>
                                                                                            );
                                                                                        })()}

                                                                                        <button 
                                                                                            onClick={() => handleRemoveFieldFromBucket(currentWidget.id, bDef.id, idx)}
                                                                                            className="text-slate-400 hover:text-red-500 material-symbols-outlined text-[14px] transition-colors cursor-pointer"
                                                                                            title="Quitar campo"
                                                                                        >
                                                                                            close
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                );
                                            })()}

                                            {/* CÁLCULOS Y DAX EN EL PANEL DE VISUALIZACIONES */}
                                            <div className="space-y-3 border-t border-slate-100 dark:border-slate-700/50 pt-3">
                                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">Transformaciones DAX</span>

                                                {/* Acumulado */}
                                                <div className="space-y-0.5">
                                                    <span className="text-[8px] font-bold text-slate-500 uppercase block">Suma Acumulada</span>
                                                    <select
                                                        value={runningTotalCol}
                                                        onChange={e => setRunningTotalCol(e.target.value)}
                                                        className="w-full p-1.5 bg-slate-50 dark:bg-slate-900 border rounded-lg text-[10px]"
                                                    >
                                                        <option value="">Ninguna</option>
                                                        {(selectedColumns[activeEditingComponent] || DEFAULT_COLUMNS[activeEditingComponent] || []).map(col => (
                                                            <option key={col} value={col}>{col}</option>
                                                        ))}
                                                    </select>
                                                </div>

                                                {/* Promedio Móvil */}
                                                <div className="space-y-0.5">
                                                    <span className="text-[8px] font-bold text-slate-500 uppercase block">Promedio Móvil</span>
                                                    <div className="flex gap-1.5">
                                                        <select
                                                            value={movingAverageCol}
                                                            onChange={e => setMovingAverageCol(e.target.value)}
                                                            className="flex-1 p-1.5 bg-slate-50 dark:bg-slate-900 border rounded-lg text-[10px]"
                                                        >
                                                            <option value="">Ninguna</option>
                                                            {(selectedColumns[activeEditingComponent] || DEFAULT_COLUMNS[activeEditingComponent] || []).map(col => (
                                                                <option key={col} value={col}>{col}</option>
                                                            ))}
                                                        </select>
                                                        {movingAverageCol && (
                                                            <input
                                                                type="number"
                                                                min={2}
                                                                max={12}
                                                                value={movingAveragePeriod}
                                                                onChange={e => setMovingAveragePeriod(Math.max(2, parseInt(e.target.value) || 2))}
                                                                className="w-12 p-1.5 bg-slate-50 dark:bg-slate-900 border rounded-lg text-[10px] text-center"
                                                                title="Período N"
                                                            />
                                                        )}
                                                    </div>
                                                </div>

                                                {/* FILTROS LÓGICOS A NIVEL DE GRÁFICO (VISUAL-LEVEL FILTERS) */}
                                                {(() => {
                                                    const activeWidget = selectedWidgetId ? widgets.find(w => w.id === selectedWidgetId) : null;
                                                    if (!activeWidget) return null;

                                                    const activeSourceRows = getComponentRows(activeWidget.componentId);

                                                    const processedRows = processCalculations(activeWidget.componentId, activeSourceRows);
                                                    const activeCols = selectedColumns[activeWidget.componentId] || DEFAULT_COLUMNS[activeWidget.componentId] || [];
                                                    const currentFilters = activeWidget.logicalFilters || [];

                                                    const defaultTarget = filterTargetField || activeWidget.axisFields?.[0]?.fieldKey || activeWidget.valueFields?.[0]?.fieldKey || activeCols[0] || '';

                                                    const uniqueColValues = Array.from(new Set(processedRows.map(r => {
                                                        const v = r[defaultTarget];
                                                        return (v !== undefined && v !== null) ? String(v).trim() : '(Vacío)';
                                                    }))).sort();

                                                    return (
                                                        <div className="space-y-2 border-t border-slate-100 dark:border-slate-700/50 pt-2.5">
                                                            <div className="flex justify-between items-center">
                                                                <span className="text-[9px] font-black uppercase text-primary tracking-wider flex items-center gap-1">
                                                                    <span className="material-symbols-outlined text-[13px]">filter_alt</span>
                                                                    Filtros del Gráfico
                                                                </span>
                                                                {currentFilters.length > 0 && (
                                                                    <span className="text-[8px] bg-primary/10 text-primary font-bold px-1.5 py-0.2 rounded-full">
                                                                        {currentFilters.length} {currentFilters.length === 1 ? 'filtro' : 'filtros'}
                                                                    </span>
                                                                )}
                                                            </div>

                                                            {currentFilters.map((rule, idx) => {
                                                                let label = `${rule.field}`;
                                                                if (rule.filterType === 'basic') {
                                                                    if (rule.excludedValues && rule.excludedValues.length > 0) {
                                                                        label += ` != [${rule.excludedValues.join(', ')}]`;
                                                                    } else if (Array.isArray(rule.value)) {
                                                                        label += ` = [${rule.value.join(', ')}]`;
                                                                    }
                                                                } else {
                                                                    const opMap: Record<string, string> = {
                                                                        equals: '=',
                                                                        not_equals: '!=',
                                                                        contains: 'contiene',
                                                                        not_contains: 'no contiene',
                                                                        greater: '>',
                                                                        less: '<',
                                                                        is_null: 'es nulo / vacío',
                                                                        is_not_null: 'no está vacío'
                                                                    };
                                                                    label += ` ${opMap[rule.operator] || rule.operator} "${rule.value}"`;
                                                                }

                                                                return (
                                                                    <div 
                                                                        key={rule.id || idx} 
                                                                        className="flex justify-between items-center p-1.5 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 text-[9px] shadow-sm animate-fade-in"
                                                                    >
                                                                        <div className="flex items-center gap-1 truncate flex-1 pr-1" title={label}>
                                                                            <span className="material-symbols-outlined text-[12px] text-primary">filter_list</span>
                                                                            <span className="truncate font-bold text-slate-700 dark:text-slate-200">{label}</span>
                                                                        </div>
                                                                        <button 
                                                                            onClick={() => {
                                                                                setWidgets(prev => prev.map(w => {
                                                                                    if (w.id !== activeWidget.id) return w;
                                                                                    return {
                                                                                        ...w,
                                                                                        logicalFilters: (w.logicalFilters || []).filter((_, i) => i !== idx)
                                                                                    };
                                                                                }));
                                                                            }} 
                                                                            className="text-red-500 hover:text-red-700 font-bold p-0.5 hover:bg-red-50 dark:hover:bg-red-950/30 rounded cursor-pointer transition-colors"
                                                                            title="Eliminar filtro"
                                                                        >
                                                                            <span className="material-symbols-outlined text-[13px]">close</span>
                                                                        </button>
                                                                    </div>
                                                                );
                                                            })}

                                                            {isAddingVisualFilter ? (
                                                                <div className="p-2.5 bg-slate-50 dark:bg-slate-900 rounded-xl border border-primary/40 space-y-2 animate-fade-in shadow-inner">
                                                                    <div className="flex justify-between items-center pb-1 border-b border-slate-200 dark:border-slate-800">
                                                                        <span className="text-[9px] font-black uppercase text-slate-700 dark:text-slate-200">
                                                                            Nuevo Filtro
                                                                        </span>
                                                                        <button 
                                                                            onClick={() => setIsAddingVisualFilter(false)}
                                                                            className="text-slate-400 hover:text-slate-600 material-symbols-outlined text-[14px]"
                                                                        >
                                                                            close
                                                                        </button>
                                                                    </div>

                                                                    <div className="space-y-0.5">
                                                                        <span className="text-[8px] font-bold text-slate-400 uppercase">Campo</span>
                                                                        <select
                                                                            value={defaultTarget}
                                                                            onChange={e => {
                                                                                setFilterTargetField(e.target.value);
                                                                                setFilterBasicChecked({});
                                                                            }}
                                                                            className="w-full p-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded text-[9px] font-bold text-slate-700 dark:text-slate-200"
                                                                        >
                                                                            {activeCols.map(col => (
                                                                                <option key={col} value={col}>{col}</option>
                                                                            ))}
                                                                        </select>
                                                                    </div>

                                                                    <div className="flex bg-slate-200 dark:bg-slate-800 p-0.5 rounded-lg text-[8px] font-bold">
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setFilterMode('basic')}
                                                                            className={`flex-1 py-0.5 rounded ${filterMode === 'basic' ? 'bg-white dark:bg-slate-700 text-primary shadow-xs' : 'text-slate-500'}`}
                                                                        >
                                                                            Filtro Básico
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setFilterMode('advanced')}
                                                                            className={`flex-1 py-0.5 rounded ${filterMode === 'advanced' ? 'bg-white dark:bg-slate-700 text-primary shadow-xs' : 'text-slate-500'}`}
                                                                        >
                                                                            Avanzado
                                                                        </button>
                                                                    </div>

                                                                    {filterMode === 'basic' && (
                                                                        <div className="space-y-1">
                                                                            <div className="flex justify-between items-center text-[8px] text-slate-400">
                                                                                <span>Valores ({uniqueColValues.length})</span>
                                                                                <div className="flex gap-1.5">
                                                                                    <button 
                                                                                        type="button" 
                                                                                        onClick={() => {
                                                                                            const all: Record<string, boolean> = {};
                                                                                            uniqueColValues.forEach(v => all[v] = true);
                                                                                            setFilterBasicChecked(all);
                                                                                        }}
                                                                                        className="text-primary hover:underline font-bold"
                                                                                    >
                                                                                        Todos
                                                                                    </button>
                                                                                    <button 
                                                                                        type="button" 
                                                                                        onClick={() => setFilterBasicChecked({})}
                                                                                        className="text-slate-400 hover:underline font-bold"
                                                                                    >
                                                                                        Limpiar
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                            <div className="max-h-28 overflow-y-auto space-y-1 p-1 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 text-[9px]">
                                                                                {uniqueColValues.map(uVal => {
                                                                                    const isChecked = filterBasicChecked[uVal] ?? true;
                                                                                    return (
                                                                                        <label key={uVal} className="flex items-center gap-1.5 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 p-0.5 rounded select-none">
                                                                                            <input 
                                                                                                type="checkbox"
                                                                                                checked={isChecked}
                                                                                                onChange={e => {
                                                                                                    setFilterBasicChecked(prev => ({
                                                                                                        ...prev,
                                                                                                        [uVal]: e.target.checked
                                                                                                    }));
                                                                                                }}
                                                                                                className="rounded text-primary h-3 w-3 cursor-pointer"
                                                                                            />
                                                                                            <span className="truncate text-slate-700 dark:text-slate-300 font-medium">
                                                                                                {uVal}
                                                                                            </span>
                                                                                        </label>
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        </div>
                                                                    )}

                                                                    {filterMode === 'advanced' && (
                                                                        <div className="space-y-1.5">
                                                                            <div className="space-y-0.5">
                                                                                <span className="text-[8px] font-bold text-slate-400 uppercase">Regla</span>
                                                                                <select
                                                                                    value={filterAdvancedOp}
                                                                                    onChange={e => setFilterAdvancedOp(e.target.value as any)}
                                                                                    className="w-full p-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded text-[9px] font-bold text-slate-700 dark:text-slate-200"
                                                                                >
                                                                                    <option value="not_equals">No es igual a</option>
                                                                                    <option value="equals">Es igual a</option>
                                                                                    <option value="not_contains">No contiene</option>
                                                                                    <option value="contains">Contiene</option>
                                                                                    <option value="is_not_null">No está vacío / No es nulo</option>
                                                                                    <option value="is_null">Está vacío / Es nulo</option>
                                                                                    <option value="greater">Es mayor que (&gt;)</option>
                                                                                    <option value="less">Es menor que (&lt;)</option>
                                                                                </select>
                                                                            </div>

                                                                            {filterAdvancedOp !== 'is_null' && filterAdvancedOp !== 'is_not_null' && (
                                                                                <div className="space-y-0.5">
                                                                                    <span className="text-[8px] font-bold text-slate-400 uppercase">Valor a comparar</span>
                                                                                    <input
                                                                                        type="text"
                                                                                        value={filterAdvancedVal}
                                                                                        onChange={e => setFilterAdvancedVal(e.target.value)}
                                                                                        placeholder="Ej: -"
                                                                                        className="w-full p-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded text-[9px] text-slate-700 dark:text-slate-200"
                                                                                    />
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    )}

                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            const field = defaultTarget;
                                                                            let newRule: LogicalFilterRule;

                                                                            if (filterMode === 'basic') {
                                                                                const excluded = uniqueColValues.filter(v => (filterBasicChecked[v] === false));
                                                                                newRule = {
                                                                                    id: `lf-${Date.now()}`,
                                                                                    field,
                                                                                    filterType: 'basic',
                                                                                    operator: 'in',
                                                                                    value: uniqueColValues.filter(v => (filterBasicChecked[v] ?? true)),
                                                                                    excludedValues: excluded
                                                                                };
                                                                            } else {
                                                                                newRule = {
                                                                                    id: `lf-${Date.now()}`,
                                                                                    field,
                                                                                    filterType: 'advanced',
                                                                                    operator: filterAdvancedOp,
                                                                                    value: filterAdvancedVal
                                                                                };
                                                                            }

                                                                            setWidgets(prev => prev.map(w => {
                                                                                if (w.id !== activeWidget.id) return w;
                                                                                return {
                                                                                    ...w,
                                                                                    logicalFilters: [...(w.logicalFilters || []), newRule]
                                                                                };
                                                                            }));

                                                                            setIsAddingVisualFilter(false);
                                                                        }}
                                                                        className="w-full py-1 bg-primary text-white text-[9px] font-black rounded-lg shadow-sm hover:bg-primary/90 transition-all cursor-pointer"
                                                                    >
                                                                        Aplicar Filtro al Gráfico
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        setIsAddingVisualFilter(true);
                                                                        setFilterTargetField(activeWidget.axisFields?.[0]?.fieldKey || activeWidget.valueFields?.[0]?.fieldKey || activeCols[0] || '');
                                                                    }}
                                                                    className="w-full text-center py-1.5 border border-dashed border-primary/40 rounded-xl text-[9px] font-black text-primary hover:bg-primary/5 transition-all flex items-center justify-center gap-1 cursor-pointer"
                                                                >
                                                                    <span className="material-symbols-outlined text-[13px]">add</span>
                                                                    + Añadir Filtro
                                                                </button>
                                                            )}
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        </>
                                    )}

                                    {/* PESTAÑA 2: FORMATO VISUAL (PINCEL) */}
                                    {visPanelMainTab === 'format' && (() => {
                                        const currentWidget = selectedWidgetId ? widgets.find(w => w.id === selectedWidgetId) : null;
                                        if (!currentWidget) {
                                            return (
                                                <div className="p-6 bg-slate-50 dark:bg-slate-900/40 rounded-2xl border border-dashed border-slate-200 dark:border-slate-700/80 text-center space-y-2">
                                                    <span className="material-symbols-outlined text-[28px] text-slate-300 dark:text-slate-600 block mx-auto">brush</span>
                                                    <p className="font-bold text-[11px] text-slate-700 dark:text-slate-300">Ningún gráfico seleccionado</p>
                                                    <p className="text-[9px] text-slate-400 leading-relaxed">
                                                        Selecciona un gráfico en el lienzo para personalizar sus colores, etiquetas, ejes, título y fondos.
                                                    </p>
                                                </div>
                                            );
                                        }

                                        const style = currentWidget.style || {};

                                        const updateStyle = (key: keyof WidgetStyleConfig, value: any) => {
                                            setWidgets(prev => prev.map(w => {
                                                if (w.id !== currentWidget.id) return w;
                                                return {
                                                    ...w,
                                                    style: {
                                                        ...(w.style || {}),
                                                        [key]: value
                                                    }
                                                };
                                            }));
                                        };

                                        return (
                                            <div className="space-y-3 animate-fade-in">
                                                {/* Sub-pestañas: [Objeto visual] y [General] */}
                                                <div className="flex bg-slate-100 dark:bg-slate-900 p-0.5 rounded-lg text-[9px] font-black">
                                                    <button
                                                        type="button"
                                                        onClick={() => setVisFormatSubTab('visual')}
                                                        className={`flex-1 py-1 rounded ${visFormatSubTab === 'visual' ? 'bg-white dark:bg-slate-800 text-primary shadow-2xs' : 'text-slate-500'}`}
                                                    >
                                                        Objeto visual
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setVisFormatSubTab('general')}
                                                        className={`flex-1 py-1 rounded ${visFormatSubTab === 'general' ? 'bg-white dark:bg-slate-800 text-primary shadow-2xs' : 'text-slate-500'}`}
                                                    >
                                                        General
                                                    </button>
                                                </div>

                                                {/* 1. SUB-PESTAÑA: OBJETO VISUAL */}
                                                {visFormatSubTab === 'visual' && (
                                                    <div className="space-y-2">
                                                        {/* Acordeón: Configuración de Segmentación (Slicer) */}
                                                        {currentWidget.type === 'slicer' && (
                                                            <details open className="group bg-slate-50 dark:bg-slate-900/60 rounded-xl border border-slate-200 dark:border-slate-700/80">
                                                                <summary className="p-2.5 flex items-center justify-between cursor-pointer select-none font-bold text-[10px] text-slate-700 dark:text-slate-200">
                                                                    <span className="flex items-center gap-1.5">
                                                                        <span className="material-symbols-outlined text-[14px] text-primary">view_list</span>
                                                                        Tipo de Segmentación
                                                                    </span>
                                                                </summary>
                                                                <div className="p-2.5 pt-1 border-t border-slate-100 dark:border-slate-800 space-y-2">
                                                                    <div className="space-y-0.5">
                                                                        <span className="text-[8px] font-bold text-slate-400 uppercase">Estilo Visual</span>
                                                                        <select
                                                                            value={style.slicerVisualMode || 'dropdown'}
                                                                            onChange={e => updateStyle('slicerVisualMode', e.target.value as any)}
                                                                            className="w-full p-1.5 bg-white dark:bg-slate-800 border rounded text-[9px] font-bold text-slate-700 dark:text-slate-200"
                                                                        >
                                                                            <option value="checkbox">Casillas de verificación (Checkboxes)</option>
                                                                            <option value="dropdown">Lista desplegable (Dropdown)</option>
                                                                            <option value="pills">Botones / Pills (Selección rápida)</option>
                                                                        </select>
                                                                    </div>

                                                                    {/* Orden Jerárquico de Fechas (Año -> Mes -> Fechas) */}
                                                                    <div className="pt-2 border-t border-slate-100 dark:border-slate-800/80 space-y-1">
                                                                        <label className="flex items-center justify-between cursor-pointer select-none text-[9px] font-bold text-slate-700 dark:text-slate-200 hover:text-primary">
                                                                            <div className="flex items-center gap-1.5">
                                                                                <span className="material-symbols-outlined text-[13px] text-primary">account_tree</span>
                                                                                <span>Jerarquía de Fechas (Año &gt; Mes &gt; Fechas)</span>
                                                                            </div>
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={style.slicerDateHierarchy !== false}
                                                                                onChange={e => updateStyle('slicerDateHierarchy', e.target.checked)}
                                                                                className="rounded text-primary h-3.5 w-3.5 cursor-pointer"
                                                                            />
                                                                        </label>
                                                                        <p className="text-[8px] text-slate-400 leading-tight">
                                                                            Agrupa las opciones en niveles desplegables o cascada (Año &gt; Mes &gt; Fechas) en vez de lista plana.
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </details>
                                                        )}

                                                        {/* Acordeón: Tamaño y Tipografía de Tarjeta KPI */}
                                                        {(currentWidget.type === 'kpi-card' || currentWidget.type === 'kpi-multi') && (
                                                            <details open className="group bg-slate-50 dark:bg-slate-900/60 rounded-xl border border-slate-200 dark:border-slate-700/80">
                                                                <summary className="p-2.5 flex items-center justify-between cursor-pointer select-none font-bold text-[10px] text-slate-700 dark:text-slate-200">
                                                                    <span className="flex items-center gap-1.5">
                                                                        <span className="material-symbols-outlined text-[14px] text-primary">format_size</span>
                                                                        Tamaño de Contenido (Tarjeta)
                                                                    </span>
                                                                </summary>
                                                                <div className="p-2.5 pt-1 border-t border-slate-100 dark:border-slate-800 space-y-2.5">
                                                                    <div className="space-y-0.5">
                                                                        <div className="flex justify-between items-center text-[8px] font-bold text-slate-400 uppercase">
                                                                            <span>Tamaño del Valor Principal</span>
                                                                            <span className="font-mono text-primary">{style.kpiValueFontSize || 30}px</span>
                                                                        </div>
                                                                        <input
                                                                            type="range"
                                                                            min={16}
                                                                            max={64}
                                                                            value={style.kpiValueFontSize || 30}
                                                                            onChange={e => updateStyle('kpiValueFontSize', parseInt(e.target.value, 10))}
                                                                            className="w-full h-2 cursor-pointer"
                                                                        />
                                                                    </div>

                                                                    <div className="space-y-0.5">
                                                                        <div className="flex justify-between items-center text-[8px] font-bold text-slate-400 uppercase">
                                                                            <span>Tamaño de la Etiqueta</span>
                                                                            <span className="font-mono text-primary">{style.kpiLabelFontSize || 10}px</span>
                                                                        </div>
                                                                        <input
                                                                            type="range"
                                                                            min={8}
                                                                            max={18}
                                                                            value={style.kpiLabelFontSize || 10}
                                                                            onChange={e => updateStyle('kpiLabelFontSize', parseInt(e.target.value, 10))}
                                                                            className="w-full h-2 cursor-pointer"
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </details>
                                                        )}

                                                        {/* Acordeón: Fila de Totales para Tabla */}
                                                        {currentWidget.type === 'table' && (
                                                            <details open className="group bg-slate-50 dark:bg-slate-900/60 rounded-xl border border-slate-200 dark:border-slate-700/80">
                                                                <summary className="p-2.5 flex items-center justify-between cursor-pointer select-none font-bold text-[10px] text-slate-700 dark:text-slate-200">
                                                                    <span className="flex items-center gap-1.5">
                                                                        <span className="material-symbols-outlined text-[14px] text-primary">functions</span>
                                                                        Fila de Totales
                                                                    </span>
                                                                    <label className="flex items-center gap-1 cursor-pointer" onClick={e => e.stopPropagation()}>
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={style.showTableTotals !== false}
                                                                            onChange={e => updateStyle('showTableTotals', e.target.checked)}
                                                                            className="rounded text-primary h-3.5 w-3.5"
                                                                        />
                                                                    </label>
                                                                </summary>
                                                                <div className="p-2.5 pt-1 border-t border-slate-100 dark:border-slate-800">
                                                                    <p className="text-[8px] text-slate-400">
                                                                        Calcula automáticamente la suma o recuento total al pie de cada columna.
                                                                    </p>
                                                                </div>
                                                            </details>
                                                        )}

                                                        {/* Acordeón: Totales y Subtotales para Matriz */}
                                                        {currentWidget.type === 'matrix' && (
                                                            <details open className="group bg-slate-50 dark:bg-slate-900/60 rounded-xl border border-slate-200 dark:border-slate-700/80">
                                                                <summary className="p-2.5 flex items-center justify-between cursor-pointer select-none font-bold text-[10px] text-slate-700 dark:text-slate-200">
                                                                    <span className="flex items-center gap-1.5">
                                                                        <span className="material-symbols-outlined text-[14px] text-primary">calculate</span>
                                                                        Totales Generales
                                                                    </span>
                                                                    <label className="flex items-center gap-1 cursor-pointer" onClick={e => e.stopPropagation()}>
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={style.showMatrixGrandTotals !== false}
                                                                            onChange={e => updateStyle('showMatrixGrandTotals', e.target.checked)}
                                                                            className="rounded text-primary h-3.5 w-3.5"
                                                                        />
                                                                    </label>
                                                                </summary>
                                                                <div className="p-2.5 pt-1 border-t border-slate-100 dark:border-slate-800 space-y-1.5">
                                                                    <p className="text-[8px] text-slate-400">
                                                                        Muestra la fila y columna de total general calculando las métricas agregadas.
                                                                    </p>
                                                                </div>
                                                            </details>
                                                        )}

                                                        {/* Acordeón: Meta / Target de KPI */}
                                                        {currentWidget.type === 'kpi-multi' && (
                                                            <details open className="group bg-slate-50 dark:bg-slate-900/60 rounded-xl border border-slate-200 dark:border-slate-700/80">
                                                                <summary className="p-2.5 flex items-center justify-between cursor-pointer select-none font-bold text-[10px] text-slate-700 dark:text-slate-200">
                                                                    <span className="flex items-center gap-1.5">
                                                                        <span className="material-symbols-outlined text-[14px] text-primary">flag</span>
                                                                        Meta / Línea Base
                                                                    </span>
                                                                </summary>
                                                                <div className="p-2.5 pt-1 border-t border-slate-100 dark:border-slate-800 space-y-2">
                                                                    <div className="space-y-0.5">
                                                                        <span className="text-[8px] font-bold text-slate-400 uppercase">Valor Meta Numérico Fijo</span>
                                                                        <input
                                                                            type="number"
                                                                            value={style.kpiTargetValue !== undefined ? style.kpiTargetValue : ''}
                                                                            onChange={e => updateStyle('kpiTargetValue', e.target.value ? parseFloat(e.target.value) : undefined)}
                                                                            placeholder="Ej: 85 (o arrastra campo a Meta)"
                                                                            className="w-full p-1 bg-white dark:bg-slate-800 border rounded text-[9px]"
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </details>
                                                        )}

                                                        {/* Acordeón: Leyenda */}
                                                        <details open className="group bg-slate-50 dark:bg-slate-900/60 rounded-xl border border-slate-200 dark:border-slate-700/80">
                                                            <summary className="p-2.5 flex items-center justify-between cursor-pointer select-none font-bold text-[10px] text-slate-700 dark:text-slate-200">
                                                                <span className="flex items-center gap-1.5">
                                                                    <span className="material-symbols-outlined text-[14px] text-primary">legend_toggle</span>
                                                                    Leyenda
                                                                </span>
                                                                <label className="flex items-center gap-1 cursor-pointer" onClick={e => e.stopPropagation()}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={style.showLegend !== false}
                                                                        onChange={e => updateStyle('showLegend', e.target.checked)}
                                                                        className="rounded text-primary h-3.5 w-3.5"
                                                                    />
                                                                </label>
                                                            </summary>
                                                            {style.showLegend !== false && (
                                                                <div className="p-2.5 pt-0 border-t border-slate-100 dark:border-slate-800 space-y-2 mt-1">
                                                                    <div className="space-y-0.5">
                                                                        <span className="text-[8px] font-bold text-slate-400 uppercase">Posición</span>
                                                                        <select
                                                                            value={style.legendPosition || 'bottom'}
                                                                            onChange={e => updateStyle('legendPosition', e.target.value as any)}
                                                                            className="w-full p-1 bg-white dark:bg-slate-800 border rounded text-[9px]"
                                                                        >
                                                                            <option value="top">Arriba</option>
                                                                            <option value="bottom">Abajo</option>
                                                                            <option value="left">Izquierda</option>
                                                                            <option value="right">Derecha</option>
                                                                        </select>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </details>

                                                        {/* Acordeón: Etiquetas de Datos */}
                                                        <details open className="group bg-slate-50 dark:bg-slate-900/60 rounded-xl border border-slate-200 dark:border-slate-700/80">
                                                            <summary className="p-2.5 flex items-center justify-between cursor-pointer select-none font-bold text-[10px] text-slate-700 dark:text-slate-200">
                                                                <span className="flex items-center gap-1.5">
                                                                    <span className="material-symbols-outlined text-[14px] text-primary">label</span>
                                                                    Etiquetas de Datos
                                                                </span>
                                                                <label className="flex items-center gap-1 cursor-pointer" onClick={e => e.stopPropagation()}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={style.showDataLabels === true}
                                                                        onChange={e => updateStyle('showDataLabels', e.target.checked)}
                                                                        className="rounded text-primary h-3.5 w-3.5"
                                                                    />
                                                                </label>
                                                            </summary>
                                                            {style.showDataLabels === true && (
                                                                <div className="p-2.5 pt-1 border-t border-slate-100 dark:border-slate-800 space-y-2">
                                                                    {/* Posición de la etiqueta */}
                                                                    <div className="space-y-0.5">
                                                                        <span className="text-[8px] font-bold text-slate-400 uppercase">Posición de la etiqueta</span>
                                                                        <select
                                                                            value={style.dataLabelPosition || 'outside'}
                                                                            onChange={e => updateStyle('dataLabelPosition', e.target.value as any)}
                                                                            className="w-full p-1 bg-white dark:bg-slate-800 border rounded text-[9px] font-medium text-slate-700 dark:text-slate-200 cursor-pointer"
                                                                        >
                                                                            <option value="outside">Externa (Con líneas conectoras cortas)</option>
                                                                            <option value="inside">Interna (Dentro de las rebanadas / Ideal 1/3)</option>
                                                                        </select>
                                                                    </div>

                                                                    {/* Formato del Valor (Inferencia o Manual) */}
                                                                    <div className="space-y-0.5">
                                                                        <span className="text-[8px] font-bold text-slate-400 uppercase">Formato del Valor</span>
                                                                        <select
                                                                            value={style.dataLabelFormat || 'auto'}
                                                                            onChange={e => updateStyle('dataLabelFormat', e.target.value as any)}
                                                                            className="w-full p-1 bg-white dark:bg-slate-800 border rounded text-[9px] font-medium text-slate-700 dark:text-slate-200 cursor-pointer"
                                                                        >
                                                                            <option value="auto">Automático (Inferencia inteligente)</option>
                                                                            <option value="integer">Número Entero (Ej: 32)</option>
                                                                            <option value="decimal">Decimal (Ej: 32.00)</option>
                                                                            <option value="percent">Porcentaje (Ej: 32%)</option>
                                                                            <option value="currency">Moneda (Ej: $32)</option>
                                                                        </select>
                                                                    </div>

                                                                    {/* Contenido de la etiqueta */}
                                                                    <div className="space-y-0.5">
                                                                        <span className="text-[8px] font-bold text-slate-400 uppercase">Contenido de la etiqueta</span>
                                                                        <select
                                                                            value={style.dataLabelContent || 'category_value'}
                                                                            onChange={e => updateStyle('dataLabelContent', e.target.value as any)}
                                                                            className="w-full p-1 bg-white dark:bg-slate-800 border rounded text-[9px] font-medium text-slate-700 dark:text-slate-200 cursor-pointer"
                                                                        >
                                                                            <option value="category_value">Categoría y Valor de Datos (Ej: Item: 82.05%)</option>
                                                                            <option value="value">Solo Valor de Datos (Ej: 82.05%)</option>
                                                                            <option value="percent_total">Porcentaje del Total (Ej: 25%)</option>
                                                                            <option value="both">Todo (Categoría, Valor y % Total)</option>
                                                                        </select>
                                                                    </div>

                                                                    {/* Decimales */}
                                                                    <div className="flex items-center justify-between">
                                                                        <span className="text-[9px] font-bold text-slate-600 dark:text-slate-300">Posiciones decimales</span>
                                                                        <input
                                                                            type="number"
                                                                            min={0}
                                                                            max={4}
                                                                            value={style.dataLabelDecimals !== undefined ? style.dataLabelDecimals : 2}
                                                                            onChange={e => updateStyle('dataLabelDecimals', Math.max(0, Math.min(4, parseInt(e.target.value) || 0)))}
                                                                            className="w-14 p-1 bg-white dark:bg-slate-800 border rounded text-[9px] text-center font-bold"
                                                                        />
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </details>

                                                        {/* Acordeón: Ejes (Solo para gráficos de Barras, Líneas, Áreas, Dispersión) */}
                                                        {currentWidget.type !== 'pie' && currentWidget.type !== 'radar' && currentWidget.type !== 'kpi-card' && currentWidget.type !== 'gauge' && currentWidget.type !== 'matrix' && (
                                                            <details open className="group bg-slate-50 dark:bg-slate-900/60 rounded-xl border border-slate-200 dark:border-slate-700/80">
                                                                <summary className="p-2.5 flex items-center justify-between cursor-pointer select-none font-bold text-[10px] text-slate-700 dark:text-slate-200">
                                                                    <span className="flex items-center gap-1.5">
                                                                        <span className="material-symbols-outlined text-[14px] text-primary">analytics</span>
                                                                        Ejes (X / Y)
                                                                    </span>
                                                                </summary>
                                                                <div className="p-2.5 pt-1 border-t border-slate-100 dark:border-slate-800 space-y-2">
                                                                    <label className="flex items-center justify-between cursor-pointer text-[9px] font-bold text-slate-600 dark:text-slate-300">
                                                                        <span>Eje X (Categorías / Horizontal)</span>
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={style.showXAxis !== false}
                                                                            onChange={e => updateStyle('showXAxis', e.target.checked)}
                                                                            className="rounded text-primary h-3.5 w-3.5"
                                                                        />
                                                                    </label>
                                                                    <label className="flex items-center justify-between cursor-pointer text-[9px] font-bold text-slate-600 dark:text-slate-300">
                                                                        <span>Eje Y (Valores / Vertical)</span>
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={style.showYAxis !== false}
                                                                            onChange={e => updateStyle('showYAxis', e.target.checked)}
                                                                            className="rounded text-primary h-3.5 w-3.5"
                                                                        />
                                                                    </label>
                                                                </div>
                                                            </details>
                                                        )}

                                                        {/* Acordeón: Colores / Segmentos */}
                                                        <details open className="group bg-slate-50 dark:bg-slate-900/60 rounded-xl border border-slate-200 dark:border-slate-700/80">
                                                            <summary className="p-2.5 flex items-center justify-between cursor-pointer select-none font-bold text-[10px] text-slate-700 dark:text-slate-200">
                                                                <span className="flex items-center gap-1.5">
                                                                    <span className="material-symbols-outlined text-[14px] text-primary">palette</span>
                                                                    Colores / Segmentos
                                                                </span>
                                                            </summary>
                                                            <div className="p-2.5 pt-1 border-t border-slate-100 dark:border-slate-800 space-y-2">
                                                                <div className="flex items-center justify-between">
                                                                    <span className="text-[9px] font-bold text-slate-600 dark:text-slate-300">Color Primario</span>
                                                                    <div className="flex items-center gap-1.5">
                                                                        <input
                                                                            type="color"
                                                                            value={style.primaryColor || '#3b82f6'}
                                                                            onChange={e => updateStyle('primaryColor', e.target.value)}
                                                                            className="w-6 h-6 rounded cursor-pointer border-none bg-transparent"
                                                                        />
                                                                        <span className="text-[8px] font-mono text-slate-400 uppercase">{style.primaryColor || '#3b82f6'}</span>
                                                                    </div>
                                                                </div>

                                                                {/* Paletas de color predefinidas */}
                                                                <div className="space-y-1">
                                                                    <span className="text-[8px] font-bold text-slate-400 uppercase">Paletas Sugeridas</span>
                                                                    <div className="grid grid-cols-3 gap-1">
                                                                        {[
                                                                            { name: 'Oceánico', colors: ['#3b82f6', '#06b6d4', '#0284c7', '#60a5fa', '#38bdf8'] },
                                                                            { name: 'Esmeralda', colors: ['#10b981', '#059669', '#34d399', '#6ee7b7', '#14b8a6'] },
                                                                            { name: 'Cálido', colors: ['#f59e0b', '#ef4444', '#f97316', '#fbbf24', '#f87171'] },
                                                                            { name: 'Púrpura', colors: ['#8b5cf6', '#6366f1', '#a855f7', '#d946ef', '#c084fc'] },
                                                                            { name: 'Corporativo', colors: ['#1e293b', '#3b82f6', '#10b981', '#f59e0b', '#ef4444'] },
                                                                            { name: 'Monocromo', colors: ['#334155', '#475569', '#64748b', '#94a3b8', '#cbd5e1'] }
                                                                        ].map(pal => (
                                                                            <button
                                                                                key={pal.name}
                                                                                type="button"
                                                                                onClick={() => updateStyle('paletteColors', pal.colors)}
                                                                                className="p-1 rounded border border-slate-200 dark:border-slate-700 hover:border-primary transition-colors flex flex-col items-center gap-0.5 bg-white dark:bg-slate-800"
                                                                            >
                                                                                <div className="flex w-full h-2 rounded overflow-hidden">
                                                                                    {pal.colors.map((c, i) => (
                                                                                        <div key={i} style={{ backgroundColor: c }} className="flex-1" />
                                                                                    ))}
                                                                                </div>
                                                                                <span className="text-[7px] font-bold text-slate-500 truncate">{pal.name}</span>
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </details>
                                                    </div>
                                                )}

                                                {/* 2. SUB-PESTAÑA: GENERAL */}
                                                {visFormatSubTab === 'general' && (
                                                    <div className="space-y-2">
                                                        {/* Acordeón: Título */}
                                                        <details open className="group bg-slate-50 dark:bg-slate-900/60 rounded-xl border border-slate-200 dark:border-slate-700/80">
                                                            <summary className="p-2.5 flex items-center justify-between cursor-pointer select-none font-bold text-[10px] text-slate-700 dark:text-slate-200">
                                                                <span className="flex items-center gap-1.5">
                                                                    <span className="material-symbols-outlined text-[14px] text-primary">title</span>
                                                                    Título
                                                                </span>
                                                                <label className="flex items-center gap-1 cursor-pointer" onClick={e => e.stopPropagation()}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={style.showTitle !== false}
                                                                        onChange={e => updateStyle('showTitle', e.target.checked)}
                                                                        className="rounded text-primary h-3.5 w-3.5"
                                                                    />
                                                                </label>
                                                            </summary>
                                                            {style.showTitle !== false && (
                                                                <div className="p-2.5 pt-1 border-t border-slate-100 dark:border-slate-800 space-y-2">
                                                                    <div className="space-y-0.5">
                                                                        <span className="text-[8px] font-bold text-slate-400 uppercase">Texto del Título</span>
                                                                        <input
                                                                            type="text"
                                                                            value={style.customTitle || ''}
                                                                            onChange={e => updateStyle('customTitle', e.target.value)}
                                                                            placeholder="Título automático del gráfico"
                                                                            className="w-full p-1 bg-white dark:bg-slate-800 border rounded text-[9px]"
                                                                        />
                                                                    </div>

                                                                    <div className="grid grid-cols-2 gap-2">
                                                                        <div className="space-y-0.5">
                                                                            <span className="text-[8px] font-bold text-slate-400 uppercase">Color de Texto</span>
                                                                            <input
                                                                                type="color"
                                                                                value={style.titleColor || '#1e293b'}
                                                                                onChange={e => updateStyle('titleColor', e.target.value)}
                                                                                className="w-full h-6 rounded cursor-pointer border-none bg-transparent"
                                                                            />
                                                                        </div>
                                                                        <div className="space-y-0.5">
                                                                            <span className="text-[8px] font-bold text-slate-400 uppercase">Tamaño ({style.titleFontSize || 11}px)</span>
                                                                            <input
                                                                                type="range"
                                                                                min={9}
                                                                                max={18}
                                                                                value={style.titleFontSize || 11}
                                                                                onChange={e => updateStyle('titleFontSize', parseInt(e.target.value))}
                                                                                className="w-full h-2 cursor-pointer"
                                                                            />
                                                                        </div>
                                                                    </div>

                                                                    <div className="space-y-0.5">
                                                                        <span className="text-[8px] font-bold text-slate-400 uppercase">Alineación</span>
                                                                        <div className="flex bg-white dark:bg-slate-800 p-0.5 rounded border border-slate-200 dark:border-slate-700">
                                                                            {(['left', 'center', 'right'] as const).map(align => (
                                                                                <button
                                                                                    key={align}
                                                                                    type="button"
                                                                                    onClick={() => updateStyle('titleAlignment', align)}
                                                                                    className={`flex-1 py-0.5 rounded text-[8px] font-black uppercase ${ (style.titleAlignment || 'left') === align ? 'bg-primary text-white' : 'text-slate-400' }`}
                                                                                >
                                                                                    {align === 'left' ? 'Izq' : align === 'center' ? 'Centro' : 'Der'}
                                                                                </button>
                                                                            ))}
                                                                        </div>
                                                                    </div>

                                                                    {/* Control de Espaciado y Línea Divisoria del Título */}
                                                                    <div className="pt-1 border-t border-slate-100 dark:border-slate-800/80 space-y-2">
                                                                        <div className="flex items-center justify-between">
                                                                            <span className="text-[8px] font-bold text-slate-600 dark:text-slate-300">Línea Divisoria de Título</span>
                                                                            <label className="flex items-center gap-1 cursor-pointer">
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={style.showTitleDivider !== false}
                                                                                    onChange={e => updateStyle('showTitleDivider', e.target.checked)}
                                                                                    className="rounded text-primary h-3.5 w-3.5"
                                                                                />
                                                                            </label>
                                                                        </div>

                                                                        <div className="grid grid-cols-2 gap-2">
                                                                            <div className="space-y-0.5">
                                                                                <span className="text-[8px] font-bold text-slate-400 uppercase">Margen Inferior ({style.titleMarginBottom !== undefined ? style.titleMarginBottom : 6}px)</span>
                                                                                <input
                                                                                    type="range"
                                                                                    min={0}
                                                                                    max={16}
                                                                                    value={style.titleMarginBottom !== undefined ? style.titleMarginBottom : 6}
                                                                                    onChange={e => updateStyle('titleMarginBottom', parseInt(e.target.value))}
                                                                                    className="w-full h-2 cursor-pointer"
                                                                                />
                                                                            </div>
                                                                            <div className="space-y-0.5">
                                                                                <span className="text-[8px] font-bold text-slate-400 uppercase">Relleno Título ({style.titlePadding !== undefined ? style.titlePadding : 4}px)</span>
                                                                                <input
                                                                                    type="range"
                                                                                    min={0}
                                                                                    max={12}
                                                                                    value={style.titlePadding !== undefined ? style.titlePadding : 4}
                                                                                    onChange={e => updateStyle('titlePadding', parseInt(e.target.value))}
                                                                                    className="w-full h-2 cursor-pointer"
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </details>

                                                        {/* Acordeón: Propiedades de Componente Específico */}
                                                        {currentWidget.type === 'image' && (
                                                            <details open className="group bg-slate-50 dark:bg-slate-900/60 rounded-xl border border-slate-200 dark:border-slate-700/80">
                                                                <summary className="p-2.5 flex items-center justify-between cursor-pointer select-none font-bold text-[10px] text-slate-700 dark:text-slate-200">
                                                                    <span className="flex items-center gap-1.5">
                                                                        <span className="material-symbols-outlined text-[14px] text-primary">photo_size_select_actual</span>
                                                                        Ajustes de Imagen
                                                                    </span>
                                                                </summary>
                                                                <div className="p-2.5 pt-1 border-t border-slate-100 dark:border-slate-800 space-y-2">
                                                                    <div className="space-y-0.5">
                                                                        <span className="text-[8px] font-bold text-slate-400 uppercase">Ajuste de Escala (Fit)</span>
                                                                        <select
                                                                            value={style.imageFit || 'cover'}
                                                                            onChange={e => updateStyle('imageFit', e.target.value as any)}
                                                                            className="w-full p-1 bg-white dark:bg-slate-800 border rounded text-[9px]"
                                                                        >
                                                                            <option value="cover">Rellenar (Cover)</option>
                                                                            <option value="contain">Ajustar (Contain)</option>
                                                                            <option value="fill">Estirar (Stretch/Fill)</option>
                                                                        </select>
                                                                    </div>
                                                                    <div className="space-y-0.5">
                                                                        <span className="text-[8px] font-bold text-slate-400 uppercase">Pie de Foto (Caption)</span>
                                                                        <input
                                                                            type="text"
                                                                            value={style.imageCaption || ''}
                                                                            onChange={e => updateStyle('imageCaption', e.target.value)}
                                                                            placeholder="Ej: Frente de trabajo Sector 2"
                                                                            className="w-full p-1 bg-white dark:bg-slate-800 border rounded text-[9px]"
                                                                        />
                                                                    </div>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setTargetImageWidgetId(currentWidget.id);
                                                                            setShowImagePickerModal(true);
                                                                        }}
                                                                        className="w-full py-1.5 bg-primary/10 hover:bg-primary/20 text-primary rounded-lg text-[9px] font-black flex items-center justify-center gap-1 transition-all"
                                                                    >
                                                                        <span className="material-symbols-outlined text-[14px]">photo_library</span>
                                                                        Seleccionar / Cambiar Imagen
                                                                    </button>
                                                                </div>
                                                            </details>
                                                        )}

                                                        {currentWidget.type === 'header-banner' && (
                                                            <details open className="group bg-slate-50 dark:bg-slate-900/60 rounded-xl border border-slate-200 dark:border-slate-700/80">
                                                                <summary className="p-2.5 flex items-center justify-between cursor-pointer select-none font-bold text-[10px] text-slate-700 dark:text-slate-200">
                                                                    <span className="flex items-center gap-1.5">
                                                                        <span className="material-symbols-outlined text-[14px] text-primary">splitscreen_top</span>
                                                                        Campos de Encabezado
                                                                    </span>
                                                                </summary>
                                                                <div className="p-2.5 pt-1 border-t border-slate-100 dark:border-slate-800 space-y-2">
                                                                    <div className="space-y-0.5">
                                                                        <span className="text-[8px] font-bold text-slate-400 uppercase">Título del Reporte</span>
                                                                        <input
                                                                            type="text"
                                                                            value={style.bannerTitle || ''}
                                                                            onChange={e => updateStyle('bannerTitle', e.target.value)}
                                                                            placeholder="INFORME DE AVANCE Y GESTIÓN OPERATIVA"
                                                                            className="w-full p-1 bg-white dark:bg-slate-800 border rounded text-[9px]"
                                                                        />
                                                                    </div>
                                                                    <div className="space-y-0.5">
                                                                        <span className="text-[8px] font-bold text-slate-400 uppercase">Subtítulo</span>
                                                                        <input
                                                                            type="text"
                                                                            value={style.bannerSubtitle || ''}
                                                                            onChange={e => updateStyle('bannerSubtitle', e.target.value)}
                                                                            placeholder="Control Semanal de Producción"
                                                                            className="w-full p-1 bg-white dark:bg-slate-800 border rounded text-[9px]"
                                                                        />
                                                                    </div>
                                                                    <div className="space-y-0.5">
                                                                        <span className="text-[8px] font-bold text-slate-400 uppercase">Nombre Proyecto</span>
                                                                        <input
                                                                            type="text"
                                                                            value={style.bannerProjectName || ''}
                                                                            onChange={e => updateStyle('bannerProjectName', e.target.value)}
                                                                            placeholder="Nombre del Proyecto"
                                                                            className="w-full p-1 bg-white dark:bg-slate-800 border rounded text-[9px]"
                                                                        />
                                                                    </div>
                                                                    <div className="space-y-0.5">
                                                                        <span className="text-[8px] font-bold text-slate-400 uppercase">Evaluador / Responsable</span>
                                                                        <input
                                                                            type="text"
                                                                            value={style.bannerEvaluator || ''}
                                                                            onChange={e => updateStyle('bannerEvaluator', e.target.value)}
                                                                            placeholder="Nombre del Evaluador"
                                                                            className="w-full p-1 bg-white dark:bg-slate-800 border rounded text-[9px]"
                                                                        />
                                                                    </div>
                                                                    <label className="flex items-center gap-1.5 text-[9px] font-bold text-slate-600 dark:text-slate-300 cursor-pointer">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={style.bannerGradient !== false}
                                                                            onChange={e => updateStyle('bannerGradient', e.target.checked)}
                                                                            className="rounded text-primary h-3.5 w-3.5"
                                                                        />
                                                                        Fondo Degradado Ejecutivo
                                                                    </label>
                                                                </div>
                                                            </details>
                                                        )}

                                                        {currentWidget.type === 'text-box' && (
                                                            <details open className="group bg-slate-50 dark:bg-slate-900/60 rounded-xl border border-slate-200 dark:border-slate-700/80">
                                                                <summary className="p-2.5 flex items-center justify-between cursor-pointer select-none font-bold text-[10px] text-slate-700 dark:text-slate-200">
                                                                    <span className="flex items-center gap-1.5">
                                                                        <span className="material-symbols-outlined text-[14px] text-primary">text_fields</span>
                                                                        Contenido de Texto
                                                                    </span>
                                                                </summary>
                                                                <div className="p-2.5 pt-1 border-t border-slate-100 dark:border-slate-800 space-y-2">
                                                                    <div className="space-y-0.5">
                                                                        <span className="text-[8px] font-bold text-slate-400 uppercase">Texto</span>
                                                                        <textarea
                                                                            rows={3}
                                                                            value={style.textContent || ''}
                                                                            onChange={e => updateStyle('textContent', e.target.value)}
                                                                            placeholder="Escribe el texto libre o nota..."
                                                                            className="w-full p-1 bg-white dark:bg-slate-800 border rounded text-[9px]"
                                                                        />
                                                                    </div>
                                                                    <div className="flex items-center justify-between">
                                                                        <span className="text-[8px] font-bold text-slate-400 uppercase">Tamaño ({style.textFontSize || 12}px)</span>
                                                                        <input
                                                                            type="range"
                                                                            min={10}
                                                                            max={24}
                                                                            value={style.textFontSize || 12}
                                                                            onChange={e => updateStyle('textFontSize', parseInt(e.target.value))}
                                                                            className="w-24 h-2 cursor-pointer"
                                                                        />
                                                                    </div>
                                                                    <div className="flex items-center gap-2">
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => updateStyle('textBold', !style.textBold)}
                                                                            className={`px-2 py-1 rounded text-xs font-bold border ${style.textBold ? 'bg-primary text-white border-primary' : 'bg-white dark:bg-slate-800 text-slate-600'}`}
                                                                        >
                                                                            B
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => updateStyle('textItalic', !style.textItalic)}
                                                                            className={`px-2 py-1 rounded text-xs italic border ${style.textItalic ? 'bg-primary text-white border-primary' : 'bg-white dark:bg-slate-800 text-slate-600'}`}
                                                                        >
                                                                            I
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </details>
                                                        )}

                                                        {/* Acordeón: Grilla de 16 Columnas × 9 Filas (Dimensiones del elemento) */}
                                                        <details open className="group bg-slate-50 dark:bg-slate-900/60 rounded-xl border border-slate-200 dark:border-slate-700/80">
                                                            <summary className="p-2.5 flex items-center justify-between cursor-pointer select-none font-bold text-[10px] text-slate-700 dark:text-slate-200">
                                                                <span className="flex items-center gap-1.5">
                                                                    <span className="material-symbols-outlined text-[14px] text-primary">grid_view</span>
                                                                    Dimensiones de Grilla (16 × 9)
                                                                </span>
                                                            </summary>
                                                            <div className="p-3 pt-2 border-t border-slate-100 dark:border-slate-800 space-y-3">
                                                                {(() => {
                                                                    const isSmallElement = ['text-box', 'image', 'slicer', 'kpi-card', 'gauge'].includes(currentWidget.type);
                                                                    const minCols = isSmallElement ? 1 : 2;
                                                                    const currentCols = currentWidget.colSpan || (currentWidget.width === '1/3' ? 5 : currentWidget.width === 'full' ? 16 : 8);
                                                                    const currentRows = currentWidget.rowSpan || (currentWidget.type === 'header-banner' ? 1 : currentWidget.type === 'kpi-card' || currentWidget.type === 'gauge' || currentWidget.type === 'text-box' ? 2 : 4);
                                                                    const colOptions = Array.from({ length: 16 - minCols + 1 }, (_, i) => minCols + i);
                                                                    const rowOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9];

                                                                    return (
                                                                        <div className="grid grid-cols-2 gap-2.5">
                                                                            {/* Desplegable Ancho (Columnas) */}
                                                                            <div className="space-y-1">
                                                                                <label className="text-[8px] font-black text-slate-400 uppercase block tracking-wider">
                                                                                    Ancho (Columnas)
                                                                                </label>
                                                                                <select
                                                                                    value={currentCols}
                                                                                    onChange={e => {
                                                                                        const newColSpan = parseInt(e.target.value, 10);
                                                                                        setWidgets(prev => prev.map(w => {
                                                                                            if (w.id !== currentWidget.id) return w;
                                                                                            const colStart = w.gridColStart || 1;
                                                                                            const clampedColStart = Math.min(colStart, 16 - newColSpan + 1);
                                                                                            return { ...w, colSpan: newColSpan, gridColStart: clampedColStart };
                                                                                        }));
                                                                                    }}
                                                                                    className="w-full p-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-800 dark:text-white cursor-pointer shadow-xs focus:ring-2 focus:ring-primary/30 outline-hidden"
                                                                                >
                                                                                    {colOptions.map(c => (
                                                                                        <option key={c} value={c}>
                                                                                            {c} {c === 1 ? 'columna' : 'columnas'} {c === 16 ? '(Completo)' : c === 8 ? '(1/2)' : c === 4 ? '(1/4)' : ''}
                                                                                        </option>
                                                                                    ))}
                                                                                </select>
                                                                            </div>

                                                                            {/* Desplegable Alto (Filas) */}
                                                                            <div className="space-y-1">
                                                                                <label className="text-[8px] font-black text-slate-400 uppercase block tracking-wider">
                                                                                    Alto (Filas)
                                                                                </label>
                                                                                <select
                                                                                    value={currentRows}
                                                                                    onChange={e => {
                                                                                        const newRowSpan = parseInt(e.target.value, 10);
                                                                                        setWidgets(prev => prev.map(w => {
                                                                                            if (w.id !== currentWidget.id) return w;
                                                                                            const rowStart = w.gridRowStart || 1;
                                                                                            const clampedRowStart = Math.min(rowStart, 9 - newRowSpan + 1);
                                                                                            return { ...w, rowSpan: newRowSpan, gridRowStart: clampedRowStart };
                                                                                        }));
                                                                                    }}
                                                                                    className="w-full p-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-800 dark:text-white cursor-pointer shadow-xs focus:ring-2 focus:ring-primary/30 outline-hidden"
                                                                                >
                                                                                    {rowOptions.map(r => (
                                                                                        <option key={r} value={r}>
                                                                                            {r} {r === 1 ? 'fila' : 'filas'} {r === 9 ? '(Completo)' : ''}
                                                                                        </option>
                                                                                    ))}
                                                                                </select>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })()}
                                                            </div>
                                                        </details>

                                                        {/* Acordeón: Efectos y Fondo */}
                                                        <details open className="group bg-slate-50 dark:bg-slate-900/60 rounded-xl border border-slate-200 dark:border-slate-700/80">
                                                            <summary className="p-2.5 flex items-center justify-between cursor-pointer select-none font-bold text-[10px] text-slate-700 dark:text-slate-200">
                                                                <span className="flex items-center gap-1.5">
                                                                    <span className="material-symbols-outlined text-[14px] text-primary">wallpaper</span>
                                                                    Fondo y Borde
                                                                </span>
                                                            </summary>
                                                            <div className="p-2.5 pt-1 border-t border-slate-100 dark:border-slate-800 space-y-2.5">
                                                                {/* Fondo */}
                                                                <div className="flex items-center justify-between">
                                                                    <label className="flex items-center gap-1.5 text-[9px] font-bold text-slate-600 dark:text-slate-300 cursor-pointer">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={style.showBackground !== false}
                                                                            onChange={e => updateStyle('showBackground', e.target.checked)}
                                                                            className="rounded text-primary h-3.5 w-3.5"
                                                                        />
                                                                        Fondo de Tarjeta
                                                                    </label>
                                                                    {style.showBackground !== false && (
                                                                        <input
                                                                            type="color"
                                                                            value={style.backgroundColor || '#ffffff'}
                                                                            onChange={e => updateStyle('backgroundColor', e.target.value)}
                                                                            className="w-6 h-6 rounded cursor-pointer border-none bg-transparent"
                                                                        />
                                                                    )}
                                                                </div>

                                                                {/* Borde */}
                                                                <div className="flex items-center justify-between">
                                                                    <label className="flex items-center gap-1.5 text-[9px] font-bold text-slate-600 dark:text-slate-300 cursor-pointer">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={style.showBorder !== false}
                                                                            onChange={e => updateStyle('showBorder', e.target.checked)}
                                                                            className="rounded text-primary h-3.5 w-3.5"
                                                                        />
                                                                        Borde Visual
                                                                    </label>
                                                                    {style.showBorder !== false && (
                                                                        <input
                                                                            type="color"
                                                                            value={style.borderColor || '#e2e8f0'}
                                                                            onChange={e => updateStyle('borderColor', e.target.value)}
                                                                            className="w-6 h-6 rounded cursor-pointer border-none bg-transparent"
                                                                        />
                                                                    )}
                                                                </div>

                                                                {/* Radio de Esquinas */}
                                                                {style.showBorder !== false && (
                                                                    <div className="space-y-0.5">
                                                                        <span className="text-[8px] font-bold text-slate-400 uppercase">Esquinas Redondeadas ({style.borderRadius !== undefined ? style.borderRadius : 16}px)</span>
                                                                        <input
                                                                            type="range"
                                                                            min={0}
                                                                            max={32}
                                                                            value={style.borderRadius !== undefined ? style.borderRadius : 16}
                                                                            onChange={e => updateStyle('borderRadius', parseInt(e.target.value))}
                                                                            className="w-full h-2 cursor-pointer"
                                                                        />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </details>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                        ) : (
                            <button 
                                onClick={() => setRightPanelVisOpen(true)}
                                className="w-8 bg-white dark:bg-slate-800 border-l border-slate-200 dark:border-slate-700 flex flex-col items-center py-4 text-slate-400 hover:text-primary transition-colors cursor-pointer select-none"
                                title="Expandir panel de visualizaciones"
                            >
                                <span className="material-symbols-outlined text-[18px]">chevron_left</span>
                                <span className="text-[9px] font-black uppercase tracking-widest mt-6 [writing-mode:vertical-lr] rotate-180">
                                    Visualizaciones
                                </span>
                            </button>
                        )}

                        {/* 3. PANEL LATERAL: DATOS (ESTILO POWER BI) */}
                        {rightPanelDataOpen ? (
                            <div className="w-80 bg-white dark:bg-slate-800 border-l border-slate-200 dark:border-slate-700 flex flex-col h-full shrink-0 shadow-lg z-10 transition-all duration-300">
                                {/* Cabecera Panel Datos */}
                                <div className="p-3.5 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between bg-slate-50/50 dark:bg-slate-850">
                                    <div className="flex items-center gap-2">
                                        <span className="material-symbols-outlined text-[18px] text-emerald-600">database</span>
                                        <span className="text-xs font-black uppercase tracking-wider text-slate-800 dark:text-white">Datos</span>
                                    </div>
                                    <button 
                                        onClick={() => setRightPanelDataOpen(false)}
                                        className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg text-slate-400 hover:text-slate-600 transition-colors"
                                        title="Colapsar panel de datos"
                                    >
                                        <span className="material-symbols-outlined text-[16px]">chevron_right</span>
                                    </button>
                                </div>

                                <div className="flex-1 overflow-y-auto p-4 space-y-4 text-xs">
                                    {/* Propiedades del Reporte (Colapsable) */}
                                    <details className="group border border-slate-200 dark:border-slate-700/80 rounded-xl bg-slate-50/40 dark:bg-slate-900/20">
                                        <summary className="p-2.5 flex items-center justify-between font-black uppercase text-[9px] tracking-wider text-slate-500 dark:text-slate-400 cursor-pointer select-none">
                                            <span>Propiedades del Reporte</span>
                                            <span className="material-symbols-outlined text-[14px] group-open:rotate-180 transition-transform">keyboard_arrow_down</span>
                                        </summary>
                                        <div className="p-3 pt-0 space-y-2.5 border-t border-slate-100 dark:border-slate-800 mt-1">
                                            <div>
                                                <label className="text-[8px] font-bold text-slate-400 uppercase block mb-1">Nombre</label>
                                                <input 
                                                    type="text" 
                                                    value={reportTitle} 
                                                    onChange={e => setReportTitle(e.target.value)} 
                                                    className="w-full text-xs p-2 rounded-lg border bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                                                    placeholder="Título..."
                                                />
                                            </div>
                                            <div>
                                                <label className="text-[8px] font-bold text-slate-400 uppercase block mb-1">Alcance (RLS)</label>
                                                <select 
                                                    value={reportScope} 
                                                    onChange={e => setReportScope(e.target.value as any)}
                                                    className="w-full text-xs p-2 rounded-lg border bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                                                >
                                                    <option value="obra">Obra</option>
                                                    <option value="multiobra">Multiobra</option>
                                                    <option value="corporativo">Corporativo</option>
                                                </select>
                                            </div>
                                        </div>
                                    </details>

                                    {/* Tablas / Módulos de Datos con Campos Arrastrables */}
                                    <div className="space-y-2">
                                        <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider block">
                                            Tablas y Campos Disponibles
                                        </span>

                                        <div className="space-y-2">
                                            {[
                                                { id: 'scurve', label: 'Planificación Maestra', icon: 'timeline', description: 'Curva S y avance físico acumulado' },
                                                { id: 'intermediate', label: 'Planificación Intermedia', icon: 'pending_actions', description: 'Lookahead y gestión de restricciones' },
                                                { id: 'ppc', label: 'Planificación Semanal', icon: 'calendar_month', description: 'PPC, asignaciones y causas de no cumplimiento' },
                                                { id: 'quality', label: 'Control de Calidad', icon: 'verified', description: 'Protocolos de control y firmas' },
                                                { id: 'subcontracts', label: 'Subcontratos', icon: 'payments', description: 'Contratos y estados de pago visados' },
                                                { id: 'metrics', label: 'Métricas del Proyecto', icon: 'speed', description: 'Indicadores estándar: PPC, PCR, Confiabilidad, Montos' }
                                            ].map(c => {
                                                const isChecked = selectedComponents.includes(c.id);
                                                const isActive = activeEditingComponent === c.id;
                                                let compColumns = selectedColumns[c.id] || DEFAULT_COLUMNS[c.id] || [];
                                                if (c.id === 'quality' && compColumns.includes('Revisor 1')) {
                                                    compColumns = [...DEFAULT_COLUMNS.quality];
                                                }

                                                return (
                                                    <div 
                                                        key={c.id} 
                                                        className={`rounded-xl border transition-all ${isActive ? 'border-primary bg-primary/5' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/40'}`}
                                                    >
                                                        <div className="flex items-center justify-between p-2.5">
                                                            <div className="flex items-center gap-2 flex-1 cursor-pointer select-none" onClick={() => setActiveEditingComponent(c.id)}>
                                                                <span className="material-symbols-outlined text-[16px] text-slate-400">table_chart</span>
                                                                <span className="font-extrabold text-[11px] text-slate-800 dark:text-slate-200 truncate">{c.label}</span>
                                                            </div>
                                                            <input 
                                                                type="checkbox" 
                                                                checked={isChecked} 
                                                                onChange={() => toggleComponent(c.id)}
                                                                className="rounded text-primary h-3.5 w-3.5 cursor-pointer"
                                                                title="Habilitar tabla en reporte"
                                                            />
                                                        </div>

                                                        {/* Campos Arrastrables dentro de la tabla */}
                                                        {isChecked && (
                                                            <div className="p-2 border-t border-slate-100 dark:border-slate-700/50 bg-slate-50/50 dark:bg-slate-900/30 space-y-1">
                                                                <span className="text-[8px] font-black uppercase text-slate-400 tracking-wider block mb-1">
                                                                    Arrastra campos al gráfico:
                                                                </span>
                                                                {compColumns.map(col => (
                                                                    <div
                                                                        key={col}
                                                                        draggable
                                                                        onDragStart={() => setDraggedField({ fieldKey: col, sourceComponentId: c.id })}
                                                                        className="flex items-center gap-1.5 p-1.5 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 text-[10px] text-slate-700 dark:text-slate-300 font-bold hover:border-primary hover:text-primary cursor-grab active:cursor-grabbing shadow-sm transition-all select-none"
                                                                    >
                                                                        <span className="material-symbols-outlined text-[13px] text-slate-400">drag_indicator</span>
                                                                        <span className="truncate flex-1">{col}</span>
                                                                        <span className="text-[8px] font-mono text-slate-400">
                                                                            {col.includes('$') || col.includes('%') || col.toLowerCase().includes('cant') || col.toLowerCase().includes('total') ? '123' : 'Abc'}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Columnas Calculadas / Virtuales */}
                                    <div className="border-t border-slate-100 dark:border-slate-700/50 pt-3 space-y-2">
                                        <div className="flex items-center justify-between">
                                            <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Columnas Virtuales</span>
                                            <button 
                                                type="button"
                                                onClick={() => {
                                                    const available = selectedColumns[activeEditingComponent] || DEFAULT_COLUMNS[activeEditingComponent] || [];
                                                    setNewCalcForm({
                                                        name: '',
                                                        calcType: 'math',
                                                        colA: available[0] || '',
                                                        operator: '-',
                                                        colB: available[1] || available[0] || '',
                                                        constantValue: 0,
                                                        concatFields: available.slice(0, 2),
                                                        concatSeparator: ' - ',
                                                        transformField: available[0] || '',
                                                        transformType: 'uppercase',
                                                        conditionField: available[0] || '',
                                                        conditionOperator: 'equals',
                                                        conditionValue: '',
                                                        thenValue: 'Cumple',
                                                        elseValue: 'No Cumple',
                                                        dateFieldA: available.find(c => c.toLowerCase().includes('fecha') || c.toLowerCase().includes('fin') || c.toLowerCase().includes('corte')) || available[0] || '',
                                                        dateFieldB: available.find(c => c.toLowerCase().includes('inicio') || c.toLowerCase().includes('base')) || available[1] || available[0] || ''
                                                    });
                                                    setShowCalcModal(true);
                                                }}
                                                className="text-primary hover:underline text-[9px] font-black cursor-pointer flex items-center gap-1"
                                            >
                                                <span className="material-symbols-outlined text-[13px]">add_circle</span>
                                                + Nueva
                                            </button>
                                        </div>

                                        {calculatedColumns.map(cc => {
                                            const typeLabel = cc.calcType === 'text_concat' ? 'Texto' : cc.calcType === 'text_transform' ? 'Formato' : cc.calcType === 'conditional_if' ? 'Regla SI' : cc.calcType === 'date_diff' ? 'Fechas' : 'Fórmula';
                                            return (
                                                <div 
                                                    key={cc.id}
                                                    draggable
                                                    onDragStart={() => setDraggedField({ fieldKey: cc.name, sourceComponentId: activeEditingComponent })}
                                                    className="flex items-center justify-between p-1.5 bg-blue-50/70 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/80 rounded-lg text-[10px] cursor-grab active:cursor-grabbing hover:border-primary transition-all group"
                                                    title={`Columna Virtual (${typeLabel}): ${cc.name}`}
                                                >
                                                    <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                                        <span className="material-symbols-outlined text-[13px] text-blue-500">functions</span>
                                                        <span className="font-bold text-primary truncate text-[10px]">{cc.name}</span>
                                                        <span className="text-[7px] font-black uppercase px-1 py-0.2 rounded bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 shrink-0">
                                                            {typeLabel}
                                                        </span>
                                                    </div>
                                                    <button 
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setCalculatedColumns(prev => prev.filter(c => c.id !== cc.id));
                                                        }}
                                                        className="text-slate-400 hover:text-red-500 font-bold text-xs p-0.5 rounded transition-colors"
                                                        title="Eliminar columna virtual"
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Botón Guardar en la parte inferior */}
                                <div className="p-3 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                                    <button
                                        onClick={() => setShowSaveConfirmModal(true)}
                                        disabled={savingLocal}
                                        className="w-full py-2.5 px-4 bg-primary text-white text-xs font-bold rounded-xl hover:bg-primary/95 transition-all shadow-md shadow-primary/20 flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
                                    >
                                        <span className="material-symbols-outlined text-[18px]">save</span>
                                        {savingLocal ? 'Guardando...' : 'Guardar Reporte'}
                                    </button>
                                </div>
                            </div>
                        ) : (
                                 <button 
                                    onClick={() => setRightPanelDataOpen(true)}
                                    className="w-8 bg-white dark:bg-slate-800 border-l border-slate-200 dark:border-slate-700 flex flex-col items-center py-4 text-slate-400 hover:text-emerald-600 transition-colors cursor-pointer select-none"
                                    title="Expandir panel de datos"
                                >
                                    <span className="material-symbols-outlined text-[18px]">chevron_left</span>
                                    <span className="text-[9px] font-black uppercase tracking-widest mt-6 [writing-mode:vertical-lr] rotate-180">
                                        Datos
                                    </span>
                                </button>
                            )}
                            </>
                        )}
                        </div>
                    ) : config.accessToken ? (
                        <>
                            {hasWarningBanner && (
                                <div className="w-full bg-amber-500/10 text-amber-500 border-b border-amber-500/20 px-6 py-2 flex items-center gap-2 z-20 absolute top-0 left-0 right-0 h-10 select-none">
                                    <span className="material-symbols-outlined text-[16px]">lock</span>
                                    Diseñador en modo Solo Lectura: No tiene permisos de modificación.
                                </div>
                            )}
                            <div
                                ref={embedContainerRef}
                                className={`w-full h-full absolute ${hasWarningBanner ? 'top-10' : 'inset-0'} rounded-b-2xl`}
                                style={{ height: hasWarningBanner ? 'calc(100% - 2.5rem)' : '100%' }}
                            />
                        </>
                    ) : (
                        <div className="max-w-xl text-center p-8 space-y-5 animate-fade-in mx-auto mt-16">
                            <div className="size-16 rounded-2xl bg-primary/10 text-primary border border-primary/20 flex items-center justify-center mx-auto shadow-inner">
                                <span className="material-symbols-outlined text-[36px]">analytics</span>
                            </div>
                            <div className="space-y-2">
                                <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                                    Módulo de Inteligencia de Negocios Activo
                                </h3>
                                <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                                    La plataforma analítica de K-Lyapp está completamente lista. Seleccione un reporte personalizado en el catálogo superior o use el diseñador para crear visualizaciones interactivas de avance y PPC.
                                </p>
                            </div>

                            <div className="bg-slate-900 text-slate-200 text-left p-4 rounded-xl font-mono text-[11px] space-y-1 shadow-inner border border-slate-800">
                                <p className="text-slate-500"># Infraestructura Analítica Activa</p>
                                <p><span className="text-emerald-400">Esquema SQL:</span> bi (7 vistas analíticas)</p>
                                <p><span className="text-emerald-400">Optimización:</span> Vista materializada de Curva S activa</p>
                                <p><span className="text-emerald-400">Gobernanza:</span> Dataset "K-Lyapp" y RLS por Obra habilitada</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* MODAL 1: CONFIRMACIÓN DE SALIDA (CANCELAR) - EXACTO A PLANIFICACIÓN SEMANAL */}
                {showCancelWarningModal && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 dark:bg-black/80 backdrop-blur-sm transition-all duration-300">
                        <div className="bg-white dark:bg-slate-900 rounded-3xl max-w-md w-full shadow-2xl border border-slate-100 dark:border-slate-800 overflow-hidden transform scale-100 animate-in fade-in zoom-in duration-200">
                            <div className="p-6 text-center space-y-4">
                                <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-amber-50 dark:bg-amber-950/30 text-amber-500 dark:text-amber-400">
                                    <span className="material-symbols-outlined text-[32px]">warning</span>
                                </div>
                                <div className="space-y-2">
                                    <h3 className="text-lg font-bold text-slate-900 dark:text-white">¿Desea salir del Diseñador sin guardar?</h3>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                                        Tiene modificaciones sin guardar en el diseño de este reporte.
                                    </p>
                                </div>
                                <div className="flex flex-col gap-2 pt-2">
                                    <button
                                        onClick={handleExitWithoutSaving}
                                        className="w-full bg-red-50 hover:bg-red-100 dark:bg-red-950/20 dark:hover:bg-red-950/40 text-red-600 dark:text-red-400 font-bold py-2.5 px-4 rounded-xl text-xs uppercase flex items-center justify-center gap-1.5 transition-all cursor-pointer"
                                    >
                                        <span className="material-symbols-outlined text-[16px]">delete_forever</span>
                                        Salir sin Guardar
                                    </button>
                                    <button
                                        onClick={handleExitWithSaving}
                                        className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-2.5 px-4 rounded-xl text-xs uppercase flex items-center justify-center gap-1.5 shadow-sm active:scale-95 transition-all cursor-pointer"
                                    >
                                        <span className="material-symbols-outlined text-[16px]">save</span>
                                        Guardar
                                    </button>
                                    <button
                                        onClick={() => setShowCancelWarningModal(false)}
                                        className="w-full bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 font-bold py-2.5 px-4 rounded-xl text-xs uppercase flex items-center justify-center gap-1.5 shadow-sm active:scale-95 transition-all cursor-pointer"
                                    >
                                        <span className="material-symbols-outlined text-[16px]">edit</span>
                                        Seguir Editando
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}


                {/* MODAL: CREADOR DE COLUMNAS VIRTUALES (MATEMÁTICA, TEXTO, CONDICIONAL, FECHAS) */}
                {showCalcModal && (
                    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/60 dark:bg-black/80 backdrop-blur-sm transition-all duration-300">
                        <div className="bg-white dark:bg-slate-900 rounded-3xl max-w-xl w-full shadow-2xl border border-slate-100 dark:border-slate-800 overflow-hidden transform scale-100 animate-in fade-in zoom-in duration-200 p-6 space-y-4">
                            
                            {/* Cabecera del Modal */}
                            <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
                                <div className="flex items-center gap-2.5">
                                    <div className="w-10 h-10 rounded-2xl bg-blue-50 dark:bg-blue-950/50 flex items-center justify-center text-primary">
                                        <span className="material-symbols-outlined text-[22px]">functions</span>
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-wider">
                                            Nueva Columna Virtual (DAX)
                                        </h3>
                                        <p className="text-[10px] text-slate-400">
                                            Crea un campo dinámico calculado fila por fila para tus gráficos y tablas.
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowCalcModal(false)}
                                    className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg cursor-pointer"
                                >
                                    <span className="material-symbols-outlined text-[20px]">close</span>
                                </button>
                            </div>

                            {/* Nombre del Campo */}
                            <div className="space-y-1">
                                <label className="text-[9px] font-black uppercase text-slate-400 tracking-wider block">
                                    Nombre del Nuevo Campo *
                                </label>
                                <input
                                    type="text"
                                    value={newCalcForm.name}
                                    onChange={e => setNewCalcForm(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="Ej: Desvío Físico %, Llave Obra-Frente, Semáforo Plazo..."
                                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-800 dark:text-slate-100 focus:border-primary outline-none"
                                />
                            </div>

                            {/* Selector de Tipo de Cálculo */}
                            <div className="space-y-1">
                                <label className="text-[9px] font-black uppercase text-slate-400 tracking-wider block">
                                    Tipo de Operación
                                </label>
                                <div className="grid grid-cols-5 gap-1.5 bg-slate-100 dark:bg-slate-800/80 p-1 rounded-2xl">
                                    {[
                                        { id: 'math', label: 'Matemática', icon: 'calculate' },
                                        { id: 'text_concat', label: 'Unir Texto', icon: 'join_inner' },
                                        { id: 'text_transform', label: 'Formato', icon: 'text_fields' },
                                        { id: 'conditional_if', label: 'Regla SI', icon: 'alt_route' },
                                        { id: 'date_diff', label: 'Fechas', icon: 'date_range' }
                                    ].map(t => {
                                        const isSelected = newCalcForm.calcType === t.id;
                                        return (
                                            <button
                                                key={t.id}
                                                type="button"
                                                onClick={() => setNewCalcForm(prev => ({ ...prev, calcType: t.id as any }))}
                                                className={`py-2 px-1 rounded-xl text-[9px] font-black flex flex-col items-center gap-1 transition-all cursor-pointer ${isSelected ? 'bg-white dark:bg-slate-700 text-primary shadow-sm' : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'}`}
                                            >
                                                <span className="material-symbols-outlined text-[15px]">{t.icon}</span>
                                                <span className="truncate">{t.label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Contenido Dinámico según Tipo */}
                            <div className="bg-slate-50 dark:bg-slate-800/40 p-3.5 rounded-2xl border border-slate-100 dark:border-slate-800 space-y-3">
                                {(() => {
                                    const availableCols = selectedColumns[activeEditingComponent] || DEFAULT_COLUMNS[activeEditingComponent] || [];

                                    // 1. FORMULARIO MATEMÁTICO
                                    if (newCalcForm.calcType === 'math') {
                                        return (
                                            <div className="space-y-2.5">
                                                <div className="grid grid-cols-3 gap-2 items-center">
                                                    <div>
                                                        <span className="text-[8px] font-black uppercase text-slate-400 block mb-1">Campo A</span>
                                                        <select
                                                            value={newCalcForm.colA}
                                                            onChange={e => setNewCalcForm(prev => ({ ...prev, colA: e.target.value }))}
                                                            className="w-full p-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs"
                                                        >
                                                            {availableCols.map(c => <option key={c} value={c}>{c}</option>)}
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <span className="text-[8px] font-black uppercase text-slate-400 block mb-1">Operación</span>
                                                        <select
                                                            value={newCalcForm.operator}
                                                            onChange={e => setNewCalcForm(prev => ({ ...prev, operator: e.target.value as any }))}
                                                            className="w-full p-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-center"
                                                        >
                                                            <option value="-">Resta ( - )</option>
                                                            <option value="+">Suma ( + )</option>
                                                            <option value="*">Multiplicación ( * )</option>
                                                            <option value="/">División ( / )</option>
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <span className="text-[8px] font-black uppercase text-slate-400 block mb-1">Campo B o Fijo</span>
                                                        <select
                                                            value={newCalcForm.colB}
                                                            onChange={e => setNewCalcForm(prev => ({ ...prev, colB: e.target.value }))}
                                                            className="w-full p-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs"
                                                        >
                                                            {availableCols.map(c => <option key={c} value={c}>{c}</option>)}
                                                            <option value="constant">Número Fijo...</option>
                                                        </select>
                                                    </div>
                                                </div>
                                                {newCalcForm.colB === 'constant' && (
                                                    <div>
                                                        <span className="text-[8px] font-black uppercase text-slate-400 block mb-1">Valor Numérico Fijo</span>
                                                        <input
                                                            type="number"
                                                            value={newCalcForm.constantValue}
                                                            onChange={e => setNewCalcForm(prev => ({ ...prev, constantValue: parseFloat(e.target.value) || 0 }))}
                                                            className="w-full p-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs"
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    }

                                    // 2. FORMULARIO CONCATENACIÓN DE TEXTO
                                    if (newCalcForm.calcType === 'text_concat') {
                                        return (
                                            <div className="space-y-2.5">
                                                <div>
                                                    <span className="text-[8px] font-black uppercase text-slate-400 block mb-1">Campos a Unir (Selecciona 2 o más)</span>
                                                    <div className="grid grid-cols-2 gap-1 max-h-32 overflow-y-auto p-1 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                                                        {availableCols.map(c => {
                                                            const isChecked = newCalcForm.concatFields.includes(c);
                                                            return (
                                                                <label key={c} className="flex items-center gap-1.5 p-1 hover:bg-slate-50 dark:hover:bg-slate-700/50 rounded cursor-pointer text-[10px]">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isChecked}
                                                                        onChange={() => {
                                                                            setNewCalcForm(prev => {
                                                                                const exists = prev.concatFields.includes(c);
                                                                                return {
                                                                                    ...prev,
                                                                                    concatFields: exists ? prev.concatFields.filter(f => f !== c) : [...prev.concatFields, c]
                                                                                };
                                                                            });
                                                                        }}
                                                                        className="rounded text-primary h-3.5 w-3.5"
                                                                    />
                                                                    <span className="truncate">{c}</span>
                                                                </label>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                                <div>
                                                    <span className="text-[8px] font-black uppercase text-slate-400 block mb-1">Separador / Delimitador</span>
                                                    <input
                                                        type="text"
                                                        value={newCalcForm.concatSeparator}
                                                        onChange={e => setNewCalcForm(prev => ({ ...prev, concatSeparator: e.target.value }))}
                                                        placeholder="Ej:  -  ó  /  ó  | "
                                                        className="w-full p-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-mono"
                                                    />
                                                </div>
                                            </div>
                                        );
                                    }

                                    // 3. FORMULARIO TRANSFORMACIÓN DE TEXTO
                                    if (newCalcForm.calcType === 'text_transform') {
                                        return (
                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <span className="text-[8px] font-black uppercase text-slate-400 block mb-1">Campo Origen</span>
                                                    <select
                                                        value={newCalcForm.transformField || availableCols[0]}
                                                        onChange={e => setNewCalcForm(prev => ({ ...prev, transformField: e.target.value }))}
                                                        className="w-full p-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs"
                                                    >
                                                        {availableCols.map(c => <option key={c} value={c}>{c}</option>)}
                                                    </select>
                                                </div>
                                                <div>
                                                    <span className="text-[8px] font-black uppercase text-slate-400 block mb-1">Función de Formato</span>
                                                    <select
                                                        value={newCalcForm.transformType}
                                                        onChange={e => setNewCalcForm(prev => ({ ...prev, transformType: e.target.value as any }))}
                                                        className="w-full p-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold"
                                                    >
                                                        <option value="uppercase">MAYÚSCULAS (UPPER)</option>
                                                        <option value="lowercase">minúsculas (lower)</option>
                                                        <option value="trim">Recortar Espacios (TRIM)</option>
                                                    </select>
                                                </div>
                                            </div>
                                        );
                                    }

                                    // 4. FORMULARIO CONDICIONAL (SI / IF)
                                    if (newCalcForm.calcType === 'conditional_if') {
                                        return (
                                            <div className="space-y-2.5">
                                                <div className="grid grid-cols-3 gap-2">
                                                    <div>
                                                        <span className="text-[8px] font-black uppercase text-slate-400 block mb-1">SI [Campo]</span>
                                                        <select
                                                            value={newCalcForm.conditionField || availableCols[0]}
                                                            onChange={e => setNewCalcForm(prev => ({ ...prev, conditionField: e.target.value }))}
                                                            className="w-full p-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs"
                                                        >
                                                            {availableCols.map(c => <option key={c} value={c}>{c}</option>)}
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <span className="text-[8px] font-black uppercase text-slate-400 block mb-1">Operador</span>
                                                        <select
                                                            value={newCalcForm.conditionOperator}
                                                            onChange={e => setNewCalcForm(prev => ({ ...prev, conditionOperator: e.target.value as any }))}
                                                            className="w-full p-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold"
                                                        >
                                                            <option value="equals">Es Igual a ( = )</option>
                                                            <option value="not_equals">Es Distinto a ( != )</option>
                                                            <option value="greater">Mayor que ( &gt; )</option>
                                                            <option value="less">Menor que ( &lt; )</option>
                                                            <option value="contains">Contiene Texto</option>
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <span className="text-[8px] font-black uppercase text-slate-400 block mb-1">Valor Comparado</span>
                                                        <input
                                                            type="text"
                                                            value={newCalcForm.conditionValue}
                                                            onChange={e => setNewCalcForm(prev => ({ ...prev, conditionValue: e.target.value }))}
                                                            placeholder="Ej: 0 ó Aprobado"
                                                            className="w-full p-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-2 gap-2 pt-1 border-t border-slate-200/60 dark:border-slate-700/60">
                                                    <div>
                                                        <span className="text-[8px] font-black uppercase text-emerald-600 dark:text-emerald-400 block mb-1">ENTONCES (Valor Si Cumple)</span>
                                                        <input
                                                            type="text"
                                                            value={newCalcForm.thenValue}
                                                            onChange={e => setNewCalcForm(prev => ({ ...prev, thenValue: e.target.value }))}
                                                            placeholder="Ej: A Tiempo / Verde / 1"
                                                            className="w-full p-2 bg-white dark:bg-slate-800 border border-emerald-200 dark:border-emerald-800/80 rounded-xl text-xs"
                                                        />
                                                    </div>
                                                    <div>
                                                        <span className="text-[8px] font-black uppercase text-rose-600 dark:text-rose-400 block mb-1">SINO (Valor Si No Cumple)</span>
                                                        <input
                                                            type="text"
                                                            value={newCalcForm.elseValue}
                                                            onChange={e => setNewCalcForm(prev => ({ ...prev, elseValue: e.target.value }))}
                                                            placeholder="Ej: Atrasado / Rojo / 0"
                                                            className="w-full p-2 bg-white dark:bg-slate-800 border border-rose-200 dark:border-rose-800/80 rounded-xl text-xs"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    }

                                    // 5. FORMULARIO DIFERENCIA DE FECHAS
                                    if (newCalcForm.calcType === 'date_diff') {
                                        return (
                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <span className="text-[8px] font-black uppercase text-slate-400 block mb-1">Fecha Final / Proyectada (A)</span>
                                                    <select
                                                        value={newCalcForm.dateFieldA || availableCols[0]}
                                                        onChange={e => setNewCalcForm(prev => ({ ...prev, dateFieldA: e.target.value }))}
                                                        className="w-full p-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs"
                                                    >
                                                        {availableCols.map(c => <option key={c} value={c}>{c}</option>)}
                                                    </select>
                                                </div>
                                                <div>
                                                    <span className="text-[8px] font-black uppercase text-slate-400 block mb-1">Fecha Inicial / Base (B)</span>
                                                    <select
                                                        value={newCalcForm.dateFieldB || availableCols[1] || availableCols[0]}
                                                        onChange={e => setNewCalcForm(prev => ({ ...prev, dateFieldB: e.target.value }))}
                                                        className="w-full p-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs"
                                                    >
                                                        {availableCols.map(c => <option key={c} value={c}>{c}</option>)}
                                                    </select>
                                                </div>
                                            </div>
                                        );
                                    }

                                    return null;
                                })()}
                            </div>

                            {/* Acciones de Modal */}
                            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                                <button
                                    type="button"
                                    onClick={() => setShowCalcModal(false)}
                                    className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 rounded-xl transition-colors cursor-pointer"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    disabled={!newCalcForm.name.trim()}
                                    onClick={() => {
                                        if (!newCalcForm.name.trim()) return;
                                        const newCol: CalculatedColumn = {
                                            id: `calc-${Date.now()}`,
                                            name: newCalcForm.name.trim(),
                                            calcType: newCalcForm.calcType,
                                            colA: newCalcForm.colA,
                                            operator: newCalcForm.operator,
                                            colB: newCalcForm.colB,
                                            constantValue: newCalcForm.constantValue,
                                            concatFields: newCalcForm.concatFields,
                                            concatSeparator: newCalcForm.concatSeparator,
                                            transformField: newCalcForm.transformField,
                                            transformType: newCalcForm.transformType,
                                            conditionField: newCalcForm.conditionField,
                                            conditionOperator: newCalcForm.conditionOperator,
                                            conditionValue: newCalcForm.conditionValue,
                                            thenValue: newCalcForm.thenValue,
                                            elseValue: newCalcForm.elseValue,
                                            dateFieldA: newCalcForm.dateFieldA,
                                            dateFieldB: newCalcForm.dateFieldB
                                        };
                                        setCalculatedColumns(prev => [...prev, newCol]);
                                        setShowCalcModal(false);
                                    }}
                                    className="px-5 py-2 text-xs font-bold bg-primary text-white rounded-xl hover:bg-primary/95 shadow-md shadow-primary/20 flex items-center gap-1.5 transition-all disabled:opacity-40 cursor-pointer"
                                >
                                    <span className="material-symbols-outlined text-[16px]">add</span>
                                    Crear Columna
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* MODAL 3: PLANTILLAS PREDISEÑADAS DE DIAPOSITIVA (SLIDE PRESETS) DESDE EL MAESTRO DE REPORTES */}
                {showPresetModal && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 dark:bg-black/80 backdrop-blur-sm transition-all duration-300">
                        <div className="bg-white dark:bg-slate-900 rounded-3xl max-w-3xl w-full shadow-2xl border border-slate-100 dark:border-slate-800 overflow-hidden transform scale-100 animate-in fade-in zoom-in duration-200 p-6 space-y-5 max-h-[90vh] flex flex-col">
                            <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3 shrink-0">
                                <div className="flex items-center gap-2">
                                    <span className="material-symbols-outlined text-[24px] text-primary">auto_stories</span>
                                    <div>
                                        <h3 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-wider">
                                            Plantillas Prediseñadas de Diapositiva
                                        </h3>
                                        <p className="text-[10px] text-slate-400">
                                            Aplica una maquetación profesional 16:9 preconfigurada desde el Maestro de Reportes.
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowPresetModal(false)}
                                    className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg cursor-pointer"
                                >
                                    <span className="material-symbols-outlined text-[20px]">close</span>
                                </button>
                            </div>

                            <div className="flex-1 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-4 p-1">
                                {loadMasterReportTemplates().map((template: MasterReportTemplate) => (
                                    <div
                                        key={template.id}
                                        onClick={() => handleApplySlidePreset(template)}
                                        className="p-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-850 hover:border-primary hover:shadow-lg hover:shadow-primary/10 transition-all cursor-pointer group flex flex-col justify-between gap-3"
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="size-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center group-hover:scale-110 transition-transform">
                                                <span className="material-symbols-outlined text-[22px]">{template.icon || 'analytics'}</span>
                                            </div>
                                            <span className="text-[9px] font-black uppercase px-2 py-0.5 rounded-full bg-slate-200/80 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                                                {template.badge || template.category}
                                            </span>
                                        </div>
                                        <div className="space-y-1">
                                            <h4 className="font-extrabold text-xs text-slate-900 dark:text-white group-hover:text-primary transition-colors">
                                                {template.name}
                                            </h4>
                                            <p className="text-[10px] text-slate-500 dark:text-slate-400 leading-relaxed">
                                                {template.description}
                                            </p>
                                        </div>
                                        <div className="text-[9px] font-semibold text-slate-400 flex items-center gap-1 border-t border-slate-100 dark:border-slate-800/80 pt-2">
                                            <span className="material-symbols-outlined text-[14px]">widgets</span>
                                            {template.widgets.length} componente{template.widgets.length !== 1 ? 's' : ''} preconfigurado{template.widgets.length !== 1 ? 's' : ''}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* MODAL 4: SELECTOR / SUBIDOR DE IMÁGENES DE AVANCE */}
                {showImagePickerModal && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 dark:bg-black/80 backdrop-blur-sm transition-all duration-300">
                        <div className="bg-white dark:bg-slate-900 rounded-3xl max-w-lg w-full shadow-2xl border border-slate-100 dark:border-slate-800 overflow-hidden transform scale-100 animate-in fade-in zoom-in duration-200 p-6 space-y-4">
                            <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
                                <div className="flex items-center gap-2">
                                    <span className="material-symbols-outlined text-[22px] text-primary">photo_library</span>
                                    <h3 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-wider">
                                        Seleccionar Imagen de Avance
                                    </h3>
                                </div>
                                <button
                                    onClick={() => setShowImagePickerModal(false)}
                                    className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg"
                                >
                                    <span className="material-symbols-outlined text-[20px]">close</span>
                                </button>
                            </div>

                            <div className="space-y-3">
                                <label className="border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-2xl p-6 flex flex-col items-center justify-center gap-2 hover:border-primary/60 transition-colors cursor-pointer bg-slate-50/50 dark:bg-slate-850">
                                    <span className="material-symbols-outlined text-[36px] text-slate-400">cloud_upload</span>
                                    <span className="text-xs font-bold text-slate-700 dark:text-slate-200">
                                        Subir foto desde este equipo
                                    </span>
                                    <span className="text-[9px] text-slate-400">JPG, PNG o WebP de terreno o dron</span>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={e => {
                                            const file = e.target.files?.[0];
                                            if (file && targetImageWidgetId) {
                                                const reader = new FileReader();
                                                reader.onload = () => {
                                                    const dataUrl = reader.result as string;
                                                    setWidgets(prev => prev.map(w => {
                                                        if (w.id !== targetImageWidgetId) return w;
                                                        return {
                                                            ...w,
                                                            style: {
                                                                ...(w.style || {}),
                                                                imageUrl: dataUrl
                                                            }
                                                        };
                                                    }));
                                                    setShowImagePickerModal(false);
                                                };
                                                reader.readAsDataURL(file);
                                            }
                                        }}
                                    />
                                </label>

                                <div className="space-y-1">
                                    <span className="text-[9px] font-bold text-slate-400 uppercase">O pegar URL directa de imagen</span>
                                    <div className="flex gap-2">
                                        <input
                                            type="url"
                                            placeholder="https://ejemplo.com/foto-obra.jpg"
                                            className="flex-1 p-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs"
                                            onKeyDown={e => {
                                                if (e.key === 'Enter' && targetImageWidgetId) {
                                                    const val = (e.target as HTMLInputElement).value.trim();
                                                    if (val) {
                                                        setWidgets(prev => prev.map(w => w.id === targetImageWidgetId ? { ...w, style: { ...(w.style || {}), imageUrl: val } } : w));
                                                        setShowImagePickerModal(false);
                                                    }
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* OVERLAY: MODO PRESENTACIÓN EN PANTALLA COMPLETA (FULLSCREEN 16:9 SLIDES) */}
                {isPresentationMode && (
                    <div className="fixed inset-0 z-[200] bg-slate-950 flex flex-col justify-between p-6 animate-fade-in select-none">
                        {/* Diapositiva Maximizada */}
                        <div className="flex-1 flex items-center justify-center">
                            {(() => {
                                const currentTheme = SLIDE_THEMES.find(t => t.id === activeThemeId) || SLIDE_THEMES[0];
                                const pageWidgets = widgets.filter(w => (w.pageId || 'page-1') === activePageId);

                                return (
                                    <div
                                        style={{
                                            backgroundColor: currentTheme.bgSlideColor,
                                            borderColor: currentTheme.borderColor
                                        }}
                                        className="w-full max-w-[1480px] h-[780px] md:h-[820px] rounded-3xl border-2 shadow-2xl p-6 flex flex-col justify-between gap-4 overflow-hidden relative shrink-0"
                                    >
                                        {/* Barra de cabecera de la diapositiva */}
                                        <div className="flex items-center justify-between border-b pb-3 border-slate-200/80 dark:border-slate-800 shrink-0">
                                            <div className="flex items-center gap-2">
                                                <span className="material-symbols-outlined text-[18px] text-primary">slideshow</span>
                                                <h3 className="text-xs font-black uppercase tracking-wider" style={{ color: currentTheme.textColor }}>
                                                    {reportPages.find(p => p.id === activePageId)?.name || 'Diapositiva'}
                                                </h3>
                                            </div>

                                            {/* Header Actions (Presentation Mode) */}
                                            <div className="flex items-center gap-2">


                                                <span className="text-[11px] font-mono text-slate-400 font-bold ml-2">
                                                    {reportPages.findIndex(p => p.id === activePageId) + 1} / {reportPages.length}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Grilla de 16 Columnas × 9 Filas */}
                                        <div 
                                            style={{
                                                display: 'grid',
                                                gridTemplateColumns: 'repeat(16, minmax(0, 1fr))',
                                                gridTemplateRows: 'repeat(9, minmax(0, 1fr))'
                                            }}
                                            className="gap-3 md:gap-3.5 w-full h-[650px] md:h-[720px] min-h-0 relative overflow-hidden"
                                        >
                                            {pageWidgets.map(widget => {
                                                const colSpan = Math.max(1, Math.min(16, widget.colSpan || (widget.width === '1/3' ? 5 : widget.width === 'full' ? 16 : 8)));
                                                const rowSpan = Math.max(1, Math.min(9, widget.rowSpan || (widget.type === 'header-banner' ? 1 : widget.type === 'kpi-card' || widget.type === 'gauge' || widget.type === 'text-box' ? 2 : 4)));
                                                const colStart = widget.gridColStart || 1;
                                                const rowStart = widget.gridRowStart || 1;

                                                const rawWidgetRows = getComponentRows(widget.componentId);
                                                const widgetRows = processCalculations(widget.componentId, rawWidgetRows);

                                                const widgetCols = selectedColumns[widget.componentId] || DEFAULT_COLUMNS[widget.componentId] || [];
                                                const hState = widgetHierarchyStates[widget.id] || { level: 0, path: [] };
                                                const currentXAxisKey = (widget.axisFields && widget.axisFields.length > hState.level)
                                                    ? widget.axisFields[hState.level].fieldKey
                                                    : (widget.xAxisKey || widgetCols[0] || 'Categoría');

                                                const wStyle = widget.style || {};
                                                const isSlicerWidget = widget.type === 'slicer';
                                                const isSlicerDropdownOpen = isSlicerWidget && !!openDropdownSlicers[widget.id];
                                                const isCompactSlicer = isSlicerWidget && (rowSpan === 1 || wStyle.showTitle === false);

                                                // Para slicers compactos (1 fila o con título desmarcado), la cabecera externa de tarjeta se oculta para no duplicar ni empujar el desplegable
                                                const showTitle = wStyle.showTitle !== false && widget.type !== 'header-banner' && widget.type !== 'text-box' && !isCompactSlicer;
                                                const customTitle = wStyle.customTitle;
                                                const titleColor = wStyle.titleColor;
                                                const titleFontSize = wStyle.titleFontSize;
                                                const titleAlign = wStyle.titleAlignment || 'left';
                                                const showBg = wStyle.showBackground !== false;
                                                const bgColor = wStyle.backgroundColor || (showBg ? currentTheme.cardBgColor : 'transparent');
                                                const showBorder = wStyle.showBorder !== false;
                                                const borderColor = wStyle.borderColor || currentTheme.borderColor;
                                                const borderRadius = wStyle.borderRadius !== undefined ? `${wStyle.borderRadius}px` : '16px';
                                                const showTitleDivider = wStyle.showTitleDivider !== false;
                                                const titlePaddingBottom = wStyle.titlePadding !== undefined ? `${wStyle.titlePadding}px` : '4px';
                                                const titleMarginBottom = wStyle.titleMarginBottom !== undefined ? `${wStyle.titleMarginBottom}px` : '6px';
                                                const titleAlignClass = titleAlign === 'center' ? 'text-center' : titleAlign === 'right' ? 'text-right' : 'text-left';

                                                return (
                                                    <div
                                                        key={widget.id}
                                                        style={{
                                                            gridColumnStart: colStart,
                                                            gridColumnEnd: `span ${colSpan}`,
                                                            gridRowStart: rowStart,
                                                            gridRowEnd: `span ${rowSpan}`,
                                                            backgroundColor: bgColor,
                                                            borderColor: borderColor,
                                                            borderRadius: borderRadius
                                                        }}
                                                        className={`${isCompactSlicer ? 'p-1.5 md:p-2' : 'p-3 md:p-4'} flex flex-col justify-center relative ${isSlicerDropdownOpen ? 'z-50' : isSlicerWidget ? 'z-20' : 'z-10'} ${isSlicerWidget ? 'overflow-visible' : 'overflow-hidden'} ${!showBorder ? 'border-transparent' : 'border shadow-md'}`}
                                                    >
                                                        {showTitle && (
                                                            <div 
                                                                style={{ 
                                                                    marginBottom: titleMarginBottom, 
                                                                    paddingBottom: titlePaddingBottom 
                                                                }} 
                                                                className={`flex items-center justify-between shrink-0 ${showTitleDivider ? 'border-b border-slate-100 dark:border-slate-800/80' : ''}`}
                                                            >
                                                                <div className={`truncate flex-1 ${titleAlignClass}`}>
                                                                    <h5 
                                                                        style={{
                                                                            color: titleColor || currentTheme.textColor,
                                                                            fontSize: titleFontSize ? `${titleFontSize}px` : undefined
                                                                        }}
                                                                        className="text-[11px] font-extrabold truncate"
                                                                    >
                                                                        {customTitle || (
                                                                            <>
                                                                                {widget.type === 'bar-vertical' && 'Barras V'}
                                                                                {widget.type === 'bar-horizontal' && 'Barras H'}
                                                                                {widget.type === 'pie' && 'Gráfico Circular'}
                                                                                {widget.type === 'matrix' && 'Matriz Jerárquica'}
                                                                                {widget.type === 'line' && 'Líneas'}
                                                                                {widget.type === 'area-stacked' && 'Áreas Ap.'}
                                                                                {widget.type === 'scatter' && 'Dispersión'}
                                                                                {widget.type === 'radar' && 'Radar'}
                                                                                {widget.type === 'kpi-card' && 'Indicador'}
                                                                                {widget.type === 'gauge' && 'Indicador de cumplimiento'}
                                                                                {widget.type === 'image' && 'Registro Fotográfico'}
                                                                                {widget.type === 'container-card' && 'Contenedor Visual'}
                                                                                {` por ${currentXAxisKey}`}
                                                                            </>
                                                                        )}
                                                                    </h5>
                                                                </div>
                                                            </div>
                                                        )}
                                                        <div className={`flex-1 w-full min-h-0 flex flex-col justify-center ${isSlicerWidget ? 'overflow-visible' : 'overflow-hidden'}`}>
                                                            {renderWidgetChart(widget, widgetRows, widgetCols, widget.componentId)}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>

                        {/* Barra Flotante de Control de Presentación */}
                        <div className="flex items-center justify-between max-w-xl mx-auto w-full bg-slate-900/90 border border-slate-800 rounded-2xl px-6 py-2.5 text-white shadow-2xl backdrop-blur-md">
                            <button
                                type="button"
                                onClick={() => {
                                    const currentIndex = reportPages.findIndex(p => p.id === activePageId);
                                    if (currentIndex > 0) {
                                        setActivePageId(reportPages[currentIndex - 1].id);
                                    }
                                }}
                                className="flex items-center gap-1 text-xs font-bold hover:text-primary transition-colors cursor-pointer"
                            >
                                <span className="material-symbols-outlined text-[16px]">arrow_back</span>
                                Anterior
                            </button>

                            <div className="flex items-center gap-2">
                                <span className="text-xs font-mono font-bold text-slate-400">
                                    Diapositiva {reportPages.findIndex(p => p.id === activePageId) + 1} de {reportPages.length}
                                </span>
                            </div>

                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        const currentIndex = reportPages.findIndex(p => p.id === activePageId);
                                        if (currentIndex < reportPages.length - 1) {
                                            setActivePageId(reportPages[currentIndex + 1].id);
                                        }
                                    }}
                                    className="flex items-center gap-1 text-xs font-bold hover:text-primary transition-colors cursor-pointer"
                                >
                                    Siguiente
                                    <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
                                </button>

                                <button
                                    type="button"
                                    onClick={() => setIsPresentationMode(false)}
                                    className="ml-2 px-2.5 py-1 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer"
                                    title="Salir de pantalla completa (Esc)"
                                >
                                    Salir [Esc]
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {/* MODAL 5: CONFIRMACIÓN Y GUARDADO DE REPORTE / PLANTILLA */}
                {showSaveConfirmModal && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 dark:bg-black/80 backdrop-blur-sm transition-all duration-300">
                        <div className="bg-white dark:bg-slate-900 rounded-3xl max-w-lg w-full shadow-2xl border border-slate-100 dark:border-slate-800 overflow-hidden transform scale-100 animate-in fade-in zoom-in duration-200 p-6 space-y-5">
                            <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
                                <div className="flex items-center gap-2">
                                    <span className="material-symbols-outlined text-[24px] text-primary">save</span>
                                    <div>
                                        <h3 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-wider">
                                            {onSaveTemplate ? 'Guardar Plantilla en el Maestro' : 'Guardar Reporte Analítico'}
                                        </h3>
                                        <p className="text-[10px] text-slate-400">
                                            Configura los detalles de identificación antes de registrar los cambios.
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowSaveConfirmModal(false)}
                                    className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg cursor-pointer"
                                >
                                    <span className="material-symbols-outlined text-[20px]">close</span>
                                </button>
                            </div>

                            <div className="space-y-3.5 text-xs">
                                <div>
                                    <label className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase block mb-1">
                                        Nombre {onSaveTemplate ? 'de la Plantilla' : 'del Reporte'} *
                                    </label>
                                    <input
                                        type="text"
                                        value={reportTitle}
                                        onChange={e => setReportTitle(e.target.value)}
                                        placeholder="Ej: Resumen Ejecutivo de Avance"
                                        className="w-full p-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white font-bold outline-none focus:ring-1 focus:ring-primary"
                                    />
                                </div>

                                <div>
                                    <label className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase block mb-1">
                                        Descripción
                                    </label>
                                    <textarea
                                        rows={2}
                                        value={reportDescription}
                                        onChange={e => setReportDescription(e.target.value)}
                                        placeholder="Describe el propósito y métricas de este reporte..."
                                        className="w-full p-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white font-medium outline-none focus:ring-1 focus:ring-primary resize-none"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase block mb-1">
                                            Categoría
                                        </label>
                                        <select
                                            value={reportCategory}
                                            onChange={e => setReportCategory(e.target.value)}
                                            className="w-full p-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-800 dark:text-slate-200 font-bold outline-none cursor-pointer"
                                        >
                                            <option value="Ejecutivo">Ejecutivo</option>
                                            <option value="Operativo">Operativo</option>
                                            <option value="Producción">Producción</option>
                                            <option value="Calidad">Calidad</option>
                                            <option value="General">General</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase block mb-1">
                                            Insignia / Etiqueta
                                        </label>
                                        <input
                                            type="text"
                                            value={reportBadge}
                                            onChange={e => setReportBadge(e.target.value)}
                                            placeholder="Ej: Recomendado"
                                            className="w-full p-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white font-bold outline-none focus:ring-1 focus:ring-primary"
                                        />
                                    </div>
                                </div>

                                <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-100 dark:border-slate-800 text-[11px] text-slate-500 dark:text-slate-400 flex items-center justify-between">
                                    <span>Componentes maquetados: <strong>{widgets.length}</strong></span>
                                    <span>Páginas / Diapositivas: <strong>{reportPages.length}</strong></span>
                                </div>
                            </div>

                            <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-slate-100 dark:border-slate-800">
                                <button
                                    type="button"
                                    onClick={() => setShowSaveConfirmModal(false)}
                                    className="px-4 py-2 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 font-bold rounded-xl text-xs transition-colors cursor-pointer"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSaveLocalReport}
                                    disabled={savingLocal}
                                    className="px-5 py-2 bg-primary hover:bg-primary/95 active:scale-95 text-white font-black uppercase tracking-wider rounded-xl text-xs shadow-md shadow-primary/20 flex items-center gap-1.5 transition-all cursor-pointer disabled:opacity-50"
                                >
                                    <span className="material-symbols-outlined text-[16px]">check_circle</span>
                                    {savingLocal ? 'Guardando...' : 'Confirmar y Guardar'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* MODAL 6: ADVERTENCIA AL CANCELAR */}
                {showCancelWarningModal && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 dark:bg-black/80 backdrop-blur-sm transition-all duration-300">
                        <div className="bg-white dark:bg-slate-900 rounded-3xl max-w-md w-full shadow-2xl border border-slate-100 dark:border-slate-800 p-6 space-y-4">
                            <div className="flex items-center gap-3 text-amber-500">
                                <span className="material-symbols-outlined text-[28px]">warning</span>
                                <h3 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-wider">
                                    ¿Deseas salir del diseñador?
                                </h3>
                            </div>
                            <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                                Los cambios o componentes añadidos que no hayas guardado se perderán.
                            </p>
                            <div className="flex items-center justify-end gap-2 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setShowCancelWarningModal(false)}
                                    className="px-4 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 font-bold rounded-xl text-xs cursor-pointer"
                                >
                                    Seguir Editando
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowCancelWarningModal(false);
                                        onCancel?.();
                                    }}
                                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl text-xs transition-colors cursor-pointer"
                                >
                                    Descartar y Salir
                                </button>
                            </div>
                        </div>
                    </div>
                )}
        </div>
    );
};
